/* eslint-disable */
import { useState, useContext, createContext, useCallback, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

// =====================================================
// SUPABASE CONFIG
// =====================================================
const SUPABASE_URL = "https://yeefwfeyeytlcuvlefxv.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InllZWZ3ZmV5ZXl0bGN1dmxlZnh2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODA4MDk3OTUsImV4cCI6MjA5NjM4NTc5NX0.20V3wb24oGARI3SQfdfGoLPb6DhvKWvd6KGnyIe8R7A";
const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// =====================================================
// SUPABASE DATA LAYER
// =====================================================
const db = {
  // خواندن همه داده‌ها از Supabase
  async loadAll() {
    const [
      { data: accounts },
      { data: persons },
      { data: items },
      { data: expCats },
      { data: purchases },
      { data: purchaseLines },
      { data: sales },
      { data: saleLines },
      { data: payments },
      { data: expenses },
      { data: manualVouchers },
      { data: ledger }
    ] = await Promise.all([
      supabase.from("accounts").select("*").order("name"),
      supabase.from("persons").select("*").order("name"),
      supabase.from("items").select("*").order("name"),
      supabase.from("expense_categories").select("*").order("code"),
      supabase.from("purchases").select("*").order("date", { ascending: false }),
      supabase.from("purchase_lines").select("*"),
      supabase.from("sales").select("*").order("date", { ascending: false }),
      supabase.from("sale_lines").select("*"),
      supabase.from("payments").select("*").order("date", { ascending: false }),
      supabase.from("expenses").select("*").order("date", { ascending: false }),
      supabase.from("manual_vouchers").select("*").order("date", { ascending: false }),
      supabase.from("ledger").select("*").order("date", { ascending: false })
    ]);

    return {
      accounts: accounts || [],
      persons: persons || [],
      items: items || [],
      expenseCategories: (expCats || []).map(c => ({ ...c, id: c.id, name: c.name, code: c.code, description: c.description || "", is_active: c.is_active })),
      purchases: (purchases || []).map(p => ({ ...p, created_at: p.created_at })),
      purchaseLines: purchaseLines || [],
      sales: sales || [],
      saleLines: saleLines || [],
      payments: payments || [],
      expenses: expenses || [],
      manualVouchers: manualVouchers || [],
      manualVoucherLines: [],
      ledger: ledger || []
    };
  },

  // ذخیره یک رکورد
  async upsert(table, row) {
    const { error } = await supabase.from(table).upsert(row, { onConflict: "id" });
    return error;
  },

  // حذف رکورد
  async delete(table, id) {
    const { error } = await supabase.from(table).delete().eq("id", id);
    return error;
  },

  // حذف چند رکورد با شرط
  async deleteWhere(table, column, value) {
    const { error } = await supabase.from(table).delete().eq(column, value);
    return error;
  },

  // upsert چند رکورد
  async upsertMany(table, rows) {
    if (!rows || rows.length === 0) return null;
    const { error } = await supabase.from(table).upsert(rows, { onConflict: "id" });
    return error;
  }
};

const AppContext = createContext(null);
const useApp = () => useContext(AppContext);
const useSb = () => {
  const ctx = useContext(AppContext);
  return { sbWrite: ctx.sbWrite, refresh: ctx.refresh, profile: ctx.profile, auditLog: ctx.auditLog };
};

const theme = {
  bg: "#0f1117", bgCard: "#1a1d27", bgInput: "#12151f",
  border: "#2a2d3a", accent: "#6366f1", accentLight: "#818cf8",
  success: "#10b981", danger: "#ef4444", warning: "#f59e0b",
  text: "#e2e8f0", textMuted: "#64748b", gold: "#d4a853"
};

const GlobalStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@300;400;500;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html { font-size: 16px; }
    body { font-family: 'Vazirmatn', sans-serif; background: ${theme.bg}; color: ${theme.text}; direction: rtl; min-height: 100vh; font-size: 1rem; -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
    ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-track { background: ${theme.bg}; } ::-webkit-scrollbar-thumb { background: ${theme.border}; border-radius: 3px; }
    input, select, textarea { font-family: 'Vazirmatn', sans-serif; direction: rtl; font-size: 1rem; }
    button { font-family: 'Vazirmatn', sans-serif; cursor: pointer; }
    table { border-collapse: collapse; width: 100%; }
    .fade-in { animation: fadeIn 0.3s ease; }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  `}</style>
);

const Card = ({ children, style = {} }) => (
  <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 24, ...style }}>{children}</div>
);

const Input = ({ label, style: extraStyle, type, value, onChange, ...props }) => {
  // برای input های عددی: صفر را به رشته خالی تبدیل می‌کند تا "0" نمایش ندهد
  const displayValue = (type === "number" && (value === 0 || value === "0")) ? "" : value;
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: theme.textMuted }}>{label}</label>}
      <input type={type} value={displayValue} onChange={onChange}
        style={{ width: "100%", padding: "9px 12px", background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, fontSize: 14, outline: "none", ...extraStyle }}
        onFocus={e => e.target.style.borderColor = theme.accent}
        onBlur={e => e.target.style.borderColor = (extraStyle && extraStyle.borderColor ? extraStyle.borderColor : theme.border)}
        {...props} />
    </div>
  );
};

const Select = ({ label, children, style: extraStyle, ...props }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: theme.textMuted }}>{label}</label>}
    <select style={{ width: "100%", padding: "9px 12px", background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, fontSize: 14, outline: "none", ...extraStyle }}
      onFocus={e => e.target.style.borderColor = theme.accent} onBlur={e => e.target.style.borderColor = theme.border} {...props}>{children}</select>
  </div>
);

// Searchable dropdown — برای لیست‌های بلند مثل اشخاص
const SearchableSelect = ({ label, value, onChange, options, placeholder = "— انتخاب کنید —", required, error }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const selected = options.find(o => o.value === value);
  const filtered = options.filter(o => !search || o.label.includes(search)).slice(0, 10);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div style={{ marginBottom: 14, position: "relative" }} ref={ref}
      onClick={e => e.stopPropagation()}>
      {label && <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: error ? theme.danger : theme.textMuted }}>{label}{required && " *"}</label>}
      <div onClick={(e) => { e.stopPropagation(); setOpen(o => !o); setSearch(""); }}
        style={{ width: "100%", padding: "9px 12px", background: theme.bgInput, border: `1px solid ${error ? theme.danger : open ? theme.accent : theme.border}`, borderRadius: 8, color: selected ? theme.text : theme.textMuted, fontSize: 14, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>{selected ? selected.label : placeholder}</span>
        <span style={{ fontSize: 10, color: theme.textMuted }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div style={{ position: "absolute", top: "100%", right: 0, left: 0, zIndex: 1000, background: theme.bgCard, border: `1px solid ${theme.accent}44`, borderRadius: 8, boxShadow: "0 8px 24px #0008", overflow: "hidden" }}>
          <div style={{ padding: "8px 10px", borderBottom: `1px solid ${theme.border}` }}>
            <input autoFocus placeholder="جستجو..." value={search} onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              style={{ width: "100%", padding: "6px 10px", background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 6, color: theme.text, fontSize: 13, outline: "none", fontFamily: "Vazirmatn" }} />
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            <div onClick={(e) => { e.stopPropagation(); onChange({ target: { value: "" } }); setOpen(false); }}
              style={{ padding: "9px 12px", cursor: "pointer", color: theme.textMuted, fontSize: 13, borderBottom: `1px solid ${theme.border}22` }}
              onMouseEnter={e => e.currentTarget.style.background = theme.bgInput}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              {placeholder}
            </div>
            {filtered.map(o => (
              <div key={o.value} onClick={(e) => { e.stopPropagation(); onChange({ target: { value: o.value } }); setOpen(false); setSearch(""); }}
                style={{ padding: "9px 12px", cursor: "pointer", fontSize: 13, background: o.value === value ? theme.accent + "22" : "transparent", color: o.value === value ? theme.accentLight : theme.text, borderBottom: `1px solid ${theme.border}11` }}
                onMouseEnter={e => { if (o.value !== value) e.currentTarget.style.background = theme.bgInput; }}
                onMouseLeave={e => { if (o.value !== value) e.currentTarget.style.background = "transparent"; }}>
                {o.label}
              </div>
            ))}
            {filtered.length === 0 && <div style={{ padding: "9px 12px", color: theme.textMuted, fontSize: 13 }}>نتیجه‌ای یافت نشد</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const Textarea = ({ label, style = {}, ...props }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: theme.textMuted }}>{label}</label>}
    <textarea style={{ width: "100%", padding: "9px 12px", background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, fontSize: 14, outline: "none", resize: "vertical", minHeight: 80, ...style }} {...props} />
  </div>
);

const Btn = ({ children, variant = "primary", size = "md", style = {}, disabled = false, ...props }) => {
  const colors = {
    primary: { bg: theme.accent, hover: theme.accentLight, color: "#fff" },
    success: { bg: theme.success, hover: "#059669", color: "#fff" },
    danger: { bg: theme.danger, hover: "#dc2626", color: "#fff" },
    ghost: { bg: "transparent", hover: theme.bgInput, color: theme.text, border: `1px solid ${theme.border}` },
    warning: { bg: theme.warning, hover: "#d97706", color: "#fff" }
  };
  const c = colors[variant];
  const pad = size === "sm" ? "6px 14px" : size === "lg" ? "12px 28px" : "9px 20px";
  return (
    <button disabled={disabled} style={{ padding: pad, fontSize: size === "sm" ? 12 : 14, fontWeight: 600, background: disabled ? theme.border : c.bg, color: disabled ? theme.textMuted : c.color, border: c.border || "none", borderRadius: 8, transition: "all 0.2s", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1, ...style }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = c.hover; }} onMouseLeave={e => { if (!disabled) e.currentTarget.style.background = c.bg; }} {...props}>{children}</button>
  );
};

const Badge = ({ children, color = theme.accent }) => (
  <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: color+"22", color, border: "1px solid "+color+"44" }}>{children}</span>
);

// Toast notification
let toastTimer = null;
const Toast = ({ msg, type, visible }) => {
  if (!visible) return null;
  const bg = type === "success" ? theme.success : type === "error" ? theme.danger : theme.warning;
  return (
    <div style={{
      position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)",
      background: bg, color: "#fff", padding: "12px 28px", borderRadius: 12,
      fontWeight: 700, fontSize: 15, zIndex: 9999, boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
      animation: "fadeIn 0.3s ease", display: "flex", alignItems: "center", gap: 10
    }}>
      {type === "success" ? "✓" : type === "error" ? "✗" : "⚠"} {msg}
    </div>
  );
};

const useToast = () => {
  const [toast, setToast] = useState({ visible: false, msg: "", type: "success" });
  const show = (msg, type = "success") => {
    setToast({ visible: true, msg, type });
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => setToast(p => ({ ...p, visible: false })), 3000);
  };
  return [toast, show];
};

const Modal = ({ open, onClose, title, children, width = 600 }) => {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fade-in" style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 16, padding: 32, width: "100%", maxWidth: width, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: theme.textMuted, fontSize: 22, lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Table = ({ cols, rows, pageSize = 20, searchable = false }) => {
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");

  // جستجو: روی متن رشته‌ای سلول‌ها
  const filtered = searchable && q.trim()
    ? rows.filter(row => row.cells.some(cell => {
        const text = typeof cell === "string" ? cell
          : typeof cell === "number" ? String(cell)
          : cell?.props?.children ? String(cell.props.children).replace(/,/g,"") : "";
        return text.includes(q.trim());
      }))
    : rows;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const visible = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  return (
    <div>
      {searchable && (
        <div style={{ marginBottom: 10, display: "flex", gap: 8, alignItems: "center" }}>
          <input value={q} onChange={e => { setQ(e.target.value); setPage(0); }}
            placeholder="🔍 جستجو..."
            style={{ flex: 1, padding: "7px 12px", background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, fontSize: 13, outline: "none" }} />
          {q && <button onClick={() => { setQ(""); setPage(0); }}
            style={{ background: "none", border: "none", color: theme.textMuted, cursor: "pointer", fontSize: 16 }}>×</button>}
          {q && <span style={{ fontSize: 12, color: theme.textMuted }}>{filtered.length} نتیجه</span>}
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table>
          <thead>
            <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
              {cols.map((c, i) => <th key={i} style={{ padding: "13px 16px", textAlign: "right", fontSize: 13, color: theme.textMuted, fontWeight: 600, whiteSpace: "nowrap" }}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0
              ? <tr><td colSpan={cols.length} style={{ textAlign: "center", padding: 40, color: theme.textMuted }}>{q ? "نتیجه‌ای یافت نشد" : "داده‌ای یافت نشد"}</td></tr>
              : visible.map((row, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${theme.border}22`, cursor: row.onDoubleClick ? "pointer" : "default" }}
                  onMouseEnter={e => e.currentTarget.style.background = theme.bgInput}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  onDoubleClick={row.onDoubleClick}>
                  {row.cells.map((cell, j) => <td key={j} style={{ padding: "12px 16px", fontSize: 14 }}>{cell}</td>)}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, paddingTop: 14, borderTop: `1px solid ${theme.border}22`, marginTop: 8 }}>
          <button onClick={() => setPage(0)} disabled={safePage === 0}
            style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${theme.border}`, borderRadius: 6, color: safePage === 0 ? theme.textMuted : theme.text, cursor: safePage === 0 ? "default" : "pointer", fontSize: 12, fontFamily: "Vazirmatn" }}>«</button>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={safePage === 0}
            style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${theme.border}`, borderRadius: 6, color: safePage === 0 ? theme.textMuted : theme.text, cursor: safePage === 0 ? "default" : "pointer", fontSize: 12, fontFamily: "Vazirmatn" }}>‹</button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, idx) => {
            const start = Math.max(0, Math.min(safePage - 2, totalPages - 5));
            const p = start + idx;
            return (
              <button key={p} onClick={() => setPage(p)}
                style={{ padding: "4px 12px", background: p === safePage ? theme.accent : "transparent", border: `1px solid ${p === safePage ? theme.accent : theme.border}`, borderRadius: 6, color: p === safePage ? "#fff" : theme.text, cursor: "pointer", fontSize: 12, fontFamily: "Vazirmatn", fontWeight: p === safePage ? 700 : 400 }}>{p + 1}</button>
            );
          })}
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={safePage === totalPages - 1}
            style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${theme.border}`, borderRadius: 6, color: safePage === totalPages - 1 ? theme.textMuted : theme.text, cursor: safePage === totalPages - 1 ? "default" : "pointer", fontSize: 12, fontFamily: "Vazirmatn" }}>›</button>
          <button onClick={() => setPage(totalPages - 1)} disabled={safePage === totalPages - 1}
            style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${theme.border}`, borderRadius: 6, color: safePage === totalPages - 1 ? theme.textMuted : theme.text, cursor: safePage === totalPages - 1 ? "default" : "pointer", fontSize: 12, fontFamily: "Vazirmatn" }}>»</button>
          <span style={{ fontSize: 11, color: theme.textMuted, marginRight: 8 }}>
            {safePage * pageSize + 1}–{Math.min(filtered.length, (safePage + 1) * pageSize)} از {filtered.length}
          </span>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, color = theme.accent, icon }) => (
  <Card style={{ flex: 1, minWidth: 160 }}>
    <div style={{ color: theme.textMuted, fontSize: 12, marginBottom: 8 }}>{icon} {label}</div>
    <div dir="ltr" style={{ fontSize: 22, fontWeight: 700, color, unicodeBidi: "isolate" }}>{value}</div>
  </Card>
);

const fmtMoney = (n, currency = "USD") => {
  const num = Number(n) || 0;
  const rounded = Math.round(num * 100) / 100;
  const isWhole = Math.abs(rounded - Math.round(rounded)) < 0.000001;
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: 2,
    useGrouping: true
  });
  const amount = formatter.format(Math.abs(rounded));
  const sign = rounded < 0 ? "-" : "";
  return currency ? `${sign}${currency} ${amount}` : `${sign}${amount}`;
};

const fmtQty = (n) => {
  const num = Number(n) || 0;
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
    useGrouping: true
  }).format(num);
};

// alias برای سازگاری با کد قدیمی — اگر currency خالی باشد، فقط عدد
const fmt = (n, currency = "") => {
  if (!currency) {
    const num = Number(n) || 0;
    const rounded = Math.round(num * 100) / 100;
    const isWhole = Math.abs(rounded - Math.round(rounded)) < 0.000001;
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: isWhole ? 0 : 2,
      maximumFractionDigits: 2,
      useGrouping: true
    }).format(rounded);
  }
  return fmtMoney(n, currency);
};

const Money = ({ value, currency = "USD", style = {} }) => (
  <span dir="ltr" style={{ unicodeBidi: "isolate", ...style }}>
    {fmtMoney(value, currency)}
  </span>
);

const today = () => new Date().toISOString().slice(0, 10);
const uid = () => crypto.randomUUID();

// sanitize برای جلوگیری از HTML injection در چاپ
const sanitizeHtml = (str) => {
  if (typeof str !== "string") return String(str || "");
  return str.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;").replace(/'/g,"&#39;");
};

// sanitize برای جلوگیری از Excel formula injection
// اعداد واقعی (positive/negative) بدون تغییر می‌مانند؛ فقط رشته‌هایی که با کاراکتر خطرناک شروع می‌شوند escape می‌شوند
const sanitizeExcel = (val) => {
  if (typeof val === "number") return val; // عدد واقعی — بدون تغییر
  const s = String(val == null ? "" : val);
  if (s === "") return s;
  // اگر کاملاً عدد است (از جمله منفی) بدون تغییر برگردان
  if (!isNaN(Number(s)) && s.trim() !== "") return s;
  // رشته‌هایی که با کاراکتر formula injection شروع می‌شوند را escape کن
  if (["=","@","\t","\r"].some(c => s.startsWith(c))) return "'" + s;
  // + و - فقط اگر بعدشان حرف باشد خطرناکند (مثل +CMD) نه اگر عدد باشند
  if ((s.startsWith("+") || s.startsWith("-")) && isNaN(Number(s))) return "'" + s;
  return s;
};

// =====================================================
// MOVING WEIGHTED AVERAGE ENGINE
// =====================================================

// ساخت لیست movements مرتب‌شده برای یک کالا
// ترتیب: date → نوع (خرید قبل از فروش در همان تاریخ) → created_at
const buildMovements = (itemId, purchaseLines, saleLines, purchases, sales) => {
  const movements = [];

  (purchaseLines || []).filter(l => l.item_id === itemId).forEach(l => {
    const pur = (purchases || []).find(p => p.id === l.purchase_id);
    if (!pur) return;
    movements.push({
      type: "purchase",
      date: pur.date || "0000-00-00",
      seq: pur.created_at || pur.date || "0000-00-00",
      qty: parseFloat(l.quantity) || 0,
      cost: parseFloat(l.unit_cost_with_overhead || l.unit_price) || 0,
      sourceId: pur.id
    });
  });

  (saleLines || []).filter(l => l.item_id === itemId).forEach(l => {
    const sal = (sales || []).find(s => s.id === l.sale_id);
    if (!sal) return;
    movements.push({
      type: "sale",
      date: sal.date || "0000-00-00",
      seq: sal.created_at || sal.date || "0000-00-00",
      qty: parseFloat(l.quantity) || 0,
      saleId: sal.id
    });
  });

  // مرتب‌سازی: تاریخ → نوع (خرید قبل فروش) → created_at
  movements.sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    if (a.type !== b.type) return a.type === "purchase" ? -1 : 1;
    return (a.seq || "").localeCompare(b.seq || "");
  });

  return movements;
};

// محاسبه میانگین موزون متحرک تا قبل از یک فروش خاص
// برای cost_price فروش — excludeSaleId: همان فروش را از محاسبه حذف می‌کند
const calculateAvgCostAtDate = (itemId, targetDate, targetSeq, purchaseLines, saleLines, purchases, sales, excludeSaleId, openingStock, openingUnitCost) => {
  const movements = buildMovements(itemId, purchaseLines, saleLines, purchases, sales)
    .filter(m => {
      // حذف فروش target از محاسبه
      if (m.type === "sale" && m.saleId === excludeSaleId) return false;
      // فقط movements قبل از این فروش (بر اساس date + seq)
      if (m.date < targetDate) return true;
      if (m.date === targetDate) {
        // خریدهای همان تاریخ قبل از فروش حساب می‌شوند
        if (m.type === "purchase") return true;
        // فروش‌های قبلی همان تاریخ
        if (m.type === "sale" && (m.seq || "") < (targetSeq || "")) return true;
        return false;
      }
      return false;
    });

  let stock = parseFloat(openingStock) || 0;
  let avg = parseFloat(openingUnitCost) || 0;

  for (const m of movements) {
    if (m.type === "purchase" && m.qty > 0) {
      const prevVal = stock * avg;
      stock += m.qty;
      avg = stock > 0 ? (prevVal + m.qty * m.cost) / stock : m.cost;
    } else if (m.type === "sale") {
      stock = stock - m.qty; // بدون Math.max — موجودی منفی نشان‌دهنده خطای داده است
    }
  }

  return { avg, stock };
};

// rebuild کامل موجودی همه کالاها — با تشخیص موجودی منفی
const rebuildItemStocks = (items, purchaseLines, saleLines, purchases, sales) => {
  const warnings = [];

  const result = items.map(item => {
    const openingStock = parseFloat(item.opening_stock) || 0;
    const openingUnitCost = parseFloat(item.opening_unit_cost != null ? item.opening_unit_cost : item.avg_purchase_price) || 0;

    const movements = buildMovements(item.id, purchaseLines, saleLines, purchases, sales);

    let runningStock = openingStock;
    let runningAvg = openingUnitCost;
    let hasNegativeStock = false;

    for (const m of movements) {
      if (m.type === "purchase") {
        if (m.qty <= 0) continue;
        const prevVal = runningStock * runningAvg;
        runningStock += m.qty;
        runningAvg = runningStock > 0 ? (prevVal + m.qty * m.cost) / runningStock : m.cost;
      } else {
        runningStock -= m.qty;
        if (runningStock < -0.001) {
          hasNegativeStock = true;
          warnings.push(item.name + ": موجودی منفی در تاریخ " + m.date);
        }
      }
    }

    return {
      ...item,
      current_stock: runningStock,
      avg_purchase_price: runningAvg,
      has_stock_warning: hasNegativeStock
    };
  });

  return { items: result, warnings };
};

// =====================================================
// EXCEL EXPORT
// =====================================================
const exportToExcel = (rows, cols, filename) => {
  const escape = (val) => {
    const s = String(val == null ? "" : val);
    if (s.includes(",") || s.includes("\"") || s.includes("\n") || s.includes("\r"))
      return "\"" + s.replace(/"/g, "\"\"") + "\"";
    return s;
  };
  const header = cols.map(escape).join(",");
  const body = rows.map(r => r.map(escape).join(",")).join("\n");
  const content = "\uFEFF" + header + "\n" + body;
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename + ".csv"; a.click();
  URL.revokeObjectURL(url);
};

// =====================================================
// PRINT
// =====================================================
const printArea = (title, tableHtml) => {
  const win = window.open("", "_blank");
  win.document.write(`
    <html dir="rtl"><head><meta charset="utf-8"><title>${title}</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:Tahoma,Arial,sans-serif;font-size:12px;direction:rtl;color:#111;background:#fff}
      @page{size:A4;margin:15mm 12mm 15mm 12mm}
      @media screen{body{padding:20px;max-width:800px;margin:0 auto}}
      @media print{body{padding:0}.no-print{display:none!important}}
      .doc-title{text-align:center;font-size:15px;font-weight:bold;margin-bottom:14px;padding-bottom:8px;border-bottom:2px solid #333;letter-spacing:0.5px}
      table{width:100%;border-collapse:collapse;margin-top:4px}
      thead th{background:#2c3e50;color:#fff;padding:7px 9px;font-size:11.5px;font-weight:bold;text-align:right;border:1px solid #2c3e50}
      tbody td{padding:6px 9px;font-size:11.5px;border:1px solid #d0d0d0;text-align:right;vertical-align:middle}
      tbody tr:nth-child(even){background:#f5f7fa}
      tbody tr:last-child td{font-weight:bold;background:#eef2f7}
      .footer{margin-top:14px;font-size:10.5px;color:#888;text-align:center;padding-top:8px;border-top:1px solid #ddd}
      .print-btn{display:block;margin:16px auto 0;padding:7px 24px;background:#2c3e50;color:#fff;border:none;border-radius:4px;font-size:13px;cursor:pointer;font-family:Tahoma,Arial,sans-serif}
    </style></head><body>
    <div class="doc-title">${title}</div>
    ${tableHtml}
    <div class="footer">تاریخ چاپ: ${new Date().toLocaleDateString("fa-IR")}</div>
    <button class="print-btn no-print" onclick="window.print()">&#128438; چاپ</button>
    </body></html>
  `);
  win.document.close();
};

const buildHtmlTable = (cols, dataRows) => {
  const header = "<tr>" + cols.map(c => `<th>${sanitizeHtml(c)}</th>`).join("") + "</tr>";
  const body = dataRows.map(r => "<tr>" + r.map(c => `<td>${sanitizeHtml(String(c == null ? "" : c))}</td>`).join("") + "</tr>").join("");
  return `<table><thead>${header}</thead><tbody>${body}</tbody></table>`;
};

// =====================================================
// شماره‌گذاری اتوماتیک
// =====================================================
const _genInvoiceNo = (purchases, sales, type) => {
  if (type === "purchase") {
    const last = (purchases || []).length;
    return `PUR-${String(last + 1).padStart(4, "0")}`;
  }
  if (type === "sale") {
    const last = (sales || []).length;
    return `SAL-${String(last + 1).padStart(4, "0")}`;
  }
  return "";
};

// VCH شماره از کل ledger — جلوگیری از تکراری شدن شماره سند
const genVoucherNoAll = (data) => {
  // fallback محلی — فقط وقتی RPC در دسترس نیست
  const allNos = (data.ledger || []).map(l => l.voucher_no).filter(Boolean);
  const nums = allNos.map(v => parseInt(String(v).replace(/\D/g, ""), 10)).filter(n => !isNaN(n));
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `VCH-${String(next).padStart(4, "0")}`;
};

// شماره سند atomic از Supabase — سراسری و غیرتکراری
const getNextVoucherNo = async () => {
  try {
    const { data, error } = await supabase.rpc("next_voucher_no");
    if (error || !data) throw error;
    return data;
  } catch {
    // اگر RPC هنوز نصب نشده، از genVoucherNoAll استفاده می‌کند
    return null;
  }
};

// شماره فاکتور امن — بر اساس max شماره موجود، نه length
const genInvoiceNoSafe = (list, prefix) => {
  const nums = (list || [])
    .map(x => parseInt(String(x.invoice_no || "").replace(/\D/g, ""), 10))
    .filter(n => !isNaN(n));
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `${prefix}-${String(next).padStart(4, "0")}`;
};

// شماره اتوماتیک دسته هزینه
const genExpCatCode = (cats) => {
  const nums = (cats || [])
    .map(c => parseInt(String(c.code || "").replace(/\D/g, ""), 10))
    .filter(n => !isNaN(n));
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return "EXP-" + String(next).padStart(3, "0");
};
const isLedgerBalanced = (entries) => {
  const byCurrency = {};
  for (const e of entries || []) {
    const cur = e.currency_code || "USD";
    if (!byCurrency[cur]) byCurrency[cur] = { debit: 0, credit: 0 };
    byCurrency[cur].debit += parseFloat(e.debit) || 0;
    byCurrency[cur].credit += parseFloat(e.credit) || 0;
  }
  return Object.values(byCurrency).every(x => Math.abs(x.debit - x.credit) < 0.001);
};

// سیستم تک‌ارزی: فقط USD
// const CURRENCY = "USD";
const CurrencySelect = ({ label }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: theme.textMuted }}>{label}</label>}
    <div style={{ padding: "9px 12px", background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.accentLight, fontWeight: 600, fontSize: 14 }}>USD — دلار 🔒</div>
  </div>
);
// const CurrencyFilterSelect = () => null;

// =====================================================
// AUTH CONTEXT
// =====================================================
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    // بررسی session فعلی
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user || null);
      if (session?.user) fetchProfile(session.user.id);
      else setAuthLoading(false);
    });
    // گوش دادن به تغییرات auth
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
      if (session?.user) fetchProfile(session.user.id);
      else { setProfile(null); setAuthLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId) => {
    const { data } = await supabase.from("profiles").select("*").eq("id", userId).single();
    setProfile(data);
    setAuthLoading(false);
  };

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return error;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null); setProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, profile, authLoading, signIn, signOut, fetchProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

// =====================================================
// LOGIN PAGE
// =====================================================
function LoginPage() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) { setError("ایمیل و رمز عبور الزامی است"); return; }
    setLoading(true); setError("");
    const err = await signIn(email, password);
    if (err) setError("ایمیل یا رمز عبور اشتباه است");
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: theme.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Vazirmatn, sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;600;700&display=swap');`}</style>
      <div style={{ width: 400, background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 16, padding: 40 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>💎</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: theme.gold }}>سیستم حسابداری زعفران</div>
          <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 4 }}>ورود به حساب کاربری</div>
        </div>
        {error && (
          <div style={{ background: theme.danger+"22", border: `1px solid ${theme.danger}44`, borderRadius: 8, padding: 10, marginBottom: 16, fontSize: 13, color: theme.danger, textAlign: "center" }}>
            {error}
          </div>
        )}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: theme.textMuted }}>ایمیل</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleLogin()}
            placeholder="younes@example.com" dir="ltr"
            style={{ width: "100%", padding: "10px 14px", background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, fontSize: 14, outline: "none", boxSizing: "border-box" }} />
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: theme.textMuted }}>رمز عبور</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleLogin()}
            placeholder="••••••••" dir="ltr"
            style={{ width: "100%", padding: "10px 14px", background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, fontSize: 14, outline: "none", boxSizing: "border-box" }} />
        </div>
        <button onClick={handleLogin} disabled={loading}
          style={{ width: "100%", padding: "12px", background: loading ? theme.border : theme.accent, color: "#fff", border: "none", borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: loading ? "default" : "pointer", fontFamily: "Vazirmatn, sans-serif" }}>
          {loading ? "در حال ورود..." : "ورود"}
        </button>
        <div style={{ marginTop: 16, textAlign: "center", fontSize: 12, color: theme.textMuted }}>
          برای دریافت دسترسی با مدیر سیستم تماس بگیرید
        </div>
      </div>
    </div>
  );
}

// =====================================================
// USERS MANAGEMENT PAGE (فقط admin)
// =====================================================
function UsersPage() {
  const { profile: myProfile } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [toast, showToast] = useToast();
  const [form, setForm] = useState({ email: "", full_name: "", password: "", role: "accountant" });

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    const { data } = await supabase.from("profiles").select("*").order("created_at");
    setUsers(data || []);
    setLoading(false);
  };

  const createUser = async () => {
    if (!form.email.trim() || !form.password.trim() || !form.full_name.trim()) {
      showToast("همه فیلدها الزامی است", "error"); return;
    }
    // ساخت کاربر از طریق admin API
    const { error } = await supabase.auth.admin.createUser({
      email: form.email, password: form.password,
      user_metadata: { full_name: form.full_name, role: form.role },
      email_confirm: true
    });
    if (error) { showToast("خطا: " + error.message, "error"); return; }
    showToast("کاربر با موفقیت ساخته شد ✓");
    setModal(false); setForm({ email: "", full_name: "", password: "", role: "accountant" });
    setTimeout(loadUsers, 1000);
  };

  const toggleActive = async (u) => {
    const { error } = await supabase.from("profiles").update({ is_active: !u.is_active }).eq("id", u.id);
    if (!error) { showToast("وضعیت تغییر کرد"); loadUsers(); }
  };

  const changeRole = async (u, newRole) => {
    const { error } = await supabase.from("profiles").update({ role: newRole }).eq("id", u.id);
    if (!error) { showToast("نقش تغییر کرد ✓"); loadUsers(); }
  };

  const roleLabel = { admin: "مدیر", accountant: "حسابدار", viewer: "فقط‌خواندنی" };
  const roleColor = { admin: theme.danger, accountant: theme.accent, viewer: theme.textMuted };

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} visible={toast.visible} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>👤 مدیریت کاربران</h2>
        <Btn onClick={() => setModal(true)}>+ کاربر جدید</Btn>
      </div>
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: theme.textMuted }}>در حال بارگذاری...</div>
      ) : (
        <Card>
          <Table cols={["نام", "ایمیل", "نقش", "وضعیت", "تاریخ عضویت", "عملیات"]}
            rows={users.map(u => ({ cells: [
              <span style={{ fontWeight: u.id === myProfile?.id ? 700 : 400 }}>
                {u.full_name} {u.id === myProfile?.id && <span style={{ fontSize: 11, color: theme.accent }}>(شما)</span>}
              </span>,
              <span dir="ltr" style={{ fontSize: 13 }}>{u.id}</span>,
              <select value={u.role} onChange={e => changeRole(u, e.target.value)}
                disabled={u.id === myProfile?.id}
                style={{ padding: "4px 8px", background: roleColor[u.role]+"22", border: `1px solid ${roleColor[u.role]}44`, borderRadius: 6, color: roleColor[u.role], fontSize: 12, cursor: "pointer", fontFamily: "Vazirmatn" }}>
                <option value="admin">مدیر</option>
                <option value="accountant">حسابدار</option>
                <option value="viewer">فقط‌خواندنی</option>
              </select>,
              <Badge color={u.is_active ? theme.success : theme.textMuted}>{u.is_active ? "فعال" : "غیرفعال"}</Badge>,
              new Date(u.created_at).toLocaleDateString("fa-IR"),
              u.id !== myProfile?.id && (
                <button onClick={() => toggleActive(u)}
                  style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${u.is_active ? theme.danger : theme.success}55`, borderRadius: 6, color: u.is_active ? theme.danger : theme.success, cursor: "pointer", fontSize: 12, fontFamily: "Vazirmatn" }}>
                  {u.is_active ? "غیرفعال" : "فعال‌کن"}
                </button>
              )
            ]}))}
          />
        </Card>
      )}
      <Modal open={modal} onClose={() => setModal(false)} title="کاربر جدید">
        <div style={{ background: `${theme.warning}22`, border: `1px solid ${theme.warning}44`, borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 12, color: theme.warning }}>
          ⚠️ رمز عبور را به صورت امن به کاربر اطلاع دهید
        </div>
        <Input label="نام کامل *" value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} />
        <Input label="ایمیل *" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
        <Input label="رمز عبور *" type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
        <Select label="نقش" value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
          <option value="admin">مدیر — دسترسی کامل</option>
          <option value="accountant">حسابدار — ثبت و ویرایش</option>
          <option value="viewer">فقط‌خواندنی — فقط گزارش</option>
        </Select>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setModal(false)}>انصراف</Btn>
          <Btn onClick={createUser}>ذخیره</Btn>
        </div>
      </Modal>
    </div>
  );
}

// =====================================================
// BACKUP PAGE
// =====================================================
function BackupPage() {
  const { profile } = useAuth();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, showToast] = useToast();
  const [label, setLabel] = useState("");
  const [restoreConfirm, setRestoreConfirm] = useState(null);

  useEffect(() => { loadBackups(); }, []);

  const loadBackups = async () => {
    const { data } = await supabase.from("backups").select("id,label,size_kb,created_at,created_by_name").order("created_at", { ascending: false }).limit(20);
    setBackups(data || []);
  };

  const takeBackup = async () => {
    if (!label.trim()) { showToast("برچسب بکاپ الزامی است", "error"); return; }
    setLoading(true);
    try {
      const [
        { data: accounts }, { data: persons }, { data: items },
        { data: expCats }, { data: purchases }, { data: purchaseLines },
        { data: sales }, { data: saleLines }, { data: payments },
        { data: expenses }, { data: manualVouchers }, { data: ledger }
      ] = await Promise.all([
        supabase.from("accounts").select("*"),
        supabase.from("persons").select("*"),
        supabase.from("items").select("*"),
        supabase.from("expense_categories").select("*"),
        supabase.from("purchases").select("*"),
        supabase.from("purchase_lines").select("*"),
        supabase.from("sales").select("*"),
        supabase.from("sale_lines").select("*"),
        supabase.from("payments").select("*"),
        supabase.from("expenses").select("*"),
        supabase.from("manual_vouchers").select("*"),
        supabase.from("ledger").select("*")
      ]);
      const snapshot = {
        accounts: accounts || [], persons: persons || [], items: items || [],
        expenseCategories: expCats || [], purchases: purchases || [],
        purchaseLines: purchaseLines || [], sales: sales || [],
        saleLines: saleLines || [], payments: payments || [],
        expenses: expenses || [], manualVouchers: manualVouchers || [],
        ledger: ledger || []
      };
      const sizeKb = Math.round(JSON.stringify(snapshot).length / 1024);
      const { error } = await supabase.from("backups").insert({
        label: label.trim(), data: snapshot, size_kb: sizeKb,
        created_by: profile?.id, created_by_name: profile?.full_name || "unknown"
      });
      if (error) throw error;
      showToast("بکاپ با موفقیت ذخیره شد ✓ (" + sizeKb + " KB)");
      setLabel(""); loadBackups();
    } catch (e) {
      showToast("خطا در ذخیره بکاپ: " + e.message, "error");
    }
    setLoading(false);
  };

  const downloadBackup = async (backup) => {
    const { data } = await supabase.from("backups").select("data").eq("id", backup.id).single();
    if (!data) return;
    const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = "backup_" + backup.label.replace(/\s/g, "_") + "_" + backup.created_at.slice(0,10) + ".json";
    a.click(); URL.revokeObjectURL(url);
  };

  const restoreBackup = async (backup) => {
    setLoading(true);
    try {
      const { data } = await supabase.from("backups").select("data").eq("id", backup.id).single();
      if (!data) throw new Error("بکاپ پیدا نشد");
      const { data: result, error } = await supabase.rpc("restore_backup", { snapshot: data.data });
      if (error) throw error;
      if (!result.ok) throw new Error(result.error || "خطای ناشناخته");
      showToast("بازیابی موفق — صفحه reload می‌شود...");
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      showToast("خطا در بازیابی: " + e.message, "error");
    }
    setLoading(false);
    setRestoreConfirm(null);
  };

  const importFromFile = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const snap = JSON.parse(ev.target.result);
        if (!snap.accounts) throw new Error("فایل معتبر نیست — کلید accounts یافت نشد");
        setLoading(true);
        const { data: result, error } = await supabase.rpc("restore_backup", { snapshot: snap });
        if (error) throw error;
        if (!result.ok) throw new Error(result.error || "خطای ناشناخته");
        showToast("فایل بکاپ بازیابی شد — صفحه reload می‌شود...");
        setTimeout(() => window.location.reload(), 1500);
      } catch (err) {
        showToast("خطا: " + err.message, "error");
        setLoading(false);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const deleteBackup = async (id) => {
    if (!window.confirm("این بکاپ حذف شود؟")) return;
    await supabase.from("backups").delete().eq("id", id);
    showToast("بکاپ حذف شد"); loadBackups();
  };

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} visible={toast.visible} />
      {restoreConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}>
          <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 16, padding: 32, maxWidth: 420, width: "90%" }}>
            <h3 style={{ marginBottom: 12, fontSize: 18 }}>⚠️ بازیابی بکاپ</h3>
            <p style={{ color: theme.textMuted, marginBottom: 20, lineHeight: 1.6 }}>
              بکاپ <strong style={{ color: theme.text }}>«{restoreConfirm.label}»</strong> بازیابی شود؟<br />
              <span style={{ color: theme.danger, fontSize: 13 }}>داده‌های فعلی با این بکاپ جایگزین می‌شوند!</span>
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Btn variant="ghost" onClick={() => setRestoreConfirm(null)}>انصراف</Btn>
              <Btn variant="danger" onClick={() => restoreBackup(restoreConfirm)}>بله، بازیابی کن</Btn>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>💾 مدیریت بکاپ</h2>
      </div>

      {/* ساخت بکاپ جدید */}
      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>📤 ذخیره بکاپ جدید</h3>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <Input label="برچسب بکاپ" value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="مثال: قبل از ویرایش فاکتور، پایان ماه خرداد ۱۴۰۵" />
          </div>
          <Btn onClick={takeBackup} style={{ marginBottom: 14 }} variant="success">
            {loading ? "در حال ذخیره..." : "💾 ذخیره بکاپ"}
          </Btn>
        </div>
        <div style={{ fontSize: 12, color: theme.textMuted }}>
          بکاپ مستقیم از Supabase گرفته می‌شود
        </div>
      </Card>

      {/* بازیابی از فایل */}
      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>📥 بازیابی از فایل JSON</h3>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <label style={{ display: "inline-block", padding: "9px 20px", background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 8, cursor: "pointer", fontSize: 14, color: theme.text }}>
            📂 انتخاب فایل بکاپ
            <input type="file" accept=".json" onChange={importFromFile} style={{ display: "none" }} />
          </label>
          <span style={{ fontSize: 12, color: theme.textMuted }}>فقط فایل‌های JSON بکاپ این سیستم قابل بازیابی هستند</span>
        </div>
      </Card>

      {/* لیست بکاپ‌ها */}
      <Card>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>📋 بکاپ‌های ذخیره‌شده در سرور ({backups.length})</h3>
        {backups.length === 0 ? (
          <p style={{ color: theme.textMuted, textAlign: "center", padding: 20 }}>هنوز بکاپی ذخیره نشده</p>
        ) : (
          <Table cols={["برچسب", "تاریخ", "حجم", "ذخیره‌کننده", "عملیات"]}
            rows={backups.map(b => ({ cells: [
              <strong>{b.label}</strong>,
              new Date(b.created_at).toLocaleString("fa-IR"),
              b.size_kb ? b.size_kb + " KB" : "-",
              b.created_by_name || "-",
              <div style={{ display: "flex", gap: 4 }}>
                <button onClick={() => downloadBackup(b)}
                  style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${theme.border}`, borderRadius: 6, color: theme.text, cursor: "pointer", fontSize: 12, fontFamily: "Vazirmatn" }}>
                  ⬇️ دانلود
                </button>
                <button onClick={() => setRestoreConfirm(b)}
                  style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${theme.success}55`, borderRadius: 6, color: theme.success, cursor: "pointer", fontSize: 12, fontFamily: "Vazirmatn" }}>
                  🔄 بازیابی
                </button>
                {profile?.role === "admin" && (
                  <button onClick={() => deleteBackup(b.id)}
                    style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${theme.danger}55`, borderRadius: 6, color: theme.danger, cursor: "pointer", fontSize: 12, fontFamily: "Vazirmatn" }}>
                    🗑
                  </button>
                )}
              </div>
            ]}))}
          />
        )}
      </Card>
    </div>
  );
}

// =====================================================
// MIGRATION PAGE — انتقال localStorage به Supabase
// =====================================================
function MigrationPage() {
  const { profile } = useAuth();
  const [logs, setLogs] = useState([]);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [toast, showToast] = useToast();

  const addLog = (msg, ok = true) => {
    setLogs(prev => [...prev, { msg, ok, t: new Date().toLocaleTimeString("fa-IR") }]);
  };

  const runMigration = async () => {
    const raw = localStorage.getItem("acc_data_v2");
    if (!raw) { showToast("داده‌ای در این مرورگر پیدا نشد", "error"); return; }

    if (!window.confirm("داده‌های localStorage به Supabase منتقل می‌شوند. ادامه؟")) return;

    setRunning(true); setLogs([]); setDone(false);
    addLog("شروع migration...");

    let data;
    try { data = JSON.parse(raw); }
    catch { addLog("خطا: داده localStorage معتبر نیست", false); setRunning(false); return; }

    const tables = [
      { key: "accounts", table: "accounts", map: a => ({ id: a.id, name: a.name, type: a.type, currency_code: "USD", opening_balance: parseFloat(a.opening_balance)||0, is_active: a.is_active!==false, is_system: a.is_system||false }) },
      { key: "expenseCategories", table: "expense_categories", map: c => ({ id: c.id, code: c.code||"EXP-000", name: c.name, description: c.description||"", is_active: c.is_active!==false }) },
      { key: "persons", table: "persons", map: p => ({ id: p.id, name: p.name, mobile: p.mobile||"", type: p.type||"customer", country: p.country||"", description: p.description||"", is_active: p.is_active!==false }) },
      { key: "items", table: "items", map: i => ({ id: i.id, name: i.name, sku: i.sku||"", unit: i.unit||"عدد", currency_code: "USD", avg_purchase_price: parseFloat(i.avg_purchase_price)||0, suggested_sale_price: parseFloat(i.suggested_sale_price)||0, opening_stock: parseFloat(i.opening_stock)||0, opening_unit_cost: parseFloat(i.opening_unit_cost)||0, current_stock: parseFloat(i.current_stock)||0, min_stock_alert: parseFloat(i.min_stock_alert)||0, is_active: i.is_active!==false }) },
      { key: "purchases", table: "purchases", map: p => ({ id: p.id, invoice_no: p.invoice_no, voucher_no: p.voucher_no||"", date: p.date, vendor_id: p.vendor_id||null, currency_code: "USD", total_amount: parseFloat(p.total_amount)||0, paid_amount: parseFloat(p.paid_amount)||0, remaining_amount: parseFloat(p.remaining_amount)||0, shipping_cost: parseFloat(p.shipping_cost)||0, customs_cost: parseFloat(p.customs_cost)||0, packing_cost: parseFloat(p.packing_cost)||0, notes: p.notes||"", created_at: p.created_at||new Date().toISOString() }) },
      { key: "purchaseLines", table: "purchase_lines", map: l => ({ id: l.id, purchase_id: l.purchase_id, item_id: l.item_id, quantity: parseFloat(l.quantity)||0, unit_price: parseFloat(l.unit_price)||0, unit_cost_with_overhead: parseFloat(l.unit_cost_with_overhead)||0, overhead_share: parseFloat(l.overhead_share)||0, total_price: parseFloat(l.total_price)||0, total_cost_with_overhead: parseFloat(l.total_cost_with_overhead)||0, currency_code: "USD" }) },
      { key: "sales", table: "sales", map: s => ({ id: s.id, invoice_no: s.invoice_no, voucher_no: s.voucher_no||"", date: s.date, customer_id: s.customer_id||null, currency_code: "USD", total_amount: parseFloat(s.total_amount)||0, final_amount: parseFloat(s.final_amount)||0, received_amount: parseFloat(s.received_amount)||0, remaining_amount: parseFloat(s.remaining_amount)||0, discount_amount: parseFloat(s.discount_amount)||0, receipt_account_id: s.receipt_account_id||null, notes: s.notes||"", created_at: s.created_at||new Date().toISOString() }) },
      { key: "saleLines", table: "sale_lines", map: l => ({ id: l.id, sale_id: l.sale_id, item_id: l.item_id, quantity: parseFloat(l.quantity)||0, unit_price: parseFloat(l.unit_price)||0, discount: parseFloat(l.discount)||0, cost_price: parseFloat(l.cost_price)||0, gross_profit: parseFloat(l.gross_profit)||0, invoice_discount_share: parseFloat(l.invoice_discount_share)||0, total_price: parseFloat(l.total_price)||0, currency_code: "USD" }) },
      { key: "payments", table: "payments", map: p => ({ id: p.id, type: p.type, voucher_no: p.voucher_no||"", date: p.date, person_id: p.person_id||null, currency_code: "USD", amount: parseFloat(p.amount)||0, account_id: p.account_id||null, method: p.method||"cash", reference_no: p.reference_no||"", notes: p.notes||"", created_at: p.created_at||new Date().toISOString() }) },
      { key: "expenses", table: "expenses", map: e => ({ id: e.id, voucher_no: e.voucher_no||"", date: e.date, category_id: e.category_id||null, currency_code: "USD", amount: parseFloat(e.amount)||0, account_id: e.account_id||null, person_id: e.person_id||null, paid_by_person: e.paid_by_person||false, description: e.description||"", created_at: e.created_at||new Date().toISOString() }) },
      { key: "manualVouchers", table: "manual_vouchers", map: v => ({ id: v.id, voucher_no: v.voucher_no, date: v.date, description: v.description||"", total_debit: parseFloat(v.total_debit)||0, total_credit: parseFloat(v.total_credit)||0, is_balanced: v.is_balanced!==false, source_type: v.source_type||"manual", created_at: v.created_at||new Date().toISOString() }) },
      { key: "ledger", table: "ledger", map: l => ({ id: l.id, date: l.date, voucher_id: l.voucher_id||null, voucher_no: l.voucher_no||"", source_type: l.source_type||"", source_id: l.source_id||null, account_id: l.account_id||null, person_id: l.person_id||null, currency_code: "USD", debit: parseFloat(l.debit)||0, credit: parseFloat(l.credit)||0, description: l.description||"", created_at: l.created_at||new Date().toISOString() }) },
    ];

    for (const { key, table, map } of tables) {
      const rows = (data[key] || []);
      if (rows.length === 0) { addLog(table + ": خالی — رد شد"); continue; }
      const mapped = rows.map(map);
      // batch 200
      let hasError = false;
      for (let i = 0; i < mapped.length; i += 200) {
        const { error } = await supabase.from(table).upsert(mapped.slice(i, i+200), { onConflict: "id" });
        if (error) { addLog(table + ": " + error.message, false); hasError = true; break; }
      }
      if (!hasError) addLog(table + ": " + mapped.length + " رکورد ✓");
    }

    addLog("Migration تمام شد!");
    setRunning(false); setDone(true);
    showToast("Migration با موفقیت انجام شد ✓");
  };

  if (profile?.role !== "admin") return (
    <div style={{ padding: 40, textAlign: "center", color: theme.textMuted }}>
      فقط مدیر می‌تواند migration انجام دهد
    </div>
  );

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} visible={toast.visible} />
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20 }}>🚀 Migration: localStorage → Supabase</h2>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 14, fontSize: 14, color: theme.textMuted, lineHeight: 1.8 }}>
          این ابزار داده‌های ذخیره‌شده در مرورگر را به Supabase منتقل می‌کند.<br />
          <span style={{ color: theme.warning }}>⚠️ این عملیات را فقط یک‌بار اجرا کنید.</span>
        </div>
        <Btn onClick={runMigration} disabled={running} variant="primary">
          {running ? "⏳ در حال انتقال..." : "🚀 شروع Migration"}
        </Btn>
        {done && <span style={{ marginRight: 12, color: theme.success, fontWeight: 700 }}>✓ تمام شد</span>}
      </Card>
      {logs.length > 0 && (
        <Card>
          <div style={{ fontFamily: "monospace", fontSize: 13 }}>
            {logs.map((l, i) => (
              <div key={i} style={{ padding: "4px 0", color: l.ok ? theme.success : theme.danger, borderBottom: `1px solid ${theme.border}22` }}>
                <span style={{ color: theme.textMuted, marginLeft: 10 }}>{l.t}</span>
                {l.ok ? "✓" : "✗"} {l.msg}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// =====================================================
// COMPANY SETTINGS PAGE
// =====================================================
function CompanySettingsPage() {
  const { profile } = useAuth();
  const [form, setForm] = useState({ company_name: "", company_phone: "", company_address: "" });
  const [loading, setLoading] = useState(false);
  const [toast, showToast] = useToast();

  useEffect(() => {
    supabase.from("company_settings").select("*").eq("id", 1).single()
      .then(({ data }) => { if (data) setForm({ company_name: data.company_name || "", company_phone: data.company_phone || "", company_address: data.company_address || "" }); });
  }, []);

  const save = async () => {
    if (profile?.role !== "admin") { showToast("فقط مدیر می‌تواند تنظیمات را تغییر دهد", "error"); return; }
    setLoading(true);
    const { error } = await supabase.from("company_settings").update({ ...form, updated_by: profile?.id, updated_at: new Date().toISOString() }).eq("id", 1);
    if (error) showToast("خطا: " + error.message, "error");
    else showToast("تنظیمات ذخیره شد ✓");
    setLoading(false);
  };

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} visible={toast.visible} />
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20 }}>⚙️ تنظیمات شرکت</h2>
      <Card style={{ maxWidth: 600 }}>
        <Input label="نام شرکت" value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} />
        <Input label="تلفن" value={form.company_phone} onChange={e => setForm(p => ({ ...p, company_phone: e.target.value }))} />
        <Textarea label="آدرس" value={form.company_address} onChange={e => setForm(p => ({ ...p, company_address: e.target.value }))} />
        {profile?.role !== "admin" && (
          <div style={{ background: `${theme.warning}22`, border: `1px solid ${theme.warning}44`, borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 12, color: theme.warning }}>
            ⚠️ فقط مدیر می‌تواند تنظیمات را تغییر دهد
          </div>
        )}
        <Btn onClick={save} disabled={loading || profile?.role !== "admin"}>
          {loading ? "در حال ذخیره..." : "ذخیره تنظیمات"}
        </Btn>
      </Card>
    </div>
  );
}


const initData = {
  accounts: [
    { id: "acc_cash_usd",   name: "صندوق نقدی",              type: "cash",           currency_code: "USD", opening_balance: 0, is_active: true, is_system: true },
    { id: "acc_bank_usd",   name: "بانک",                     type: "bank",           currency_code: "USD", opening_balance: 0, is_active: true, is_system: true },
    { id: "acc_person",     name: "کنترل اشخاص",              type: "person_control", currency_code: "USD", opening_balance: 0, is_active: true, is_system: true },
    { id: "acc_income",     name: "درآمد فروش",               type: "income",         currency_code: "USD", opening_balance: 0, is_active: true, is_system: true },
    { id: "acc_cogs",       name: "بهای تمام‌شده (COGS)",     type: "cogs",           currency_code: "USD", opening_balance: 0, is_active: true, is_system: true },
    { id: "acc_inv",        name: "موجودی کالا",              type: "inventory",      currency_code: "USD", opening_balance: 0, is_active: true, is_system: true },
    { id: "acc_exp",        name: "هزینه‌ها",                  type: "expense",        currency_code: "USD", opening_balance: 0, is_active: true, is_system: true }
  ],
  expenseCategories: [
    { id: "ec1", code: "EXP-001", name: "اجاره",       is_active: true, description: "" },
    { id: "ec2", code: "EXP-002", name: "حقوق",        is_active: true, description: "" },
    { id: "ec3", code: "EXP-003", name: "حمل‌ونقل",    is_active: true, description: "" },
    { id: "ec4", code: "EXP-004", name: "تبلیغات",     is_active: true, description: "" },
    { id: "ec5", code: "EXP-005", name: "اینترنت",     is_active: true, description: "" },
    { id: "ec6", code: "EXP-006", name: "کارمزد بانک", is_active: true, description: "" },
    { id: "ec7", code: "EXP-007", name: "دفتر",        is_active: true, description: "" },
    { id: "ec8", code: "EXP-008", name: "سایر",        is_active: true, description: "" }
  ],
  persons: [], items: [],
  purchases: [], purchaseLines: [],
  sales: [], saleLines: [],
  payments: [], expenses: [],
  manualVouchers: [], manualVoucherLines: [],
  ledger: []
};

// [فاز۲-آیتم۲۰] Migration امن: داده‌های قدیمی حفظ می‌شوند
const SYSTEM_ACCOUNT_IDS = ["acc_cash_usd","acc_bank_usd","acc_person","acc_income","acc_cogs","acc_inv","acc_exp"];

const migrateStore = (data) => {
  const seen = new Set();
  const cleaned = (data.accounts || []).filter(a => { if (seen.has(a.id)) return false; seen.add(a.id); return true; });
  const result = cleaned.map(a => ({ ...a, currency_code: "USD", is_system: SYSTEM_ACCOUNT_IDS.includes(a.id) ? true : (a.is_system || false) }));

  const cogsIdx = result.findIndex(a => a.id === "acc_cogs");
  if (cogsIdx >= 0 && result[cogsIdx].type === "expense") result[cogsIdx] = { ...result[cogsIdx], type: "cogs" };

  const ea = (list, acc) => { if (!list.some(a => a.id === acc.id)) list.push(acc); };
  ea(result, { id: "acc_cash_usd", name: "صندوق نقدی",         type: "cash",           currency_code: "USD", opening_balance: 0, is_active: true, is_system: true });
  ea(result, { id: "acc_bank_usd", name: "بانک",                type: "bank",           currency_code: "USD", opening_balance: 0, is_active: true, is_system: true });
  ea(result, { id: "acc_person",   name: "کنترل اشخاص",         type: "person_control", currency_code: "USD", opening_balance: 0, is_active: true, is_system: true });
  ea(result, { id: "acc_income",   name: "درآمد فروش",          type: "income",         currency_code: "USD", opening_balance: 0, is_active: true, is_system: true });
  ea(result, { id: "acc_cogs",     name: "بهای تمام‌شده (COGS)",type: "cogs",           currency_code: "USD", opening_balance: 0, is_active: true, is_system: true });
  ea(result, { id: "acc_inv",      name: "موجودی کالا",         type: "inventory",      currency_code: "USD", opening_balance: 0, is_active: true, is_system: true });
  ea(result, { id: "acc_exp",      name: "هزینه‌ها",             type: "expense",        currency_code: "USD", opening_balance: 0, is_active: true, is_system: true });

  const migratedItems = (data.items || []).map(item => ({
    ...item, currency_code: "USD",
    opening_unit_cost: item.opening_unit_cost != null ? item.opening_unit_cost : (item.avg_purchase_price != null ? item.avg_purchase_price : 0)
  }));
  const migratedLedger = (data.ledger || []).map(l => ({ ...l, currency_code: "USD" }));

  // migration expenseCategories — اضافه کردن code/is_active/description به دسته‌های قدیمی
  const migratedCats = (data.expenseCategories || []).map((c, idx) => ({
    ...c,
    code: c.code || ("EXP-" + String(idx + 1).padStart(3, "0")),
    is_active: c.is_active !== false,
    description: c.description || ""
  }));

  return { ...initData, ...data, accounts: result, items: migratedItems, ledger: migratedLedger, expenseCategories: migratedCats };
};

const loadStore = () => initData; // داده از Supabase می‌آید، نه localStorage
const saveStore = () => {}; // no-op — ذخیره در Supabase انجام می‌شود

// =====================================================
// SUPABASE DATA LOADER
// =====================================================
const loadFromSupabase = async () => {
  const [
    { data: accounts },
    { data: persons },
    { data: items },
    { data: expCats },
    { data: purchases },
    { data: purchaseLines },
    { data: sales },
    { data: saleLines },
    { data: payments },
    { data: expenses },
    { data: manualVouchers },
    { data: ledger }
  ] = await Promise.all([
    supabase.from("accounts").select("*").order("name"),
    supabase.from("persons").select("*").order("name"),
    supabase.from("items").select("*").order("name"),
    supabase.from("expense_categories").select("*").order("code"),
    supabase.from("purchases").select("*").order("date", { ascending: false }),
    supabase.from("purchase_lines").select("*"),
    supabase.from("sales").select("*").order("date", { ascending: false }),
    supabase.from("sale_lines").select("*"),
    supabase.from("payments").select("*").order("date", { ascending: false }),
    supabase.from("expenses").select("*").order("date", { ascending: false }),
    supabase.from("manual_vouchers").select("*").order("date", { ascending: false }),
    supabase.from("ledger").select("*").order("date", { ascending: false })
  ]);

  return {
    accounts: accounts || initData.accounts,
    persons: persons || [],
    items: items || [],
    expenseCategories: (expCats || initData.expenseCategories).map(c => ({
      id: c.id, code: c.code, name: c.name, description: c.description || "", is_active: c.is_active
    })),
    purchases: purchases || [],
    purchaseLines: (purchaseLines || []).map(l => ({ ...l, purchase_id: l.purchase_id })),
    sales: sales || [],
    saleLines: (saleLines || []).map(l => ({ ...l, sale_id: l.sale_id })),
    payments: payments || [],
    expenses: expenses || [],
    manualVouchers: manualVouchers || [],
    manualVoucherLines: [],
    ledger: ledger || []
  };
};

// Supabase writer — همه عملیات نوشتن
const sbWrite = {
  async upsert(table, row) {
    const { error } = await supabase.from(table).upsert(row, { onConflict: "id" });
    if (error) console.error("upsert", table, error.message);
    return error;
  },
  async upsertMany(table, rows) {
    if (!rows || rows.length === 0) return null;
    for (let i = 0; i < rows.length; i += 100) {
      const { error } = await supabase.from(table).upsert(rows.slice(i, i + 100), { onConflict: "id" });
      if (error) { console.error("upsertMany", table, error.message); return error; }
    }
    return null;
  },
  async delete(table, id) {
    const { error } = await supabase.from(table).delete().eq("id", id);
    if (error) console.error("delete", table, error.message);
    return error;
  },
  async deleteWhere(table, col, val) {
    const { error } = await supabase.from(table).delete().eq(col, val);
    if (error) console.error("deleteWhere", table, error.message);
    return error;
  }
};

// Audit log helper
const auditLog = async (action, table, recordId, details, userId) => {
  await supabase.from("audit_log").insert({
    action, table_name: table, record_id: recordId,
    details: details || {}, performed_by: userId,
    performed_at: new Date().toISOString()
  }).then(() => {}); // fire and forget
};

// =====================================================
// MAIN APP
// =====================================================
export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}

function AppInner() {
  const { user, profile, authLoading, signOut } = useAuth();
  const [data, setData] = useState(initData);
  const [dbLoading, setDbLoading] = useState(true);
  const [page, setPage] = useState("dashboard");
  const [sideOpen, setSideOpen] = useState(true);

  // بارگذاری داده از Supabase بعد از login
  useEffect(() => {
    if (user) {
      setDbLoading(true);
      loadFromSupabase().then(d => {
        setData(d);
        setDbLoading(false);
      }).catch(e => {
        console.error("loadFromSupabase:", e);
        setDbLoading(false);
      });
    }
  }, [user]);

  // تابع refresh — بعد از هر عملیات داده را از Supabase reload می‌کند
  const refresh = useCallback(async () => {
    const d = await loadFromSupabase();
    setData(d);
  }, []);

  // update — برای سازگاری با کد قدیمی (localStorage) — فقط state را آپدیت می‌کند
  const update = useCallback((updater) => {
    setData(prev => updater(prev));
  }, []);

  const ctx = { data, update, refresh, sbWrite, auditLog, profile };

  if (authLoading) return (
    <div style={{ minHeight: "100vh", background: theme.bg, display: "flex", alignItems: "center", justifyContent: "center", color: theme.textMuted, fontFamily: "Vazirmatn, sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>💎</div>
        <div>در حال بارگذاری...</div>
      </div>
    </div>
  );

  if (!user) return <LoginPage />;

  if (profile && !profile.is_active) return (
    <div style={{ minHeight: "100vh", background: theme.bg, display: "flex", alignItems: "center", justifyContent: "center", color: theme.textMuted, fontFamily: "Vazirmatn, sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>🚫</div>
        <div style={{ color: theme.danger, fontSize: 16, marginBottom: 12 }}>حساب شما غیرفعال است</div>
        <button onClick={signOut} style={{ padding: "8px 20px", background: theme.border, border: "none", borderRadius: 8, color: theme.text, cursor: "pointer", fontFamily: "Vazirmatn" }}>خروج</button>
      </div>
    </div>
  );

  if (dbLoading) return (
    <div style={{ minHeight: "100vh", background: theme.bg, display: "flex", alignItems: "center", justifyContent: "center", color: theme.textMuted, fontFamily: "Vazirmatn, sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
        <div>در حال بارگذاری داده‌ها...</div>
      </div>
    </div>
  );

  const isViewer = profile?.role === "viewer";
  const isAdmin = profile?.role === "admin";

  const nav = [
    { key: "dashboard", icon: "🏠", label: "داشبورد" },
    { key: "sep1", type: "sep", label: "عملیات" },
    ...(!isViewer ? [
      { key: "purchases", icon: "📦", label: "خرید" },
      { key: "sales", icon: "🛒", label: "فروش" },
      { key: "receive", icon: "💰", label: "دریافت از شخص" },
      { key: "pay", icon: "💸", label: "پرداخت به شخص" },
      { key: "transfer", icon: "🔄", label: "انتقال بین اشخاص" },
      { key: "expenses", icon: "🧾", label: "هزینه" },
      { key: "manual", icon: "📝", label: "سند دستی" },
    ] : []),
    { key: "sep2", type: "sep", label: "پایه" },
    ...(!isViewer ? [
      { key: "persons", icon: "👥", label: "اشخاص" },
      { key: "items", icon: "📋", label: "کالاها" },
      { key: "accounts", icon: "🏦", label: "حساب‌ها" },
      { key: "expense_cats", icon: "🗂️", label: "دسته‌های هزینه" },
    ] : []),
    { key: "sep3", type: "sep", label: "گزارش‌ها" },
    { key: "rpt_persons", icon: "📊", label: "مانده اشخاص" },
    { key: "rpt_statement", icon: "📑", label: "ریز حساب شخص" },
    { key: "rpt_inventory", icon: "📦", label: "موجودی کالا" },
    { key: "rpt_cashflow", icon: "💳", label: "صندوق و بانک" },
    { key: "rpt_profit", icon: "📈", label: "سود و زیان" },
    { key: "rpt_kardex", icon: "🗂", label: "کاردکس کالا" },
    { key: "rpt_expenses", icon: "🧾", label: "گزارش هزینه‌ها" },
    { key: "ledger", icon: "📒", label: "دفتر کل" },
    { key: "sep4", type: "sep", label: "سیستم" },
    { key: "backup", icon: "💾", label: "بکاپ و بازیابی" },
    { key: "company_settings", icon: "⚙️", label: "تنظیمات شرکت" },
    ...(isAdmin ? [
      { key: "users", icon: "👤", label: "مدیریت کاربران" },
      { key: "migration", icon: "🚀", label: "Migration داده‌ها" },
    ] : []),
  ];

  const pages = {
    dashboard: <Dashboard />, purchases: <PurchasePage />, sales: <SalePage />,
    receive: <PaymentPage type="receive" />, pay: <PaymentPage type="pay" />,
    expenses: <ExpensePage />, manual: <ManualVoucherPage />, transfer: <PersonTransferPage />,
    persons: <PersonsPage />, items: <ItemsPage />, accounts: <AccountsPage />,
    expense_cats: <ExpenseCategoriesPage />,
    rpt_persons: <ReportPersonBalances />, rpt_statement: <ReportPersonStatement />,
    rpt_inventory: <ReportInventory />, rpt_cashflow: <ReportCashFlow />,
    rpt_profit: <ReportProfit />, rpt_kardex: <ReportKardex />,
    rpt_expenses: <ReportExpenses />, ledger: <LedgerPage />,
    backup: <BackupPage />,
    company_settings: <CompanySettingsPage />,
    users: isAdmin ? <UsersPage /> : <Dashboard />,
    migration: isAdmin ? <MigrationPage /> : <Dashboard />,
  };

  const roleLabel = { admin: "مدیر", accountant: "حسابدار", viewer: "فقط‌خواندنی" };
  const roleColor = { admin: theme.danger, accountant: theme.accent, viewer: theme.textMuted };

  return (
    <AppContext.Provider value={ctx}>
      <GlobalStyle />
      <div style={{ display: "flex", minHeight: "100vh" }}>
        <div style={{ width: sideOpen ? 270 : 64, background: theme.bgCard, borderLeft: `1px solid ${theme.border}`, transition: "width 0.3s", overflow: "hidden", display: "flex", flexDirection: "column", position: "fixed", right: 0, top: 0, bottom: 0, zIndex: 100 }}>
          <div style={{ padding: "16px 14px", borderBottom: `1px solid ${theme.border}`, display: "flex", alignItems: "center", gap: 10 }}>
            {sideOpen && <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.gold }}>💎 سیستم حسابداری</div>
              {profile && (
                <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ color: roleColor[profile.role] || theme.textMuted, fontSize: 10 }}>●</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{profile.full_name}</span>
                  <span style={{ color: roleColor[profile.role] || theme.textMuted }}>({roleLabel[profile.role] || profile.role})</span>
                </div>
              )}
            </div>}
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <button onClick={() => setSideOpen(p => !p)} style={{ background: "none", border: "none", color: theme.textMuted, fontSize: 18, cursor: "pointer", padding: 4 }}>☰</button>
              {sideOpen && <button onClick={signOut} title="خروج"
                style={{ background: "none", border: `1px solid ${theme.border}`, borderRadius: 6, color: theme.textMuted, fontSize: 12, cursor: "pointer", padding: "4px 8px", fontFamily: "Vazirmatn" }}>
                خروج
              </button>}
            </div>
          </div>
          <div style={{ overflowY: "auto", flex: 1, padding: "8px 0" }}>
            {nav.map(item => {
              if (item.type === "sep") return sideOpen
                ? <div key={item.key} style={{ padding: "12px 16px 4px", fontSize: 10, color: theme.textMuted, fontWeight: 700, letterSpacing: 1 }}>{item.label}</div>
                : <div key={item.key} style={{ borderTop: `1px solid ${theme.border}22`, margin: "6px 8px" }} />;
              const active = page === item.key;
              return (
                <button key={item.key} onClick={() => setPage(item.key)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 18px", background: active ? `${theme.accent}22` : "transparent", border: "none", borderRight: active ? `3px solid ${theme.accent}` : "3px solid transparent", color: active ? theme.accentLight : theme.textMuted, textAlign: "right", fontSize: 14, fontWeight: active ? 600 : 400, transition: "all 0.15s" }}>
                  <span style={{ fontSize: 16 }}>{item.icon}</span>
                  {sideOpen && item.label}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ flex: 1, marginRight: sideOpen ? 270 : 64, transition: "margin 0.3s" }}>
          <div className="fade-in" style={{ padding: 32, maxWidth: 1400 }}>
            {pages[page] || <Dashboard />}
          </div>
        </div>
      </div>
    </AppContext.Provider>
  );
}

// =====================================================
// DASHBOARD
// =====================================================
function Dashboard() {
  const { data } = useApp();
  const { profile } = useAuth();
  const isAdmin = profile?.role === "admin";
  const ledger = data.ledger || [];
  const thisMonth = new Date().toISOString().slice(0, 7);
  const monthlySales = (data.sales || []).filter(s => s.date?.startsWith(thisMonth)).reduce((sum, s) => sum + (s.final_amount || 0), 0);
  const lowStock = (data.items || []).filter(i => i.min_stock_alert > 0 && i.current_stock <= i.min_stock_alert);
  const debtorCount = (data.persons || []).filter(p => {
    const bal = ledger.filter(l => l.person_id === p.id).reduce((s, l) => s + (l.debit || 0) - (l.credit || 0), 0);
    return bal > 0;
  }).length;

  const cashAccounts = (data.accounts || []).filter(a => a.type === "cash" || a.type === "bank").map(a => {
    const entries = ledger.filter(l => l.account_id === a.id);
    const balance = (a.opening_balance || 0) + entries.reduce((s, l) => s + (l.debit || 0) - (l.credit || 0), 0);
    return { ...a, balance };
  });

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>داشبورد</h1>
        <p style={{ color: theme.textMuted }}>{new Date().toLocaleDateString("fa-IR", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
      </div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
        <Stat label="فروش این ماه (USD)" value={fmtMoney(monthlySales, "USD")} color={theme.success} icon="💰" />
        <Stat label="مشتریان بدهکار" value={debtorCount + " نفر"} color={theme.warning} icon="👥" />
        <Stat label="کالاهای کم‌موجودی" value={lowStock.length + " قلم"} color={theme.danger} icon="⚠️" />
        <Stat label="کل تراکنش‌ها" value={ledger.length + " سند"} color={theme.accent} icon="📒" />
      </div>
      <Card style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 16, fontSize: 16 }}>💳 موجودی صندوق‌ها و بانک‌ها</h3>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {cashAccounts.map(a => (
            <div key={a.id} style={{ flex: 1, minWidth: 160, padding: "14px 18px", background: theme.bgInput, borderRadius: 10, border: `1px solid ${theme.border}` }}>
              <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 6 }}>{a.name}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: a.balance >= 0 ? theme.success : theme.danger }}>{fmtMoney(a.balance, a.currency_code)}</div>
            </div>
          ))}
        </div>
      </Card>
      {/* دکمه پاک‌سازی — فقط برای admin، با double-check نقش */}
      {isAdmin && (
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
        <button onClick={async () => {
          if (profile?.role !== "admin") { alert("دسترسی ندارید"); return; }
          if (!window.confirm("آیا می‌خواهید همه تراکنش‌ها (Ledger) را پاک کنید و دوباره شروع کنید؟\nاشخاص، کالاها و حساب‌ها حفظ می‌شوند.")) return;
          try {
            const { data: res, error } = await supabase.rpc("admin_reset_transactions");
            if (error || !res?.ok) {
              alert("خطا: " + (error?.message || res?.error || "اجازه دسترسی ندارید"));
              return;
            }
            window.location.reload();
          } catch (err) { alert("خطا: " + err.message); }
        }} style={{ padding: "7px 16px", background: "transparent", border: `1px solid ${theme.danger}55`, borderRadius: 8, color: theme.danger, fontSize: 12, cursor: "pointer", fontFamily: "Vazirmatn" }}>
          🗑 پاک‌سازی تراکنش‌ها (شروع مجدد)
        </button>
      </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Card>
          <h3 style={{ marginBottom: 14, fontSize: 15 }}>📦 آخرین خریدها</h3>
          {(data.purchases || []).slice(0, 5).map(p => (
            <div key={p.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${theme.border}22`, fontSize: 13 }}>
              <span style={{ color: theme.textMuted }}>{p.date}</span>
              <span>{(data.persons || []).find(x => x.id === p.vendor_id)?.name || "-"}</span>
              <span style={{ color: theme.danger }}>{fmtMoney(p.total_amount, p.currency_code)}</span>
            </div>
          ))}
          {!(data.purchases || []).length && <p style={{ color: theme.textMuted, fontSize: 13 }}>خریدی ثبت نشده</p>}
        </Card>
        <Card>
          <h3 style={{ marginBottom: 14, fontSize: 15 }}>🛒 آخرین فروش‌ها</h3>
          {(data.sales || []).slice(0, 5).map(s => (
            <div key={s.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${theme.border}22`, fontSize: 13 }}>
              <span style={{ color: theme.textMuted }}>{s.date}</span>
              <span>{(data.persons || []).find(x => x.id === s.customer_id)?.name || "-"}</span>
              <span style={{ color: theme.success }}>{fmtMoney(s.final_amount, s.currency_code)}</span>
            </div>
          ))}
          {!(data.sales || []).length && <p style={{ color: theme.textMuted, fontSize: 13 }}>فروشی ثبت نشده</p>}
        </Card>
      </div>
    </div>
  );
}

// =====================================================
// PERSONS PAGE
// =====================================================
function PersonsPage() {
  const { data, update } = useApp();
  const [toast, showToast] = useToast();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { name: "", mobile: "", type: "customer", country: "", description: "", is_active: true };
  const [form, setForm] = useState(emptyForm);
  const typeLabel = { customer: "مشتری", vendor: "فروشنده", both: "هر دو", employee: "کارمند", broker: "صراف", company: "شرکت" };

  const openAdd = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...p }); setModal(true); };

  const { sbWrite, refresh, profile: myProfile, auditLog } = useSb();

  const save = async () => {
    if (!form.name.trim()) return showToast("نام الزامی است", "error");
    const isNew = !editing;
    const row = { ...form, id: editing || uid(), updated_at: new Date().toISOString() };
    const error = await sbWrite.upsert("persons", row);
    if (error) { showToast("خطا: " + error.message, "error"); return; }
    await auditLog(isNew ? "insert" : "update", "persons", row.id, { name: row.name }, myProfile?.id);
    await refresh();
    setModal(false);
    showToast(isNew ? "شخص با موفقیت ثبت شد ✓" : "شخص با موفقیت ویرایش شد ✓");
  };

  const getBalance = (personId) =>
    (data.ledger || []).filter(l => l.person_id === personId && l.currency_code === "USD").reduce((s, l) => s + (l.debit || 0) - (l.credit || 0), 0);

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} visible={toast.visible} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>👥 اشخاص</h2>
        <Btn onClick={openAdd}>+ افزودن شخص</Btn>
      </div>
      <Card>
        <Table cols={["نام", "موبایل", "نوع", "کشور", "مانده USD", "وضعیت", "ویرایش"]} searchable
          rows={(data.persons || []).map(p => ({ cells: [
            p.name, p.mobile || "-",
            <Badge color={p.type === "customer" ? theme.success : p.type === "vendor" ? theme.warning : theme.accent}>{typeLabel[p.type]}</Badge>,
            p.country || "-",
            <span style={{ color: getBalance(p.id) < 0 ? theme.danger : theme.text }}>{fmtMoney(getBalance(p.id), "USD")}</span>,
            <Badge color={p.is_active ? theme.success : theme.textMuted}>{p.is_active ? "فعال" : "غیرفعال"}</Badge>,
            <button onClick={()=>openEdit(p)} style={{padding:"4px 10px",background:"transparent",border:"1px solid "+theme.border,borderRadius:6,color:theme.text,cursor:"pointer",fontSize:11,fontFamily:"Vazirmatn"}}>✏️ ویرایش</button>
          ]}))}
        />
      </Card>
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "ویرایش شخص" : "افزودن شخص"}>
        <Input label="نام *" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
        <Input label="موبایل" value={form.mobile} onChange={e => setForm(p => ({ ...p, mobile: e.target.value }))} />
        <Select label="نوع *" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
          <option value="customer">مشتری</option><option value="vendor">فروشنده</option>
          <option value="both">هر دو</option><option value="employee">کارمند</option>
          <option value="broker">صراف</option><option value="company">شرکت</option>
        </Select>
        <Input label="کشور" value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} />
        <Textarea label="توضیحات" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
        <Select label="وضعیت" value={String(form.is_active)} onChange={e => setForm(p => ({ ...p, is_active: e.target.value === "true" }))}>
          <option value="true">فعال</option><option value="false">غیرفعال</option>
        </Select>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setModal(false)}>انصراف</Btn>
          <Btn onClick={save}>ذخیره</Btn>
        </div>
      </Modal>
    </div>
  );
}

// =====================================================
// ITEMS PAGE
// =====================================================
function ItemsPage() {
  const { data } = useApp();
  const { sbWrite, refresh, profile: myProfile, auditLog } = useSb();
  const [toast, showToast] = useToast();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = {
    name: "", sku: "", unit: "عدد",
    opening_stock: 0, opening_unit_cost: 0,
    avg_purchase_price: 0, suggested_sale_price: 0,
    current_stock: 0, currency_code: "USD",
    min_stock_alert: 0, is_active: true
  };
  const [form, setForm] = useState(emptyForm);

  const save = async () => {
    if (!form.name.trim()) return showToast("نام کالا الزامی است", "error");
    const isNew = !editing;
    let row;
    if (editing) {
      const item = (data.items || []).find(i => i.id === editing);
      const hasTx = (data.purchaseLines || []).some(l => l.item_id === editing) ||
                    (data.saleLines || []).some(l => l.item_id === editing);
      const safeForm = hasTx
        ? { ...form, opening_stock: item.opening_stock, opening_unit_cost: item.opening_unit_cost }
        : form;
      row = { ...safeForm, id: editing, updated_at: new Date().toISOString() };
    } else {
      row = {
        ...form, id: uid(),
        current_stock: parseFloat(form.opening_stock) || 0,
        avg_purchase_price: parseFloat(form.opening_unit_cost) || parseFloat(form.avg_purchase_price) || 0,
        created_at: new Date().toISOString(), updated_at: new Date().toISOString()
      };
    }
    const error = await sbWrite.upsert("items", row);
    if (error) { showToast("خطا: " + error.message, "error"); return; }
    await auditLog(isNew ? "insert" : "update", "items", row.id, { name: row.name }, myProfile?.id);
    await refresh();
    setModal(false);
    showToast(isNew ? "کالا با موفقیت ثبت شد ✓" : "کالا با موفقیت ویرایش شد ✓");
  };

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} visible={toast.visible} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>📋 کالاها</h2>
        <Btn onClick={() => { setEditing(null); setForm(emptyForm); setModal(true); }}>+ افزودن کالا</Btn>
      </div>
      <Card>
        <Table cols={["نام", "کد", "واحد", "موجودی", "قیمت خرید میانگین", "قیمت فروش", "ارزش موجودی", "وضعیت", "ویرایش"]} searchable
          rows={(data.items || []).map(item => ({ cells: [
            item.name, item.sku || "-", item.unit,
            <span style={{ color: item.min_stock_alert > 0 && item.current_stock <= item.min_stock_alert ? theme.danger : theme.text }}>{fmtQty(item.current_stock)}</span>,
            fmtMoney(item.avg_purchase_price, item.currency_code),
            fmtMoney(item.suggested_sale_price, item.currency_code),
            fmtMoney(item.current_stock * item.avg_purchase_price, item.currency_code),
            <Badge color={item.is_active ? theme.success : theme.textMuted}>{item.is_active ? "فعال" : "غیرفعال"}</Badge>,
            <button onClick={()=>{setEditing(item.id);setForm({...item, opening_unit_cost: item.opening_unit_cost != null ? item.opening_unit_cost : item.avg_purchase_price});setModal(true)}} style={{padding:"4px 10px",background:"transparent",border:"1px solid "+theme.border,borderRadius:6,color:theme.text,cursor:"pointer",fontSize:11,fontFamily:"Vazirmatn"}}>✏️ ویرایش</button>
          ]}))}
        />
      </Card>
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "ویرایش کالا" : "افزودن کالا"}>
        <Input label="نام کالا *" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
        <Input label="کد / SKU" value={form.sku} onChange={e => setForm(p => ({ ...p, sku: e.target.value }))} />
        <Select label="واحد" value={form.unit} onChange={e => setForm(p => ({ ...p, unit: e.target.value }))}>
          {["عدد", "گرم", "کیلوگرم", "تن", "متر", "لیتر"].map(u => <option key={u} value={u}>{u}</option>)}
        </Select>
        <Select label="ارز" value={form.currency_code} onChange={e => setForm(p => ({ ...p, currency_code: e.target.value }))}>
          <option value="USD">USD — دلار</option>
        </Select>
        {(() => {
          const hasTx = editing && (
            (data.purchaseLines || []).some(l => l.item_id === editing) ||
            (data.saleLines || []).some(l => l.item_id === editing)
          );
          return hasTx ? (
            <div style={{ background: `${theme.warning}22`, border: `1px solid ${theme.warning}44`, borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 12, color: theme.warning }}>
              ⚠️ این کالا تراکنش دارد. موجودی اولیه و قیمت تمام‌شده اولیه قفل هستند.
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <Input label="موجودی اولیه" type="number" value={form.opening_stock} onChange={e => setForm(p => ({ ...p, opening_stock: e.target.value }))} />
              <Input label="قیمت تمام‌شده اولیه (هر واحد)" type="number" value={form.opening_unit_cost}
                onChange={e => setForm(p => ({ ...p, opening_unit_cost: e.target.value }))}
              />
            </div>
          );
        })()}
        <Input label="قیمت فروش پیشنهادی" type="number" value={form.suggested_sale_price} onChange={e => setForm(p => ({ ...p, suggested_sale_price: e.target.value }))} />
        <Input label="حداقل موجودی هشدار" type="number" value={form.min_stock_alert} onChange={e => setForm(p => ({ ...p, min_stock_alert: e.target.value }))} />
        {form.opening_unit_cost > 0 && !editing && (
          <div style={{ background: `${theme.accent}11`, border: `1px solid ${theme.accent}33`, borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 12, color: theme.accentLight }}>
            ℹ️ ارزش موجودی اولیه: {fmtMoney((parseFloat(form.opening_stock)||0) * (parseFloat(form.opening_unit_cost)||0), "USD")}
          </div>
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setModal(false)}>انصراف</Btn>
          <Btn onClick={save}>ذخیره</Btn>
        </div>
      </Modal>
    </div>
  );
}

// =====================================================
// ACCOUNTS PAGE
// =====================================================
function AccountsPage() {
  const { data } = useApp();
  const { sbWrite, refresh, profile: myProfile, auditLog } = useSb();
  const [toast, showToast] = useToast();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { name: "", type: "cash", currency_code: "USD", opening_balance: 0, is_active: true };
  const [form, setForm] = useState(emptyForm);
  const typeLabel = { cash: "نقدی", bank: "بانک", receivable: "دریافتنی", payable: "پرداختنی", income: "درآمد", expense: "هزینه", inventory: "انبار", equity: "حقوق صاحبان", cogs: "بهای تمام‌شده", person_control: "کنترل اشخاص" };

  const getBalance = (acc) => {
    const entries = (data.ledger || []).filter(l => l.account_id === acc.id);
    return (acc.opening_balance || 0) + entries.reduce((s, l) => s + (l.debit || 0) - (l.credit || 0), 0);
  };

  const save = async () => {
    if (!form.name.trim()) return showToast("نام حساب الزامی است", "error");
    let row;
    if (editing) {
      const existing = (data.accounts || []).find(a => a.id === editing);
      row = existing?.is_system
        ? { ...existing, name: form.name, opening_balance: form.opening_balance, updated_at: new Date().toISOString() }
        : { ...form, id: editing, updated_at: new Date().toISOString() };
    } else {
      row = { ...form, id: uid(), is_system: false, created_at: new Date().toISOString(), updated_at: new Date().toISOString() };
    }
    const error = await sbWrite.upsert("accounts", row);
    if (error) { showToast("خطا: " + error.message, "error"); return; }
    await refresh();
    setModal(false);
    showToast(editing ? "حساب ویرایش شد ✓" : "حساب ثبت شد ✓");
  };

  const openEdit = (a) => {
    setEditing(a.id);
    setForm({ ...a });
    setModal(true);
  };

  const isSystemAcc = editing && (data.accounts || []).find(a => a.id === editing)?.is_system;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>🏦 حساب‌ها</h2>
        <Btn onClick={() => { setEditing(null); setForm(emptyForm); setModal(true); }}>+ افزودن حساب</Btn>
      </div>
      <Card>
        <Table cols={["نام", "نوع", "ارز", "مانده افتتاحیه", "مانده فعلی", "وضعیت", "ویرایش"]}
          rows={(data.accounts || []).map(a => ({ cells: [
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {a.name}
              {a.is_system && <span style={{ fontSize: 10, background: theme.accent+"22", color: theme.accentLight, padding: "1px 6px", borderRadius: 4 }}>سیستمی</span>}
            </span>,
            <Badge color={theme.accent}>{typeLabel[a.type] || a.type}</Badge>,
            a.currency_code, fmtMoney(a.opening_balance),
            <span style={{ fontWeight: 700, color: getBalance(a) >= 0 ? theme.success : theme.danger }}>{fmtMoney(getBalance(a), a.currency_code)}</span>,
            <Badge color={(a.is_active !== false) ? theme.success : theme.textMuted}>{(a.is_active !== false) ? "فعال" : "غیرفعال"}</Badge>,
            <button onClick={() => openEdit(a)} style={{padding:"4px 10px",background:"transparent",border:"1px solid "+theme.border,borderRadius:6,color:theme.text,cursor:"pointer",fontSize:11,fontFamily:"Vazirmatn"}}>✏️ ویرایش</button>
          ]}))}
        />
      </Card>
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "ویرایش حساب" : "افزودن حساب"}>
        {isSystemAcc && (
          <div style={{ background: `${theme.warning}22`, border: `1px solid ${theme.warning}44`, borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 12, color: theme.warning }}>
            ⚠️ این حساب سیستمی است. فقط نام و مانده افتتاحیه قابل تغییر است.
          </div>
        )}
        <Input label="نام حساب *" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
        {!isSystemAcc && (
          <>
            <Select label="نوع" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
              <option value="cash">نقدی</option><option value="bank">بانک</option>
              <option value="receivable">دریافتنی</option><option value="payable">پرداختنی</option>
              <option value="income">درآمد</option><option value="expense">هزینه</option>
              <option value="inventory">انبار</option><option value="equity">حقوق صاحبان</option>
            </Select>
            <CurrencySelect label="ارز" value={form.currency_code} onChange={e => setForm(p => ({ ...p, currency_code: e.target.value }))} />
          </>
        )}
        <Input label="مانده افتتاحیه" type="number" value={form.opening_balance} onChange={e => setForm(p => ({ ...p, opening_balance: parseFloat(e.target.value) || 0 }))} />
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setModal(false)}>انصراف</Btn>
          <Btn onClick={save}>ذخیره</Btn>
        </div>
      </Modal>
    </div>
  );
}

// =====================================================
// QUICK PERSON MODAL — افزودن سریع شخص از داخل فاکتور
// =====================================================
function QuickPersonModal({ open, onClose, defaultType, onCreated }) {
  const { sbWrite, refresh, profile: myProfile, auditLog } = useSb();
  const [toast, showToast] = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", type: defaultType || "customer", country: "", description: "" });

  useEffect(() => {
    if (open) setForm({ name: "", mobile: "", type: defaultType || "customer", country: "", description: "" });
  }, [open, defaultType]);

  const typeLabel = { customer: "مشتری", vendor: "فروشنده", both: "هر دو", employee: "کارمند", broker: "صراف", company: "شرکت" };

  const save = async () => {
    if (!form.name.trim()) return showToast("نام الزامی است", "error");
    setSaving(true);
    const row = {
      id: uid(),
      name: form.name.trim(),
      mobile: form.mobile || "",
      type: form.type,
      country: form.country || "",
      description: form.description || "",
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    const error = await sbWrite.upsert("persons", row);
    if (error) { showToast("خطا: " + error.message, "error"); setSaving(false); return; }
    await auditLog("insert", "persons", row.id, { name: row.name }, myProfile ? myProfile.id : null);
    await refresh();
    showToast("شخص با موفقیت ثبت شد ✓");
    setSaving(false);
    onCreated(row);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="+ افزودن شخص جدید" width={480}>
      <Toast msg={toast.msg} type={toast.type} visible={toast.visible} />
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <Input label="نام شخص *" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
        <Input label="موبایل" value={form.mobile} onChange={e => setForm(p => ({ ...p, mobile: e.target.value }))} />
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", marginBottom: 6, fontSize: 13, color: theme.textMuted }}>نوع شخص</label>
          <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
            style={{ width: "100%", padding: "9px 12px", background: theme.bgInput, border: "1px solid " + theme.border, borderRadius: 8, color: theme.text, fontSize: 14, outline: "none", fontFamily: "Vazirmatn" }}>
            {Object.entries(typeLabel).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <Input label="کشور" value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} />
        <Input label="توضیحات" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <Btn variant="ghost" onClick={onClose}>انصراف</Btn>
          <Btn onClick={save} disabled={saving}>{saving ? "در حال ذخیره..." : "ثبت و انتخاب"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

// =====================================================
// PURCHASE PAGE — شماره فاکتور و سند اتوماتیک
// =====================================================
function PurchasePage() {
  const { data, update } = useApp();
  const { sbWrite, refresh, profile: myProfile, auditLog } = useSb();
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [toast, showToast] = useToast();
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fVendor, setFVendor] = useState("");
  const [quickPersonOpen, setQuickPersonOpen] = useState(false);

  const openModal = () => {
    savingRef.current = false;
    setSaving(false);
    setEditId(null);
    setSubmitted(false);
    setForm({
      invoice_no: genInvoiceNoSafe(data.purchases, "PUR"),
      voucher_no: genVoucherNoAll(data), date: today(),
      vendor_id: "", currency_code: "USD",
      paid_amount: 0, payment_account_id: "",
      shipping_cost: 0, customs_cost: 0, packing_cost: 0, notes: ""
    });
    setLines([{ item_id: "", quantity: 1, unit_price: 0 }]);
    setModal(true);
  };

  const openEdit = (p) => {
    savingRef.current = false;
    setSaving(false);
    setEditId(p.id);
    setSubmitted(false);
    setForm({ ...p });
    const existingLines = (data.purchaseLines || []).filter(l => l.purchase_id === p.id).map(l => ({
      ...l,
      total_manual: (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0)
    }));
    setLines(existingLines.length > 0 ? existingLines : [{ item_id: "", quantity: 1, unit_price: 0 }]);
    setModal(true);
  };

  const deletePurchase = async (p) => {
    const purchasedItemIds = (data.purchaseLines || []).filter(l => l.purchase_id === p.id).map(l => l.item_id);
    const hasSubsequentSales = purchasedItemIds.some(itemId =>
      (data.saleLines || []).some(sl => {
        if (sl.item_id !== itemId) return false;
        const sal = (data.sales || []).find(s => s.id === sl.sale_id);
        return sal && sal.date >= p.date;
      })
    );
    if (hasSubsequentSales) {
      showToast("این فاکتور قابل حذف نیست — برای کالاهای آن فروش بعدی وجود دارد.", "error"); return;
    }
    if (!window.confirm("فاکتور " + p.invoice_no + " حذف شود؟")) return;
    const { data: res, error } = await supabase.rpc("delete_purchase", { p_purchase_id: p.id });
    if (error || !res?.ok) { showToast("خطا در حذف: " + (error?.message || res?.error || "خطای ناشناخته"), "error"); return; }
    await auditLog("delete", "purchases", p.id, { invoice_no: p.invoice_no }, myProfile?.id);
    await refresh();
    showToast("فاکتور حذف شد");
  };

  const [form, setForm] = useState({
    invoice_no: "", voucher_no: "", date: today(), vendor_id: "", currency_code: "USD",
    paid_amount: 0, payment_account_id: "", shipping_cost: 0, customs_cost: 0, packing_cost: 0, notes: ""
  });
  const [lines, setLines] = useState([{ item_id: "", quantity: 1, unit_price: 0 }]);

  // نمایش همه اشخاص فعال در فاکتور خرید (فروشنده، هر دو، شرکت)
  const vendors = (data.persons || []).filter(p => p.is_active !== false);
  const cashAccounts = (data.accounts || []).filter(a => a.type === "cash" || a.type === "bank");
  const invAccount = (data.accounts || []).find(a => a.type === "inventory");
  const personCtrlAcc = (data.accounts || []).find(a => a.type === "person_control");

  const totalLines = lines.reduce((s, l) => {
    if (l.total_manual !== undefined) return s + (parseFloat(l.total_manual) || 0);
    return s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0);
  }, 0);
  const extraCosts = (parseFloat(form.shipping_cost) || 0) + (parseFloat(form.customs_cost) || 0) + (parseFloat(form.packing_cost) || 0);
  const totalAmount = totalLines + extraCosts;
  const paidAmount = parseFloat(form.paid_amount) || 0;
  const remaining = totalAmount - paidAmount;

  const addLine = () => setLines(p => [...p, { item_id: "", quantity: 1, unit_price: 0 }]);
  const removeLine = (i) => setLines(p => p.filter((_, idx) => idx !== i));
  const updateLine = (i, key, val) => setLines(p => p.map((l, idx) => idx === i ? { ...l, [key]: val } : l));

  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      setSubmitted(true);
      if (!form.vendor_id) return;
      if (!form.invoice_no.trim()) return;
      if ((data.purchases || []).some(p => p.invoice_no === form.invoice_no && p.id !== editId)) {
        showToast("شماره فاکتور خرید تکراری است", "error"); return;
      }
      if (form.voucher_no && (data.purchases || []).some(p => p.voucher_no === form.voucher_no && p.id !== editId)) {
        showToast("شماره سند خرید تکراری است", "error"); return;
      }
      if (lines.some(l => !l.item_id)) return;

      for (let idx = 0; idx < lines.length; idx++) {
        const l = lines[idx];
        const qty = parseFloat(l.quantity) || 0;
        const unitPrice = parseFloat(l.unit_price) || 0;
        const lineTotal = l.total_manual !== undefined ? (parseFloat(l.total_manual) || 0) : qty * unitPrice;
        if (qty <= 0) { showToast("ردیف " + (idx+1) + ": مقدار باید بزرگ‌تر از صفر باشد", "error"); return; }
        if (unitPrice < 0) { showToast("ردیف " + (idx+1) + ": قیمت واحد نمی‌تواند منفی باشد", "error"); return; }
        if (lineTotal <= 0) { showToast("ردیف " + (idx+1) + ": جمع ردیف باید بزرگ‌تر از صفر باشد", "error"); return; }
      }
      if (totalAmount <= 0) { showToast("جمع کل خرید باید بزرگ‌تر از صفر باشد", "error"); return; }
      if (paidAmount < 0) { showToast("مبلغ پرداخت‌شده نمی‌تواند منفی باشد", "error"); return; }
      if (paidAmount > totalAmount) { showToast("مبلغ پرداخت‌شده نمی‌تواند بیشتر از جمع کل خرید باشد", "error"); return; }
      if (paidAmount > 0 && !form.payment_account_id) { showToast("برای مبلغ پرداخت‌شده باید حساب پرداخت انتخاب شود", "error"); return; }
      if (!invAccount) { showToast("حساب موجودی کالا تعریف نشده است", "error"); return; }
      if (!personCtrlAcc) { showToast("حساب کنترل اشخاص تعریف نشده است", "error"); return; }

      {
        const newItemIds = lines.map(l => l.item_id).filter(Boolean);
        const oldItemIds = editId ? (data.purchaseLines || []).filter(l => l.purchase_id === editId).map(l => l.item_id) : [];
        const allItemIds = [...new Set([...newItemIds, ...oldItemIds])];
        const hasSubsequentSales = (data.saleLines || []).some(sl => {
          if (!allItemIds.includes(sl.item_id)) return false;
          const sal = (data.sales || []).find(s => s.id === sl.sale_id);
          return sal && sal.date >= form.date;
        });
        if (hasSubsequentSales) {
          showToast("برای این کالا در همین تاریخ یا بعد از آن، فروش ثبت شده است. خرید تاریخی مجاز نیست — لطفاً تاریخ را تغییر دهید یا سند اصلاحی ثبت کنید.", "error");
          return;
        }
      }

      const purchaseId = editId || uid();
      const currency = "USD";
      const desc = "خرید فاکتور " + form.invoice_no;

      const linesWithOverhead = lines.map(l => {
        const qty = parseFloat(l.quantity) || 0;
        const up = parseFloat(l.unit_price) || 0;
        const lineValue = l.total_manual !== undefined ? (parseFloat(l.total_manual) || 0) : qty * up;
        const overheadShare = totalLines > 0 ? (lineValue / totalLines) * extraCosts : 0;
        const totalLineCost = lineValue + overheadShare;
        const unitCostWithOverhead = qty > 0 ? totalLineCost / qty : up;
        return { ...l, overhead_share: overheadShare, unit_cost_with_overhead: unitCostWithOverhead };
      });

      const ledgerEntries = [];
      ledgerEntries.push({ id: uid(), date: form.date, voucher_id: purchaseId, voucher_no: form.voucher_no, source_type: "purchase", source_id: purchaseId, account_id: invAccount.id, person_id: null, currency_code: currency, debit: totalAmount, credit: 0, description: desc });
      if (paidAmount > 0 && form.payment_account_id) ledgerEntries.push({ id: uid(), date: form.date, voucher_id: purchaseId, voucher_no: form.voucher_no, source_type: "purchase", source_id: purchaseId, account_id: form.payment_account_id, person_id: null, currency_code: currency, debit: 0, credit: paidAmount, description: desc });
      if (remaining > 0) ledgerEntries.push({ id: uid(), date: form.date, voucher_id: purchaseId, voucher_no: form.voucher_no, source_type: "purchase", source_id: purchaseId, account_id: personCtrlAcc.id, person_id: form.vendor_id, currency_code: "USD", debit: 0, credit: remaining, description: desc + " - مانده بدهی به فروشنده" });

      if (!isLedgerBalanced(ledgerEntries)) { showToast("سند حسابداری متعادل نیست؛ ثبت انجام نشد", "error"); return; }

      {
        const testLines = [
          ...(data.purchaseLines || []).filter(l => l.purchase_id !== purchaseId),
          ...linesWithOverhead.map(l => ({ ...l, purchase_id: purchaseId, currency_code: currency }))
        ];
        const testPurchases = editId
          ? data.purchases.map(x => x.id === editId ? { ...form, id: editId, created_at: x.created_at || form.date } : x)
          : [...(data.purchases || []), { ...form, id: purchaseId, created_at: new Date().toISOString() }];
        const { warnings: dryW } = rebuildItemStocks(data.items || [], testLines, data.saleLines || [], testPurchases, data.sales || []);
        if (dryW && dryW.length > 0) { showToast("این خرید باعث موجودی منفی می‌شود: " + dryW.join(" | "), "error"); return; }
      }

      const purchaseRow = {
        id: purchaseId, invoice_no: form.invoice_no, voucher_no: form.voucher_no,
        date: form.date, vendor_id: form.vendor_id || null, currency_code: currency,
        total_amount: totalAmount, paid_amount: paidAmount, remaining_amount: remaining,
        shipping_cost: parseFloat(form.shipping_cost) || 0,
        customs_cost: parseFloat(form.customs_cost) || 0,
        packing_cost: parseFloat(form.packing_cost) || 0,
        notes: form.notes || "", created_by: myProfile?.id || null,
        created_at: editId ? undefined : new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      const newPurchaseLines = linesWithOverhead.map(l => {
        const qty = parseFloat(l.quantity) || 0;
        const up = parseFloat(l.unit_price) || 0;
        const lineTotal = l.total_manual !== undefined ? (parseFloat(l.total_manual) || 0) : qty * up;
        const realUnitPrice = qty > 0 ? Math.round((lineTotal / qty) * 1e8) / 1e8 : up;
        return { ...l, id: uid(), purchase_id: purchaseId, unit_price: realUnitPrice, total_price: lineTotal, total_cost_with_overhead: qty * (l.unit_cost_with_overhead || up), currency_code: currency };
      });

      const testPurchases2 = editId
        ? data.purchases.map(x => x.id === editId ? { ...purchaseRow, created_at: x.created_at } : x)
        : [...(data.purchases || []), { ...purchaseRow, created_at: new Date().toISOString() }];
      const testPurchaseLines2 = [
        ...(data.purchaseLines || []).filter(l => l.purchase_id !== purchaseId),
        ...newPurchaseLines
      ];
      const { items: rebuiltItems } = rebuildItemStocks(data.items || [], testPurchaseLines2, data.saleLines || [], testPurchases2, data.sales || []);

      if (!editId) {
        const { data: exInv } = await supabase.from("purchases").select("id").eq("invoice_no", form.invoice_no).maybeSingle();
        if (exInv) { showToast("شماره فاکتور خرید تکراری است", "error"); return; }
        if (form.voucher_no) {
          const { data: exVch } = await supabase.from("purchases").select("id").eq("voucher_no", form.voucher_no).maybeSingle();
          if (exVch) { showToast("شماره سند خرید تکراری است", "error"); return; }
        }
      }

      const { data: rpcRes, error: rpcErr } = await supabase.rpc("save_purchase", {
        p: {
          purchase: purchaseRow, lines: newPurchaseLines, ledger: ledgerEntries,
          items: rebuiltItems.map(i => ({ id: i.id, current_stock: i.current_stock, avg_purchase_price: i.avg_purchase_price })),
          is_edit: !!editId
        }
      });
      const rpcOk = rpcRes && (typeof rpcRes === "object" ? rpcRes.ok : false);
      const rawErrMsg = rpcErr?.message || (rpcRes && typeof rpcRes === "object" ? rpcRes.error : String(rpcRes)) || "خطای ناشناخته";
      const friendlyErr = rawErrMsg.includes("ux_purchases_invoice_no") ? "شماره فاکتور خرید تکراری است"
        : rawErrMsg.includes("ux_purchases_voucher_no") ? "شماره سند خرید تکراری است" : rawErrMsg;
      if (rpcErr || !rpcOk) { showToast("خطا در ثبت: " + friendlyErr, "error"); return; }

      await auditLog(editId ? "update" : "insert", "purchases", purchaseId, { invoice_no: form.invoice_no }, myProfile?.id);
      await refresh();
      const wasEdit = !!editId;
      setModal(false); setEditId(null); setSubmitted(false);
      showToast(wasEdit ? "فاکتور با موفقیت ویرایش شد ✓" : "فاکتور خرید با موفقیت ثبت شد ✓");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} visible={toast.visible} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>📦 خریدها</h2>
        <Btn onClick={openModal}>+ ثبت خرید</Btn>
      </div>
      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 10 }}>
          <Input label="از تاریخ" type="date" value={fFrom} onChange={e => setFFrom(e.target.value)} />
          <Input label="تا تاریخ" type="date" value={fTo} onChange={e => setFTo(e.target.value)} />
          <Select label="فروشنده" value={fVendor} onChange={e => setFVendor(e.target.value)}>
            <option value="">همه فروشندگان</option>
            {(data.persons || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </div>
        {(fFrom || fTo || fVendor) && (
          <Btn size="sm" variant="ghost" onClick={() => { setFFrom(""); setFTo(""); setFVendor(""); }} style={{ marginTop: 4 }}>× پاک‌کردن فیلتر</Btn>
        )}
      </Card>
      <Card>
        <Table cols={["ش. فاکتور", "ش. سند", "تاریخ", "فروشنده", "ارز", "مبلغ کل", "پرداخت شده", "مانده", "عملیات"]} searchable
          rows={(data.purchases || []).filter(p => {
              if (fFrom && p.date < fFrom) return false;
              if (fTo && p.date > fTo) return false;
              if (fVendor && p.vendor_id !== fVendor) return false;
              return true;
            }).map(p => ({ cells: [
            <strong style={{ color: theme.accentLight }}>{p.invoice_no}</strong>,
            <span style={{ color: theme.textMuted, fontSize: 12 }}>{p.voucher_no || "-"}</span>,
            p.date,
            (data.persons || []).find(x => x.id === p.vendor_id)?.name || "-",
            p.currency_code, fmtMoney(p.total_amount),
            <span style={{ color: theme.success }}>{fmtMoney(p.paid_amount)}</span>,
            <span style={{ color: p.remaining_amount > 0 ? theme.danger : theme.success }}>{fmtMoney(p.remaining_amount)}</span>,
            <div style={{display:"flex",gap:4}}><button onClick={()=>openEdit(p)} style={{padding:"4px 10px",background:"transparent",border:"1px solid "+theme.border,borderRadius:6,color:theme.text,cursor:"pointer",fontSize:11,fontFamily:"Vazirmatn"}}>✏️ ویرایش</button><button onClick={()=>deletePurchase(p)} style={{padding:"4px 10px",background:"transparent",border:"1px solid "+theme.danger+"55",borderRadius:6,color:theme.danger,cursor:"pointer",fontSize:11,fontFamily:"Vazirmatn"}}>🗑 حذف</button></div>
          ]}))}
        />
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title={editId ? "ویرایش فاکتور خرید" : "ثبت فاکتور خرید"} width={720}>
        {/* شماره فاکتور و سند جدا */}
        <div style={{ background: theme.bgInput, borderRadius: 10, padding: 14, marginBottom: 16, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, color: theme.textMuted, marginBottom: 4 }}>شماره فاکتور (اتوماتیک)</label>
            <div style={{ padding: "9px 12px", background: theme.bgCard, border: `1px solid ${theme.accent}44`, borderRadius: 8, color: theme.accentLight, fontWeight: 700 }}>{form.invoice_no}</div>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: theme.textMuted, marginBottom: 4 }}>شماره سند (اتوماتیک)</label>
            <div style={{ padding: "9px 12px", background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.textMuted }}>{form.voucher_no}</div>
          </div>
          <Input label="تاریخ *" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <SearchableSelect
            label="فروشنده *"
            value={form.vendor_id}
            onChange={e => setForm(p => ({ ...p, vendor_id: e.target.value }))}
            options={vendors.map(v => { const typeMap = {customer:"مشتری", vendor:"فروشنده", both:"هر دو", employee:"کارمند", broker:"صراف", company:"شرکت"}; return { value: v.id, label: v.name + " [" + (typeMap[v.type]||v.type) + "]" }; })}
            placeholder="— انتخاب فروشنده —"
            required
            error={submitted && !form.vendor_id}
          />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <CurrencySelect label="ارز" />
            <button onClick={() => setQuickPersonOpen(true)}
              style={{ fontSize: 12, color: theme.accent, background: "transparent", border: "none", cursor: "pointer", textAlign: "right", padding: "2px 0", fontFamily: "Vazirmatn", marginTop: -8 }}>
              + افزودن فروشنده جدید
            </button>
          </div>
        </div>

        <QuickPersonModal
          open={quickPersonOpen}
          onClose={() => setQuickPersonOpen(false)}
          defaultType="vendor"
          onCreated={person => setForm(p => ({ ...p, vendor_id: person.id }))}
        />

        {vendors.length === 0 && <div style={{ background: `${theme.warning}22`, border: `1px solid ${theme.warning}44`, borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 13, color: theme.warning }}>⚠️ ابتدا از بخش «اشخاص» یک شخص اضافه کنید</div>}

        <div style={{ margin: "16px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <strong style={{ fontSize: 14 }}>ردیف‌های کالا</strong>
            <Btn size="sm" onClick={addLine}>+ ردیف</Btn>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr auto", gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: theme.textMuted, padding: "0 4px" }}>کالا</span>
            <span style={{ fontSize: 11, color: theme.textMuted, textAlign: "center" }}>مقدار</span>
            <span style={{ fontSize: 11, color: theme.textMuted, textAlign: "center" }}>قیمت واحد</span>
            <span style={{ fontSize: 11, color: theme.textMuted, textAlign: "center" }}>جمع کل</span>
            <span></span>
          </div>
          {lines.map((line, i) => {
            const qty = parseFloat(line.quantity) || 0;
            const up = parseFloat(line.unit_price) || 0;
            const total = parseFloat(line.total_manual) || (qty * up);
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr auto", gap: 6, marginBottom: 8, alignItems: "center" }}>
                <select value={line.item_id} onChange={e => {
                    // در خرید فقط کالا را انتخاب می‌کنیم — قیمت را کاربر وارد می‌کند
                    setLines(p => p.map((l, idx) => idx === i ? { ...l, item_id: e.target.value } : l));
                  }}
                  style={{ padding: "8px 10px", background: theme.bgInput, border: (submitted && !line.item_id ? "2px solid "+theme.danger : "1px solid "+theme.border), borderRadius: 8, color: theme.text, fontSize: 13 }}>
                  <option value="">— انتخاب کالا —</option>
                  {(data.items || []).filter(x => x.is_active !== false).map(x => (
                    <option key={x.id} value={x.id}>{x.name} ({x.unit})</option>
                  ))}
                </select>
                <input type="number" placeholder="مقدار"
                  value={line.quantity === 0 || line.quantity === "0" ? "" : line.quantity}
                  onChange={e => { const v = e.target.value; setLines(p => p.map((l, idx) => idx === i ? { ...l, quantity: v, total_manual: (parseFloat(v)||0)*(parseFloat(l.unit_price)||0) } : l)); }}
                  style={{ padding: "8px", background: theme.bgInput, border: "1px solid "+theme.border, borderRadius: 8, color: theme.text, fontSize: 13, textAlign: "left", direction: "ltr" }} />
                <input type="number" placeholder="قیمت واحد"
                  value={line.unit_price === 0 || line.unit_price === "0" ? "" : line.unit_price}
                  onChange={e => { const v = e.target.value; setLines(p => p.map((l, idx) => idx === i ? { ...l, unit_price: v, total_manual: (parseFloat(l.quantity)||0)*(parseFloat(v)||0) } : l)); }}
                  style={{ padding: "8px", background: theme.bgInput, border: "1px solid "+theme.border, borderRadius: 8, color: theme.text, fontSize: 13, textAlign: "left", direction: "ltr" }} />
                <input type="number" placeholder="جمع کل"
                  value={(line.total_manual !== undefined ? line.total_manual : qty * up) === 0 ? "" : (line.total_manual !== undefined ? line.total_manual : qty * up)}
                  onChange={e => { const v = parseFloat(e.target.value)||0; const qty = parseFloat(line.quantity)||1; const newUp = qty > 0 ? Math.round((v / qty) * 1e8) / 1e8 : 0; setLines(p => p.map((l, idx) => idx === i ? { ...l, total_manual: v, unit_price: newUp } : l)); }}
                  style={{ padding: "8px", background: theme.bgCard, border: "1px solid "+theme.accent+"66", borderRadius: 8, color: theme.accentLight, fontSize: 13, fontWeight: 600, textAlign: "left", direction: "ltr" }} />
                <button onClick={() => removeLine(i)} style={{ padding: "6px 10px", background: "transparent", border: "1px solid "+theme.danger+"55", borderRadius: 6, color: theme.danger, cursor: "pointer", fontSize: 14, fontFamily: "Vazirmatn" }}>×</button>
              </div>
            );
          })}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Input label="هزینه حمل" type="number" value={form.shipping_cost} onChange={e => setForm(p => ({ ...p, shipping_cost: e.target.value }))} />
          <Input label="هزینه گمرک" type="number" value={form.customs_cost} onChange={e => setForm(p => ({ ...p, customs_cost: e.target.value }))} />
          <Input label="هزینه بسته‌بندی" type="number" value={form.packing_cost} onChange={e => setForm(p => ({ ...p, packing_cost: e.target.value }))} />
        </div>

        <div style={{ background: theme.bgInput, borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: theme.textMuted }}>جمع کالاها</span><strong><Money value={totalLines} currency={form.currency_code} /></strong></div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: theme.textMuted }}>هزینه‌های جانبی</span><strong><Money value={extraCosts} currency={form.currency_code} /></strong></div>
          <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, borderTop: `1px solid ${theme.border}` }}><span style={{ fontWeight: 700 }}>جمع کل</span><strong style={{ color: theme.accent, fontSize: 16 }}><Money value={totalAmount} currency={form.currency_code} /></strong></div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Input label="مبلغ پرداخت‌شده" type="number" value={form.paid_amount} onChange={e => setForm(p => ({ ...p, paid_amount: e.target.value }))} />
          <Select label="پرداخت از حساب" value={form.payment_account_id} onChange={e => setForm(p => ({ ...p, payment_account_id: e.target.value }))}>
            <option value="">— انتخاب حساب —</option>
            {cashAccounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency_code})</option>)}
          </Select>
        </div>
        <div style={{ background: remaining > 0 ? `${theme.danger}11` : `${theme.success}11`, borderRadius: 8, padding: 10, marginBottom: 12, textAlign: "center" }}>
          <span style={{ color: remaining > 0 ? theme.danger : theme.success, fontWeight: 700 }}>مانده: {fmtMoney(remaining, form.currency_code)}</span>
        </div>
        <Textarea label="توضیحات" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setModal(false)}>انصراف</Btn>
          <Btn variant="success" onClick={save} disabled={saving} style={{ opacity: saving ? 0.6 : 1 }}>
            {saving ? "⏳ در حال ثبت..." : (editId ? "✓ ذخیره ویرایش" : "✓ ثبت خرید")}
          </Btn>
        </div>
      </Modal>
    </div>
  );
}

// =====================================================
// SALE PAGE — شماره فاکتور و سند اتوماتیک
// =====================================================
function SalePage() {
  const { data } = useApp();
  const { sbWrite, refresh, profile: myProfile, auditLog } = useSb();
  const [toast, showToast] = useToast();
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [sfFrom, setSfFrom] = useState("");
  const [sfTo, setSfTo] = useState("");
  const [sfCustomer, setSfCustomer] = useState("");
  const [sfRemaining, setSfRemaining] = useState(""); // "all" | "open" | "closed" 
  const [quickPersonOpen, setQuickPersonOpen] = useState(false);
  const [form, setForm] = useState({ invoice_no: "", voucher_no: "", date: today(), customer_id: "", currency_code: "USD", discount_amount: 0, received_amount: 0, receipt_account_id: "", notes: "" });
  const [lines, setLines] = useState([{ item_id: "", quantity: 1, unit_price: 0, discount: 0 }]);

  const openModal = () => {
    savingRef.current = false;
    setSaving(false);
    setEditId(null);
    setSubmitted(false);
    setForm({ invoice_no: genInvoiceNoSafe(data.sales, "SAL"), voucher_no: genVoucherNoAll(data), date: today(), customer_id: "", currency_code: "USD", discount_amount: 0, received_amount: 0, receipt_account_id: "", notes: "" });
    setLines([{ item_id: "", quantity: 1, unit_price: 0, discount: 0 }]);
    setModal(true);
  };

  const customers = (data.persons || []).filter(p => p.type === "customer" || p.type === "both");
  const cashAccounts = (data.accounts || []).filter(a => a.type === "cash" || a.type === "bank");
  const personCtrlAccSale = (data.accounts || []).find(a => a.type === "person_control");
  const incomeAccount = (data.accounts || []).find(a => a.type === "income");

  const totalLines = lines.reduce((s, l) => {
    const base = l.total_manual !== undefined ? (parseFloat(l.total_manual) || 0) : (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0);
    return s + base - (parseFloat(l.discount) || 0);
  }, 0);
  const finalAmount = totalLines - (parseFloat(form.discount_amount) || 0);
  const receivedAmount = parseFloat(form.received_amount) || 0;
  const remaining = finalAmount - receivedAmount;

  const addLine = () => setLines(p => [...p, { item_id: "", quantity: 1, unit_price: 0, discount: 0 }]);
  const removeLine = (i) => setLines(p => p.filter((_, idx) => idx !== i));
  const updateLine = (i, key, val) => setLines(p => p.map((l, idx) => idx === i ? { ...l, [key]: val } : l));

  const openEditSale = (s) => {
    savingRef.current = false;
    setSaving(false);
    setEditId(s.id);
    setSubmitted(false);
    setForm({ ...s });
    const existingLines = (data.saleLines || []).filter(l => l.sale_id === s.id).map(l => ({
      ...l,
      total_manual: (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0)
    }));
    setLines(existingLines.length > 0 ? existingLines : [{ item_id: "", quantity: 1, unit_price: 0, discount: 0 }]);
    setModal(true);
  };

  const deleteSale = async (s) => {
    if (!window.confirm("فاکتور " + s.invoice_no + " حذف شود؟")) return;
    const { data: res, error } = await supabase.rpc("delete_sale", { p_sale_id: s.id });
    if (error || !res?.ok) { showToast("خطا در حذف: " + (error?.message || res?.error || "خطای ناشناخته"), "error"); return; }
    // rebuild موجودی
    const freshData = await loadFromSupabase();
    const { items: rebuiltItems } = rebuildItemStocks(freshData.items, freshData.purchaseLines, freshData.saleLines, freshData.purchases, freshData.sales);
    await sbWrite.upsertMany("items", rebuiltItems.map(i => ({ ...i, updated_at: new Date().toISOString() })));
    await auditLog("delete", "sales", s.id, { invoice_no: s.invoice_no }, myProfile?.id);
    await refresh();
    showToast("فاکتور حذف شد");
  };

  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      setSubmitted(true);
      if (!form.customer_id) return;
      if (!form.invoice_no.trim()) return;
      if ((data.sales || []).some(s => s.invoice_no === form.invoice_no && s.id !== editId)) {
        showToast("شماره فاکتور فروش تکراری است", "error"); return;
      }
      if (form.voucher_no && (data.sales || []).some(s => s.voucher_no === form.voucher_no && s.id !== editId)) {
        showToast("شماره سند فروش تکراری است", "error"); return;
      }
      if (lines.some(l => !l.item_id)) return;

      for (let idx = 0; idx < lines.length; idx++) {
        const l = lines[idx];
        const qty = parseFloat(l.quantity) || 0;
        const unitPrice = parseFloat(l.unit_price) || 0;
        const lineDiscount = parseFloat(l.discount) || 0;
        const lineTotal = (l.total_manual !== undefined ? (parseFloat(l.total_manual) || 0) : qty * unitPrice) - lineDiscount;
        if (qty <= 0) { showToast("ردیف " + (idx+1) + ": مقدار باید بزرگ‌تر از صفر باشد", "error"); return; }
        if (unitPrice < 0) { showToast("ردیف " + (idx+1) + ": قیمت واحد نمی‌تواند منفی باشد", "error"); return; }
        if (lineDiscount < 0) { showToast("ردیف " + (idx+1) + ": تخفیف نمی‌تواند منفی باشد", "error"); return; }
        if (lineTotal <= 0) { showToast("ردیف " + (idx+1) + ": جمع ردیف پس از تخفیف باید بزرگ‌تر از صفر باشد", "error"); return; }
      }
      const discountTotal = parseFloat(form.discount_amount) || 0;
      if (discountTotal < 0) { showToast("تخفیف کلی نمی‌تواند منفی باشد", "error"); return; }
      if (finalAmount <= 0) { showToast("مبلغ نهایی فروش باید بزرگ‌تر از صفر باشد", "error"); return; }
      if (receivedAmount < 0) { showToast("مبلغ دریافت‌شده نمی‌تواند منفی باشد", "error"); return; }
      if (receivedAmount > finalAmount) { showToast("مبلغ دریافت‌شده نمی‌تواند بیشتر از مبلغ نهایی فروش باشد", "error"); return; }
      if (receivedAmount > 0 && !form.receipt_account_id) { showToast("برای مبلغ دریافت‌شده باید حساب دریافت انتخاب شود", "error"); return; }
      if (!incomeAccount) { showToast("حساب درآمد فروش تعریف نشده است", "error"); return; }
      if (!personCtrlAccSale) { showToast("حساب کنترل اشخاص تعریف نشده است", "error"); return; }

      const saleExcludeSeq = (editId ? (data.sales || []).find(s => s.id === editId)?.created_at : null) || new Date().toISOString();
      const qtyByItem = {};
      for (const line of lines) {
        const itemId = line.item_id;
        if (!itemId) continue;
        qtyByItem[itemId] = (qtyByItem[itemId] || 0) + (parseFloat(line.quantity) || 0);
      }
      for (const [itemId, totalRequestedQty] of Object.entries(qtyByItem)) {
        const item = (data.items || []).find(i => i.id === itemId);
        if (!item) continue;
        const openingStock = parseFloat(item.opening_stock) || 0;
        const openingUnitCost = parseFloat(item.opening_unit_cost != null ? item.opening_unit_cost : item.avg_purchase_price) || 0;
        const { stock: stockAtDate } = calculateAvgCostAtDate(
          itemId, form.date, saleExcludeSeq,
          data.purchaseLines || [], data.saleLines || [],
          data.purchases || [], data.sales || [],
          editId || null, openingStock, openingUnitCost
        );
        if (stockAtDate < totalRequestedQty - 0.001) {
          showToast(
            "موجودی «" + item.name + "» در تاریخ " + form.date + " کافی نیست.\n" +
            "موجودی در این تاریخ: " + fmtQty(Math.max(0, stockAtDate)) + " " + item.unit +
            " — درخواستی (جمع همه ردیف‌ها): " + fmtQty(totalRequestedQty) + " " + item.unit,
            "error"
          );
          return;
        }
      }

      const cogsAccount = (data.accounts || []).find(a => a.type === "cogs");
      const invAccount = (data.accounts || []).find(a => a.type === "inventory");
      const saleId = editId || uid();
      const currency = "USD";
      const desc = "فروش فاکتور " + form.invoice_no;

      const newSaleLinesForThisSale = lines.map(l => {
        const item = (data.items || []).find(i => i.id === l.item_id);
        const openingStock = parseFloat(item?.opening_stock) || 0;
        const openingUnitCost = parseFloat(item?.opening_unit_cost != null ? item.opening_unit_cost : item?.avg_purchase_price) || 0;
        const { avg: costPrice } = calculateAvgCostAtDate(
          l.item_id, form.date, form.created_at || form.date,
          data.purchaseLines || [], data.saleLines || [],
          data.purchases || [], data.sales || [],
          saleId, openingStock, openingUnitCost
        );
        const qty = parseFloat(l.quantity) || 0;
        const lineDiscount = parseFloat(l.discount) || 0;
        const baseTotal = l.total_manual !== undefined ? (parseFloat(l.total_manual) || 0) : qty * (parseFloat(l.unit_price) || 0);
        const realUnitPrice = qty > 0 ? Math.round((baseTotal / qty) * 1e8) / 1e8 : (parseFloat(l.unit_price) || 0);
        const totalPriceAfterLineDiscount = baseTotal - lineDiscount;
        return {
          ...l, id: uid(), sale_id: saleId,
          unit_price: realUnitPrice,
          cost_price: costPrice,
          total_price_before_invoice_discount: totalPriceAfterLineDiscount,
          total_price: totalPriceAfterLineDiscount,
          currency_code: currency,
          _cogs: qty * costPrice
        };
      });

      const invoiceDiscount = parseFloat(form.discount_amount) || 0;
      const totalBeforeInvoiceDiscount = newSaleLinesForThisSale.reduce((s, sl) => s + sl.total_price_before_invoice_discount, 0);
      const finalSaleLines = newSaleLinesForThisSale.map(sl => {
        const share = totalBeforeInvoiceDiscount > 0 ? sl.total_price_before_invoice_discount / totalBeforeInvoiceDiscount : 0;
        const lineInvoiceDiscountShare = invoiceDiscount * share;
        const revenueAfterAllDiscounts = sl.total_price_before_invoice_discount - lineInvoiceDiscountShare;
        return {
          ...sl,
          total_price: revenueAfterAllDiscounts,
          invoice_discount_share: lineInvoiceDiscountShare,
          gross_profit: revenueAfterAllDiscounts - sl._cogs,
          _cogs: undefined
        };
      });

      const totalCOGS = finalSaleLines.reduce((s, sl) => s + (parseFloat(sl.quantity) || 0) * (sl.cost_price || 0), 0);
      if (totalCOGS > 0 && (!cogsAccount || !invAccount)) { showToast("حساب موجودی کالا یا بهای تمام‌شده تعریف نشده است", "error"); return; }

      const newSaleLines = [...(data.saleLines || []).filter(l => l.sale_id !== saleId), ...finalSaleLines];
      const newSales = editId
        ? data.sales.map(x => x.id === editId ? { ...form, id: editId, total_amount: totalLines, final_amount: finalAmount, received_amount: receivedAmount, remaining_amount: remaining } : x)
        : [...(data.sales || []), { ...form, id: saleId, total_amount: totalLines, final_amount: finalAmount, received_amount: receivedAmount, remaining_amount: remaining, created_at: new Date().toISOString() }];
      const { items: rebuiltItems, warnings: _saleWarnings } = rebuildItemStocks(data.items || [], data.purchaseLines || [], newSaleLines, data.purchases || [], newSales);
      if (_saleWarnings && _saleWarnings.length > 0) { showToast("فروش باعث موجودی منفی می‌شود: " + _saleWarnings.join(" | "), "error"); return; }

      const ledgerEntries = [];
      if (receivedAmount > 0 && form.receipt_account_id) ledgerEntries.push({ id: uid(), date: form.date, voucher_id: saleId, voucher_no: form.voucher_no, source_type: "sale", source_id: saleId, account_id: form.receipt_account_id, person_id: null, currency_code: currency, debit: receivedAmount, credit: 0, description: desc });
      if (remaining > 0) ledgerEntries.push({ id: uid(), date: form.date, voucher_id: saleId, voucher_no: form.voucher_no, source_type: "sale", source_id: saleId, account_id: personCtrlAccSale.id, person_id: form.customer_id, currency_code: "USD", debit: remaining, credit: 0, description: desc + " - مانده طلب از مشتری" });
      ledgerEntries.push({ id: uid(), date: form.date, voucher_id: saleId, voucher_no: form.voucher_no, source_type: "sale", source_id: saleId, account_id: incomeAccount.id, person_id: null, currency_code: currency, debit: 0, credit: finalAmount, description: desc });
      if (totalCOGS > 0 && cogsAccount) ledgerEntries.push({ id: uid(), date: form.date, voucher_id: saleId, voucher_no: form.voucher_no, source_type: "sale", source_id: saleId, account_id: cogsAccount.id, person_id: null, currency_code: currency, debit: totalCOGS, credit: 0, description: desc + " - بهای تمام‌شده (MWA)" });
      if (totalCOGS > 0 && invAccount) ledgerEntries.push({ id: uid(), date: form.date, voucher_id: saleId, voucher_no: form.voucher_no, source_type: "sale", source_id: saleId, account_id: invAccount.id, person_id: null, currency_code: currency, debit: 0, credit: totalCOGS, description: desc + " - کاهش موجودی" });
      if (!isLedgerBalanced(ledgerEntries)) { showToast("سند حسابداری متعادل نیست؛ ثبت انجام نشد", "error"); return; }

      const saleRow = {
        id: saleId, invoice_no: form.invoice_no, voucher_no: form.voucher_no,
        date: form.date, customer_id: form.customer_id || null, currency_code: currency,
        total_amount: totalLines, final_amount: finalAmount, received_amount: receivedAmount,
        remaining_amount: remaining, discount_amount: parseFloat(form.discount_amount) || 0,
        receipt_account_id: form.receipt_account_id || null, notes: form.notes || "",
        created_by: myProfile?.id || null,
        created_at: editId ? undefined : new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (!editId) {
        const { data: exInv } = await supabase.from("sales").select("id").eq("invoice_no", form.invoice_no).maybeSingle();
        if (exInv) { showToast("شماره فاکتور فروش تکراری است", "error"); return; }
        if (form.voucher_no) {
          const { data: exVch } = await supabase.from("sales").select("id").eq("voucher_no", form.voucher_no).maybeSingle();
          if (exVch) { showToast("شماره سند فروش تکراری است", "error"); return; }
        }
      }

      const { data: rpcRes, error: rpcErr } = await supabase.rpc("save_sale", {
        p: { sale: saleRow, lines: finalSaleLines, ledger: ledgerEntries,
          items: rebuiltItems.map(i => ({ id: i.id, current_stock: i.current_stock, avg_purchase_price: i.avg_purchase_price })),
          is_edit: !!editId }
      });
      const rpcOk = rpcRes && (typeof rpcRes === "object" ? rpcRes.ok : false);
      const rawErr = rpcErr?.message || (rpcRes && typeof rpcRes === "object" ? rpcRes.error : String(rpcRes)) || "خطای ناشناخته";
      const friendlyErr = rawErr.includes("ux_sales_invoice_no") ? "شماره فاکتور فروش تکراری است"
        : rawErr.includes("ux_sales_voucher_no") ? "شماره سند فروش تکراری است" : rawErr;
      if (rpcErr || !rpcOk) { showToast("خطا در ثبت: " + friendlyErr, "error"); return; }

      await auditLog(editId ? "update" : "insert", "sales", saleId, { invoice_no: form.invoice_no }, myProfile?.id);
      await refresh();
      const wasEditSale = !!editId;
      setModal(false); setEditId(null); setSubmitted(false);
      showToast(wasEditSale ? "فاکتور با موفقیت ویرایش شد ✓" : "فاکتور فروش با موفقیت ثبت شد ✓");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} visible={toast.visible} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>🛒 فروش</h2>
        <Btn onClick={openModal}>+ ثبت فروش</Btn>
      </div>
      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
          <Input label="از تاریخ" type="date" value={sfFrom} onChange={e => setSfFrom(e.target.value)} />
          <Input label="تا تاریخ" type="date" value={sfTo} onChange={e => setSfTo(e.target.value)} />
          <Select label="مشتری" value={sfCustomer} onChange={e => setSfCustomer(e.target.value)}>
            <option value="">همه مشتریان</option>
            {(data.persons || []).filter(p => p.type === "customer" || p.type === "both").map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select label="وضعیت" value={sfRemaining} onChange={e => setSfRemaining(e.target.value)}>
            <option value="">همه</option>
            <option value="open">مانده‌دار</option>
            <option value="closed">تسویه‌شده</option>
          </Select>
        </div>
        {(sfFrom || sfTo || sfCustomer || sfRemaining) && (
          <Btn size="sm" variant="ghost" onClick={() => { setSfFrom(""); setSfTo(""); setSfCustomer(""); setSfRemaining(""); }} style={{ marginTop: 4 }}>× پاک‌کردن فیلتر</Btn>
        )}
      </Card>
      <Card>
        <Table cols={["ش. فاکتور", "ش. سند", "تاریخ", "مشتری", "ارز", "مبلغ نهایی", "دریافت شده", "مانده", "عملیات"]} searchable
          rows={(data.sales || []).filter(s => {
              if (sfFrom && s.date < sfFrom) return false;
              if (sfTo && s.date > sfTo) return false;
              if (sfCustomer && s.customer_id !== sfCustomer) return false;
              if (sfRemaining === "open" && !(s.remaining_amount > 0)) return false;
              if (sfRemaining === "closed" && s.remaining_amount > 0) return false;
              return true;
            }).map(s => ({ cells: [
            <strong style={{ color: theme.accentLight }}>{s.invoice_no}</strong>,
            <span style={{ color: theme.textMuted, fontSize: 11 }}>{s.voucher_no || "-"}</span>,
            s.date, (data.persons || []).find(x => x.id === s.customer_id)?.name || "-",
            s.currency_code, fmtMoney(s.final_amount),
            <span style={{ color: theme.success }}>{fmtMoney(s.received_amount)}</span>,
            <span style={{ color: s.remaining_amount > 0 ? theme.danger : theme.success }}>{fmtMoney(s.remaining_amount)}</span>,
            <div style={{display:"flex",gap:4}}>
              <button onClick={()=>openEditSale(s)} style={{padding:"4px 10px",background:"transparent",border:"1px solid "+theme.border,borderRadius:6,color:theme.text,cursor:"pointer",fontSize:11,fontFamily:"Vazirmatn"}}>✏️ ویرایش</button>
              <button onClick={()=>deleteSale(s)} style={{padding:"4px 10px",background:"transparent",border:"1px solid "+theme.danger+"55",borderRadius:6,color:theme.danger,cursor:"pointer",fontSize:11,fontFamily:"Vazirmatn"}}>🗑 حذف</button>
            </div>
          ]}))}
        />
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title={editId ? "ویرایش فاکتور فروش" : "ثبت فاکتور فروش"} width={720}>
        <div style={{ background: theme.bgInput, borderRadius: 10, padding: 14, marginBottom: 16, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, color: theme.textMuted, marginBottom: 4 }}>شماره فاکتور (اتوماتیک)</label>
            <div style={{ padding: "9px 12px", background: theme.bgCard, border: `1px solid ${theme.accent}44`, borderRadius: 8, color: theme.accentLight, fontWeight: 700 }}>{form.invoice_no}</div>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: theme.textMuted, marginBottom: 4 }}>شماره سند (اتوماتیک)</label>
            <div style={{ padding: "9px 12px", background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.textMuted }}>{form.voucher_no}</div>
          </div>
          <Input label="تاریخ *" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <SearchableSelect
            label="مشتری *"
            value={form.customer_id}
            onChange={e => setForm(p => ({ ...p, customer_id: e.target.value }))}
            options={customers.map(c => ({ value: c.id, label: c.name }))}
            placeholder="— انتخاب مشتری —"
            required
            error={submitted && !form.customer_id}
          />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <CurrencySelect label="ارز" />
            <button onClick={() => setQuickPersonOpen(true)}
              style={{ fontSize: 12, color: theme.accent, background: "transparent", border: "none", cursor: "pointer", textAlign: "right", padding: "2px 0", fontFamily: "Vazirmatn", marginTop: -8 }}>
              + افزودن مشتری جدید
            </button>
          </div>
        </div>

        <QuickPersonModal
          open={quickPersonOpen}
          onClose={() => setQuickPersonOpen(false)}
          defaultType="customer"
          onCreated={person => setForm(p => ({ ...p, customer_id: person.id }))}
        />

        {customers.length === 0 && <div style={{ background: `${theme.warning}22`, border: `1px solid ${theme.warning}44`, borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 13, color: theme.warning }}>⚠️ ابتدا از بخش «اشخاص» یک مشتری اضافه کنید</div>}

        <div style={{ margin: "16px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <strong style={{ fontSize: 14 }}>ردیف‌های کالا</strong>
            <Btn size="sm" onClick={addLine}>+ ردیف</Btn>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr 1fr auto", gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: theme.textMuted, padding: "0 4px" }}>کالا (موجودی)</span>
            <span style={{ fontSize: 11, color: theme.textMuted, textAlign: "center" }}>مقدار</span>
            <span style={{ fontSize: 11, color: theme.textMuted, textAlign: "center" }}>قیمت واحد</span>
            <span style={{ fontSize: 11, color: theme.textMuted, textAlign: "center" }}>جمع کل</span>
            <span></span>
          </div>
          {lines.map((line, i) => {
            const qty = parseFloat(line.quantity) || 0;
            const up = parseFloat(line.unit_price) || 0;
            const lineTotal = line.total_manual !== undefined ? line.total_manual : qty * up;
            const hasError = submitted && !line.item_id;
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr 1fr auto", gap: 6, marginBottom: 8, alignItems: "center" }}>
                <select value={line.item_id} onChange={e => {
                  const item = (data.items || []).find(x => x.id === e.target.value);
                  const newUp = item?.suggested_sale_price || 0;
                  const newTotal = (parseFloat(line.quantity)||0) * newUp;
                  setLines(p => p.map((l, idx) => idx === i ? { ...l, item_id: e.target.value, unit_price: newUp, total_manual: newTotal } : l));
                }} style={{ padding: "8px 10px", background: theme.bgInput, border: hasError ? "2px solid "+theme.danger : "1px solid "+theme.border, borderRadius: 8, color: line.item_id ? theme.text : theme.textMuted, fontSize: 13 }}>
                  <option value="">— انتخاب کالا —</option>
                  {(data.items || []).filter(x => x.is_active !== false).map(x => (
                    <option key={x.id} value={x.id} style={{ color: x.current_stock <= 0 ? "#ef4444" : "inherit" }}>
                      {x.name} — موجودی: {fmtQty(x.current_stock)} {x.unit}{x.current_stock <= 0 ? " ❌" : x.current_stock <= (x.min_stock_alert || 0) && x.min_stock_alert > 0 ? " ⚠️" : ""}
                    </option>
                  ))}
                </select>
                <input type="number" placeholder="مقدار"
                  value={line.quantity === 0 || line.quantity === "0" ? "" : line.quantity}
                  onChange={e => {
                    const newQty = e.target.value;
                    const newTotal = (parseFloat(newQty)||0) * (parseFloat(line.unit_price)||0);
                    setLines(p => p.map((l, idx) => idx === i ? { ...l, quantity: newQty, total_manual: newTotal } : l));
                  }}
                  style={{ padding: "8px 8px", background: theme.bgInput, border: "1px solid "+theme.border, borderRadius: 8, color: theme.text, fontSize: 13, textAlign: "left", direction: "ltr" }} />
                <input type="number" placeholder="قیمت"
                  value={line.unit_price === 0 || line.unit_price === "0" ? "" : line.unit_price}
                  onChange={e => {
                    const newUp = e.target.value;
                    const newTotal = (parseFloat(line.quantity)||0) * (parseFloat(newUp)||0);
                    setLines(p => p.map((l, idx) => idx === i ? { ...l, unit_price: newUp, total_manual: newTotal } : l));
                  }}
                  style={{ padding: "8px 8px", background: theme.bgInput, border: "1px solid "+theme.border, borderRadius: 8, color: theme.text, fontSize: 13, textAlign: "left", direction: "ltr" }} />
                <input type="number" placeholder="جمع"
                  value={lineTotal === 0 ? "" : lineTotal}
                  onChange={e => {
                    const newTotal = e.target.value;
                    const newUp = qty > 0 ? Math.round(((parseFloat(newTotal)||0) / qty) * 1e8) / 1e8 : 0;
                    setLines(p => p.map((l, idx) => idx === i ? { ...l, total_manual: parseFloat(newTotal)||0, unit_price: newUp } : l));
                  }}
                  style={{ padding: "8px 8px", background: theme.bgCard, border: "1px solid "+theme.accent+"66", borderRadius: 8, color: theme.accentLight, fontSize: 13, fontWeight: 600, textAlign: "left", direction: "ltr" }} />
                <button onClick={() => removeLine(i)} style={{ padding: "6px 10px", background: "transparent", border: "1px solid "+theme.danger+"55", borderRadius: 6, color: theme.danger, cursor: "pointer", fontSize: 14, fontFamily: "Vazirmatn" }}>×</button>
              </div>
            );
          })}
        </div>

        <div style={{ background: theme.bgInput, borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: theme.textMuted }}>جمع</span><strong><Money value={totalLines} currency={form.currency_code} /></strong></div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, alignItems: "center" }}>
            <span style={{ color: theme.textMuted }}>تخفیف کلی</span>
            <input type="number" value={form.discount_amount} onChange={e => setForm(p => ({ ...p, discount_amount: e.target.value }))}
              style={{ width: 120, padding: "5px 10px", background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 6, color: theme.text }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, borderTop: `1px solid ${theme.border}` }}><span style={{ fontWeight: 700 }}>مبلغ نهایی</span><strong style={{ color: theme.success, fontSize: 16 }}><Money value={finalAmount} currency={form.currency_code} /></strong></div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Input label="مبلغ دریافت‌شده" type="number" value={form.received_amount} onChange={e => setForm(p => ({ ...p, received_amount: e.target.value }))} />
          <Select label="دریافت در حساب" value={form.receipt_account_id} onChange={e => setForm(p => ({ ...p, receipt_account_id: e.target.value }))}>
            <option value="">— انتخاب حساب —</option>
            {cashAccounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency_code})</option>)}
          </Select>
        </div>
        <Textarea label="توضیحات" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setModal(false)}>انصراف</Btn>
          <Btn variant="success" onClick={save} disabled={saving} style={{ opacity: saving ? 0.6 : 1 }}>
            {saving ? "⏳ در حال ثبت..." : (editId ? "✓ ذخیره ویرایش" : "✓ ثبت فروش")}
          </Btn>
        </div>
      </Modal>
    </div>
  );
}

// =====================================================
// PAYMENT PAGE
// =====================================================
function PaymentPage({ type }) {
  const { data } = useApp();
  const { sbWrite, refresh, profile: myProfile, auditLog } = useSb();
  const [modal, setModal] = useState(false);
  const emptyForm = { voucher_no: "", date: today(), person_id: "", currency_code: "USD", amount: 0, account_id: "", method: "cash", reference_no: "", notes: "" };
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [toast, showToast] = useToast();
  const isReceive = type === "receive";
  const cashAccounts = (data.accounts || []).filter(a => a.type === "cash" || a.type === "bank");

  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      if (!form.person_id) { showToast("شخص الزامی است", "error"); return; }
      if (!(parseFloat(form.amount) > 0)) { showToast("مبلغ الزامی است", "error"); return; }
      if (!form.account_id) { showToast("حساب الزامی است", "error"); return; }

      const personCtrlAcc = (data.accounts || []).find(a => a.type === "person_control");
      if (!personCtrlAcc) { showToast("حساب کنترل اشخاص برای این ارز تعریف نشده است", "error"); return; }

      const payId = editId || uid();
      const amount = parseFloat(form.amount);
      const voucherNo = form.voucher_no || (await getNextVoucherNo()) || genVoucherNoAll(data);

      if (voucherNo && (data.payments || []).some(p => p.voucher_no === voucherNo && p.id !== editId)) {
        showToast("شماره سند تکراری است", "error"); return;
      }

      const person = (data.persons || []).find(p => p.id === form.person_id);
      const desc = isReceive ? "دریافت از " + (person?.name || "") : "پرداخت به " + (person?.name || "");

      const ledgerEntries = isReceive
        ? [
            { id: uid(), date: form.date, voucher_id: payId, voucher_no: voucherNo, source_type: "payment", source_id: payId, account_id: form.account_id,    person_id: null,           currency_code: form.currency_code, debit: amount, credit: 0,      description: desc },
            { id: uid(), date: form.date, voucher_id: payId, voucher_no: voucherNo, source_type: "payment", source_id: payId, account_id: personCtrlAcc.id,    person_id: form.person_id, currency_code: form.currency_code, debit: 0,      credit: amount, description: desc }
          ]
        : [
            { id: uid(), date: form.date, voucher_id: payId, voucher_no: voucherNo, source_type: "payment", source_id: payId, account_id: personCtrlAcc.id,    person_id: form.person_id, currency_code: form.currency_code, debit: amount, credit: 0,      description: desc },
            { id: uid(), date: form.date, voucher_id: payId, voucher_no: voucherNo, source_type: "payment", source_id: payId, account_id: form.account_id,    person_id: null,           currency_code: form.currency_code, debit: 0,      credit: amount, description: desc }
          ];

      if (!isLedgerBalanced(ledgerEntries)) { showToast("سند حسابداری متعادل نیست؛ ثبت انجام نشد", "error"); return; }

      const wasEditPay = !!editId;
      const payRow = { ...form, id: payId, type, amount, voucher_no: voucherNo, created_by: myProfile?.id, created_at: wasEditPay ? undefined : new Date().toISOString() };
      const { data: rpcRes, error: rpcErr } = await supabase.rpc("save_payment", {
        p: { payment: payRow, ledger: ledgerEntries, is_edit: wasEditPay }
      });
      const rpcOk = rpcRes && (typeof rpcRes === "object" ? rpcRes.ok : false);
      const rawErr = rpcErr?.message || (rpcRes && typeof rpcRes === "object" ? rpcRes.error : String(rpcRes)) || "خطای ناشناخته";
      if (rpcErr || !rpcOk) { showToast("خطا در ثبت: " + rawErr, "error"); return; }
      await auditLog(wasEditPay ? "update" : "insert", "payments", payId, { amount, type }, myProfile?.id);
      await refresh();
      setModal(false); setForm(emptyForm); setEditId(null);
      showToast(wasEditPay
        ? (isReceive ? "دریافت با موفقیت ویرایش شد ✓" : "پرداخت با موفقیت ویرایش شد ✓")
        : (isReceive ? "دریافت با موفقیت ثبت شد ✓" : "پرداخت با موفقیت ثبت شد ✓"));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} visible={toast.visible} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>{isReceive ? "💰 دریافت از شخص" : "💸 پرداخت به شخص"}</h2>
        <Btn onClick={() => { setEditId(null); setForm({ ...emptyForm, voucher_no: genVoucherNoAll(data) }); setModal(true); }}>+ {isReceive ? "ثبت دریافت" : "ثبت پرداخت"}</Btn>
      </div>
      <Card>
        <Table cols={["تاریخ", "شخص", "ارز", "مبلغ", "حساب", "روش", "توضیحات", "عملیات"]} searchable
          rows={(data.payments || []).filter(p => p.type === type).map(p => ({ cells: [
            p.date, (data.persons || []).find(x => x.id === p.person_id)?.name || "-",
            p.currency_code,
            <span style={{ color: isReceive ? theme.success : theme.danger, fontWeight: 600 }}>{fmtMoney(p.amount)}</span>,
            (data.accounts || []).find(a => a.id === p.account_id)?.name || "-",
            p.method, p.notes || "-",
            <div style={{display:"flex",gap:4}}><button onClick={()=>{setEditId(p.id); setForm({...p}); setModal(true)}} style={{padding:"4px 10px",background:"transparent",border:"1px solid "+theme.border,borderRadius:6,color:theme.text,cursor:"pointer",fontSize:11,fontFamily:"Vazirmatn"}}>✏️ ویرایش</button><button onClick={()=>{ if(!window.confirm("حذف شود؟")) return; (async () => { const { data: r, error: e } = await supabase.rpc("delete_payment", { p_payment_id: p.id }); if(e||!r?.ok){alert("خطا: "+(e?.message||r?.error||"خطای ناشناخته")); return;} await refresh(); })() }} style={{padding:"4px 10px",background:"transparent",border:"1px solid "+theme.danger+"55",borderRadius:6,color:theme.danger,cursor:"pointer",fontSize:11,fontFamily:"Vazirmatn"}}>🗑 حذف</button></div>
          ]}))}
        />
      </Card>
      <Modal open={modal} onClose={() => setModal(false)} title={isReceive ? "ثبت دریافت" : "ثبت پرداخت"}>
        <Input label="تاریخ" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
        <Select label="شخص *" value={form.person_id} onChange={e => setForm(p => ({ ...p, person_id: e.target.value }))}>
          <option value="">— انتخاب شخص —</option>
          {(data.persons || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <CurrencySelect label="ارز" value={form.currency_code} onChange={e => setForm(p => ({ ...p, currency_code: e.target.value }))} />
          <Input label="مبلغ *" type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} />
        </div>
        <Select label={isReceive ? "دریافت در حساب *" : "پرداخت از حساب *"} value={form.account_id} onChange={e => setForm(p => ({ ...p, account_id: e.target.value }))}>
          <option value="">— انتخاب حساب —</option>
          {cashAccounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency_code})</option>)}
        </Select>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Select label="روش" value={form.method} onChange={e => setForm(p => ({ ...p, method: e.target.value }))}>
            <option value="cash">نقد</option><option value="bank">بانک</option>
            <option value="transfer">حواله</option><option value="check">چک</option>
          </Select>
          <Input label="شماره مرجع" value={form.reference_no} onChange={e => setForm(p => ({ ...p, reference_no: e.target.value }))} />
        </div>
        <Textarea label="توضیحات" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setModal(false)}>انصراف</Btn>
          <Btn variant={isReceive ? "success" : "warning"} onClick={save} disabled={saving}>{saving ? "⏳ در حال ثبت..." : "✓ ثبت"}</Btn>
        </div>
      </Modal>
    </div>
  );
}

// =====================================================
// EXPENSE PAGE — با امکان انتخاب شخص پرداخت‌کننده
// =====================================================
function ExpensePage() {
  const { data } = useApp();
  const { sbWrite, refresh, profile: myProfile, auditLog } = useSb();
  const [toast, showToast] = useToast();
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const emptyForm = { voucher_no: "", date: today(), category_id: "", currency_code: "USD", amount: 0, account_id: "", person_id: "", paid_by_person: false, description: "" };
  const [form, setForm] = useState(emptyForm);

  const cashAccounts = (data.accounts || []).filter(a => a.type === "cash" || a.type === "bank");
  const expAccount = (data.accounts || []).find(a => a.type === "expense");
  const personCtrlAccExp = (data.accounts || []).find(a => a.type === "person_control");

  const setEditExpense = (e) => {
    setEditId(e.id);
    setForm({ ...e });
    setModal(true);
  };

  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      if (!form.category_id) { showToast("دسته هزینه الزامی است", "error"); return; }
      if (!(parseFloat(form.amount) > 0)) { showToast("مبلغ الزامی است", "error"); return; }
      if (!form.paid_by_person && !form.account_id) { showToast("حساب پرداخت الزامی است", "error"); return; }
      if (form.paid_by_person && !form.person_id) { showToast("شخص پرداخت‌کننده الزامی است", "error"); return; }
      if (!expAccount) { showToast("حساب هزینه‌ها تعریف نشده است", "error"); return; }
      if (form.paid_by_person && !personCtrlAccExp) { showToast("حساب کنترل اشخاص تعریف نشده است", "error"); return; }

      const expId = editId || uid();
      const amount = parseFloat(form.amount);
      const voucherNo = form.voucher_no || (await getNextVoucherNo()) || genVoucherNoAll(data);

      if (voucherNo && (data.expenses || []).some(e => e.voucher_no === voucherNo && e.id !== editId)) {
        showToast("شماره سند هزینه تکراری است", "error"); return;
      }

      const cat = (data.expenseCategories || []).find(c => c.id === form.category_id);
      const person = (data.persons || []).find(p => p.id === form.person_id);
      const desc = "هزینه " + (cat?.name || "") + ": " + (form.description || "");

      const ledgerEntries = [];
      if (form.paid_by_person) {
        ledgerEntries.push({ id: uid(), date: form.date, voucher_id: expId, voucher_no: voucherNo, source_type: "expense", source_id: expId, account_id: expAccount.id, person_id: null, currency_code: form.currency_code, debit: amount, credit: 0, description: desc });
        if (personCtrlAccExp) ledgerEntries.push({ id: uid(), date: form.date, voucher_id: expId, voucher_no: voucherNo, source_type: "expense", source_id: expId, account_id: personCtrlAccExp.id, person_id: form.person_id, currency_code: "USD", debit: 0, credit: amount, description: desc + " — پرداخت توسط " + (person?.name || "") });
      } else {
        ledgerEntries.push({ id: uid(), date: form.date, voucher_id: expId, voucher_no: voucherNo, source_type: "expense", source_id: expId, account_id: expAccount.id, person_id: null, currency_code: form.currency_code, debit: amount, credit: 0, description: desc });
        ledgerEntries.push({ id: uid(), date: form.date, voucher_id: expId, voucher_no: voucherNo, source_type: "expense", source_id: expId, account_id: form.account_id, person_id: null, currency_code: form.currency_code, debit: 0, credit: amount, description: desc });
      }

      if (!isLedgerBalanced(ledgerEntries)) { showToast("سند حسابداری متعادل نیست؛ ثبت انجام نشد", "error"); return; }

      const wasEditExp = !!editId;
      const expRow = { ...form, id: expId, amount, voucher_no: voucherNo, created_by: myProfile?.id, created_at: wasEditExp ? undefined : new Date().toISOString() };
      const { data: rpcRes, error: rpcErr } = await supabase.rpc("save_expense", {
        p: { expense: expRow, ledger: ledgerEntries, is_edit: wasEditExp }
      });
      const rpcOk = rpcRes && (typeof rpcRes === "object" ? rpcRes.ok : false);
      const rawErr = rpcErr?.message || (rpcRes && typeof rpcRes === "object" ? rpcRes.error : String(rpcRes)) || "خطای ناشناخته";
      if (rpcErr || !rpcOk) { showToast("خطا در ثبت: " + rawErr, "error"); return; }
      await auditLog(wasEditExp ? "update" : "insert", "expenses", expId, { amount }, myProfile?.id);
      await refresh();
      setModal(false); setForm(emptyForm); setEditId(null);
      showToast(wasEditExp ? "هزینه با موفقیت ویرایش شد ✓" : "هزینه با موفقیت ثبت شد ✓");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} visible={toast.visible} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>🧾 هزینه‌ها</h2>
        <Btn onClick={() => { setEditId(null); setForm({ ...emptyForm, voucher_no: genVoucherNoAll(data) }); setModal(true); }}>+ ثبت هزینه</Btn>
      </div>
      <Card>
        <Table cols={["تاریخ", "دسته", "ارز", "مبلغ", "پرداخت‌کننده", "توضیحات", "عملیات"]} searchable
          rows={(data.expenses || []).map(e => ({ cells: [
            e.date,
            (data.expenseCategories || []).find(c => c.id === e.category_id)?.name || "-",
            e.currency_code,
            <span style={{ color: theme.danger }}>{fmtMoney(e.amount)}</span>,
            e.paid_by_person
              ? <Badge color={theme.warning}>{(data.persons || []).find(p => p.id === e.person_id)?.name || "-"}</Badge>
              : (data.accounts || []).find(a => a.id === e.account_id)?.name || "-",
            e.description || "-",
            <div style={{display:"flex",gap:4}}><button onClick={()=>{setEditExpense(e)}} style={{padding:"4px 10px",background:"transparent",border:"1px solid "+theme.border,borderRadius:6,color:theme.text,cursor:"pointer",fontSize:11,fontFamily:"Vazirmatn"}}>✏️ ویرایش</button><button onClick={()=>{ if(!window.confirm("حذف شود؟")) return; (async () => { const { data: r, error: er } = await supabase.rpc("delete_expense", { p_expense_id: e.id }); if(er||!r?.ok){alert("خطا: "+(er?.message||r?.error||"خطای ناشناخته")); return;} await refresh(); })() }} style={{padding:"4px 10px",background:"transparent",border:"1px solid "+theme.danger+"55",borderRadius:6,color:theme.danger,cursor:"pointer",fontSize:11,fontFamily:"Vazirmatn"}}>🗑 حذف</button></div>
          ]}))}
        />
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title="ثبت هزینه">
        <Input label="تاریخ" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
        <Select label="دسته هزینه *" value={form.category_id} onChange={e => setForm(p => ({ ...p, category_id: e.target.value }))}>
          <option value="">— انتخاب دسته —</option>
          {(data.expenseCategories || []).filter(c => c.is_active !== false).map(c => <option key={c.id} value={c.id}>{c.code ? c.code + " — " : ""}{c.name}</option>)}
        </Select>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <CurrencySelect label="ارز" value={form.currency_code} onChange={e => setForm(p => ({ ...p, currency_code: e.target.value }))} />
          <Input label="مبلغ *" type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} />
        </div>

        {/* کلید انتخاب نوع پرداخت */}
        <div style={{ background: theme.bgInput, borderRadius: 10, padding: 14, marginBottom: 14 }}>
          <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 600 }}>چه کسی هزینه را پرداخت کرده؟</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setForm(p => ({ ...p, paid_by_person: false }))}
              style={{ flex: 1, padding: "10px", borderRadius: 8, border: `2px solid ${!form.paid_by_person ? theme.accent : theme.border}`, background: !form.paid_by_person ? `${theme.accent}22` : "transparent", color: !form.paid_by_person ? theme.accentLight : theme.textMuted, fontFamily: "Vazirmatn", fontSize: 13, fontWeight: 600 }}>
              💳 از صندوق / بانک
            </button>
            <button onClick={() => setForm(p => ({ ...p, paid_by_person: true }))}
              style={{ flex: 1, padding: "10px", borderRadius: 8, border: `2px solid ${form.paid_by_person ? theme.warning : theme.border}`, background: form.paid_by_person ? `${theme.warning}22` : "transparent", color: form.paid_by_person ? theme.warning : theme.textMuted, fontFamily: "Vazirmatn", fontSize: 13, fontWeight: 600 }}>
              👤 توسط شخص (بستانکار می‌شود)
            </button>
          </div>
        </div>

        {!form.paid_by_person && (
          <Select label="پرداخت از حساب *" value={form.account_id} onChange={e => setForm(p => ({ ...p, account_id: e.target.value }))}>
            <option value="">— انتخاب حساب —</option>
            {cashAccounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency_code})</option>)}
          </Select>
        )}

        {form.paid_by_person && (
          <div>
            <Select label="شخص پرداخت‌کننده *" value={form.person_id} onChange={e => setForm(p => ({ ...p, person_id: e.target.value }))}>
              <option value="">— انتخاب شخص —</option>
              {(data.persons || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
            <div style={{ background: `${theme.warning}11`, border: `1px solid ${theme.warning}33`, borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 12, color: theme.warning }}>
              ℹ️ این شخص {fmtMoney(parseFloat(form.amount) || 0, form.currency_code)} بستانکار می‌شود
            </div>
          </div>
        )}

        <Textarea label="توضیحات" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setModal(false)}>انصراف</Btn>
          <Btn variant="danger" onClick={save} disabled={saving}>{saving ? "⏳ در حال ثبت..." : "✓ ثبت هزینه"}</Btn>
        </div>
      </Modal>
    </div>
  );
}

// =====================================================
// MANUAL VOUCHER PAGE — شماره سند اتوماتیک
// =====================================================
function ManualVoucherPage() {
  const { data } = useApp();
  const { sbWrite, refresh, profile: myProfile, auditLog } = useSb();
  const [toast, showToast] = useToast();
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [form, setForm] = useState({ voucher_no: "", date: today(), description: "" });
  const [lines, setLines] = useState([
    { account_id: "", person_id: "", currency_code: "USD", debit: 0, credit: 0, description: "" },
    { account_id: "", person_id: "", currency_code: "USD", debit: 0, credit: 0, description: "" }
  ]);

  const openModal = () => {
    setEditId(null);
    setForm({ voucher_no: genVoucherNoAll(data), date: today(), description: "" });
    setLines([
      { account_id: "", person_id: "", currency_code: "USD", debit: 0, credit: 0, description: "" },
      { account_id: "", person_id: "", currency_code: "USD", debit: 0, credit: 0, description: "" }
    ]);
    setModal(true);
  };

  const setEditVoucher = (v) => {
    setEditId(v.id);
    setForm({ ...v });
    // load lines from ledger (manualVoucherLines is not stored separately)
    const ledgerLines = (data.ledger || [])
      .filter(l => l.source_id === v.id && l.source_type === "manual")
      .map(l => ({
        account_id: l.account_id || "",
        person_id: l.person_id || "",
        currency_code: l.currency_code || "USD",
        debit: l.debit || 0,
        credit: l.credit || 0,
        description: l.description || ""
      }));
    setLines(ledgerLines.length > 0
      ? ledgerLines
      : [
          { account_id: "", person_id: "", currency_code: "USD", debit: 0, credit: 0, description: "" },
          { account_id: "", person_id: "", currency_code: "USD", debit: 0, credit: 0, description: "" }
        ]);
    setModal(true);
  };

  const totalDebit = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.001;

  // تراز به تفکیک ارز
  const balanceByCurrency = ["USD"].map(cur => {
    const d = lines.filter(l => l.currency_code === cur).reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
    const c = lines.filter(l => l.currency_code === cur).reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
    return { cur, d, c, diff: Math.abs(d - c), ok: Math.abs(d - c) < 0.001 };
  }).filter(x => x.d > 0 || x.c > 0);
  const isBalancedByCurrency = balanceByCurrency.every(x => x.ok);

  const addLine = () => setLines(p => [...p, { account_id: "", person_id: "", currency_code: "USD", debit: 0, credit: 0, description: "" }]);
  const removeLine = (i) => setLines(p => p.filter((_, idx) => idx !== i));
  const updateLine = (i, key, val) => setLines(p => p.map((l, idx) => idx === i ? { ...l, [key]: val } : l));

  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      if (!isBalancedByCurrency) {
        const issues = balanceByCurrency.filter(x => !x.ok).map(x => x.cur + ": اختلاف " + fmtMoney(x.diff)).join("، ");
        showToast("سند به تفکیک ارز متعادل نیست: " + issues, "error"); return;
      }
      if (lines.some(l => !l.account_id)) { showToast("حساب را در تمام ردیف‌ها انتخاب کنید", "error"); return; }

      const blockedTypes = ["inventory", "income", "cogs"];
      for (let i = 0; i < lines.length; i++) {
        const acc = (data.accounts || []).find(a => a.id === lines[i].account_id);
        if (acc && blockedTypes.includes(acc.type)) {
          showToast("ردیف " + (i+1) + ": حساب «" + acc.name + "» در سند دستی مجاز نیست", "error"); return;
        }
      }

      for (let i = 0; i < lines.length; i++) {
        const l = lines[i];
        const d = parseFloat(l.debit) || 0;
        const c = parseFloat(l.credit) || 0;
        if (d > 0 && c > 0) { showToast("ردیف " + (i+1) + ": یک ردیف نمی‌تواند هم بدهکار و هم بستانکار باشد", "error"); return; }
        if (d === 0 && c === 0) { showToast("ردیف " + (i+1) + ": مبلغ صفر قابل قبول نیست", "error"); return; }
        const acc = (data.accounts || []).find(a => a.id === l.account_id);
        if (acc && ["receivable","payable","person_control"].includes(acc.type) && !l.person_id) {
          showToast("ردیف " + (i+1) + ": حساب «" + acc.name + "» نیاز به انتخاب شخص دارد", "error"); return;
        }
      }

      if (form.voucher_no && (data.manualVouchers || []).some(v => v.voucher_no === form.voucher_no && v.id !== editId)) {
        showToast("شماره سند دستی تکراری است", "error"); return;
      }

      const vId = editId || uid();
      const ledgerEntries = lines.map(l => {
        const acc = (data.accounts || []).find(a => a.id === l.account_id);
        const allowPersonId = acc && ["person_control", "receivable", "payable"].includes(acc.type);
        return {
          id: uid(), date: form.date, voucher_id: vId, voucher_no: form.voucher_no,
          source_type: "manual", source_id: vId,
          account_id: l.account_id,
          person_id: allowPersonId ? (l.person_id || null) : null,
          currency_code: "USD",
          debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0,
          description: l.description || form.description
        };
      });

      if (!isLedgerBalanced(ledgerEntries)) { showToast("سند حسابداری متعادل نیست؛ ثبت انجام نشد", "error"); return; }

      const mvRow = { ...form, id: vId, total_debit: totalDebit, total_credit: totalCredit, is_balanced: true, created_by: myProfile?.id, created_at: editId ? undefined : new Date().toISOString() };
      const { data: rpcRes, error: rpcErr } = await supabase.rpc("save_manual_voucher", {
        p: { manual_voucher: mvRow, ledger: ledgerEntries, is_edit: !!editId }
      });
      const rpcOk = rpcRes && (typeof rpcRes === "object" ? rpcRes.ok : false);
      const rawErr = rpcErr?.message || (rpcRes && typeof rpcRes === "object" ? rpcRes.error : String(rpcRes)) || "خطای ناشناخته";
      if (rpcErr || !rpcOk) { showToast("خطا در ثبت: " + rawErr, "error"); return; }
      await auditLog(editId ? "update" : "insert", "manual_vouchers", vId, { voucher_no: form.voucher_no }, myProfile?.id);
      await refresh();
      setModal(false); setEditId(null);
      showToast(editId ? "سند با موفقیت ویرایش شد" : "سند با موفقیت ثبت شد");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} visible={toast.visible} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>📝 سند دستی</h2>
        <Btn onClick={openModal}>+ سند جدید</Btn>
      </div>
      <Card>
        <Table cols={["شماره سند", "تاریخ", "شرح", "جمع بدهکار", "جمع بستانکار", "متعادل", "عملیات"]}
          rows={(data.manualVouchers || []).filter(v => !v.source_type || v.source_type === "manual").map(v => ({ cells: [
            <strong style={{ color: theme.accentLight }}>{v.voucher_no}</strong>,
            v.date, v.description,
            fmtMoney(v.total_debit), fmtMoney(v.total_credit),
            <Badge color={v.is_balanced ? theme.success : theme.danger}>{v.is_balanced ? "✓ متعادل" : "✗ خطا"}</Badge>,
            <div style={{display:"flex",gap:4}}><button onClick={()=>setEditVoucher(v)} style={{padding:"4px 10px",background:"transparent",border:"1px solid "+theme.border,borderRadius:6,color:theme.text,cursor:"pointer",fontSize:11,fontFamily:"Vazirmatn"}}>✏️ ویرایش</button><button onClick={()=>{ if(!window.confirm("سند حذف شود؟")) return; (async () => { const { data: r, error: e } = await supabase.rpc("delete_manual_voucher", { p_voucher_id: v.id }); if(e||!r?.ok){alert("خطا: "+(e?.message||r?.error)); return;} await refresh(); })(); }} style={{padding:"4px 10px",background:"transparent",border:"1px solid "+theme.danger+"55",borderRadius:6,color:theme.danger,cursor:"pointer",fontSize:11,fontFamily:"Vazirmatn"}}>🗑 حذف</button></div>
          ]}))}
        />
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title={editId ? "ویرایش سند دستی" : "ثبت سند دستی"} width={820}>
        <div style={{ background: theme.bgInput, borderRadius: 10, padding: 14, marginBottom: 16, display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 12 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, color: theme.textMuted, marginBottom: 4 }}>شماره سند (اتوماتیک)</label>
            <div style={{ padding: "9px 12px", background: theme.bgCard, border: `1px solid ${theme.accent}44`, borderRadius: 8, color: theme.accentLight, fontWeight: 700 }}>{form.voucher_no}</div>
          </div>
          <Input label="تاریخ *" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
          <Textarea label="شرح سند" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} style={{ minHeight: 42 }} />
        </div>

        <div style={{ overflowX: "auto", marginBottom: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                {["حساب *", "شخص", "ارز", "بدهکار", "بستانکار", "شرح", ""].map((h, i) => <th key={i} style={{ padding: "8px 8px", textAlign: "right", fontSize: 11, color: theme.textMuted }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => {
                const lineAcc = (data.accounts || []).find(a => a.id === line.account_id);
                const isPersonCtrl = lineAcc && ["person_control","receivable","payable"].includes(lineAcc.type);
                return (
                <tr key={i} style={{ borderBottom: `1px solid ${theme.border}22` }}>
                  <td style={{ padding: "5px 5px" }}>
                    <select value={line.account_id} onChange={e => {
                      const newAcc = (data.accounts || []).find(a => a.id === e.target.value);
                      // حساب‌های سیستمی موجودی، درآمد، COGS در سند دستی قابل انتخاب نیستند
                      const blocked = ["inventory", "income", "cogs"].includes(newAcc?.type);
                      if (blocked) {
                        alert("حساب «" + newAcc.name + "» سیستمی است و در سند دستی قابل استفاده نیست.\nاین حساب‌ها فقط از طریق فاکتور خرید/فروش به‌روز می‌شوند.");
                        return;
                      }
                      const newIsPersonCtrl = newAcc && ["person_control","receivable","payable"].includes(newAcc.type);
                      updateLine(i, "account_id", e.target.value);
                      if (!newIsPersonCtrl) updateLine(i, "person_id", "");
                    }}
                      style={{ padding: "7px 8px", background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 6, color: theme.text, fontSize: 12, width: "100%", minWidth: 160 }}>
                      <option value="">— حساب —</option>
                      {(data.accounts || []).map(a => {
                        const isBlocked = ["inventory", "income", "cogs"].includes(a.type);
                        return (
                          <option key={a.id} value={a.id}
                            style={{ color: isBlocked ? theme.textMuted : "inherit" }}>
                            {a.name}{isBlocked ? " 🔒" : ""}
                          </option>
                        );
                      })}
                    </select>
                  </td>
                  <td style={{ padding: "5px 5px" }}>
                    <select value={line.person_id || ""} onChange={e => updateLine(i, "person_id", e.target.value)}
                      disabled={!isPersonCtrl}
                      style={{ padding: "7px 8px", background: isPersonCtrl ? theme.bgInput : theme.bg, border: `1px solid ${isPersonCtrl ? theme.border : theme.border+"44"}`, borderRadius: 6, color: isPersonCtrl ? theme.text : theme.textMuted, fontSize: 12, minWidth: 120, opacity: isPersonCtrl ? 1 : 0.4, cursor: isPersonCtrl ? "auto" : "not-allowed" }}>
                      <option value="">-</option>
                      {isPersonCtrl && (data.persons || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    {!isPersonCtrl && line.account_id && (
                      <div style={{ fontSize: 9, color: theme.textMuted, marginTop: 2 }}>— فقط کنترل اشخاص</div>
                    )}
                  </td>
                  <td style={{ padding: "5px 5px" }}>
                    <select value={line.currency_code} onChange={e => updateLine(i, "currency_code", e.target.value)}
                      style={{ padding: "7px 8px", background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 6, color: theme.text, fontSize: 12 }}>
                      <option value="USD">USD</option>
                      
                    </select>
                  </td>
                  {["debit", "credit"].map(f => (
                    <td key={f} style={{ padding: "5px 5px" }}>
                      <input type="number" value={line[f]} onChange={e => updateLine(i, f, e.target.value)}
                        style={{ padding: "7px 8px", background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 6, color: theme.text, fontSize: 12, width: 110 }} />
                    </td>
                  ))}
                  <td style={{ padding: "5px 5px" }}>
                    <input value={line.description} onChange={e => updateLine(i, "description", e.target.value)}
                      style={{ padding: "7px 8px", background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 6, color: theme.text, fontSize: 12, width: 130 }} />
                  </td>
                  <td style={{ padding: "5px 5px" }}>
                    <button onClick={() => removeLine(i)} style={{ background: "none", border: "none", color: theme.danger, fontSize: 18, cursor: "pointer" }}>×</button>
                  </td>
                </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: `2px solid ${theme.border}` }}>
                <td colSpan={3} style={{ padding: 10 }}><Btn size="sm" variant="ghost" onClick={addLine}>+ ردیف</Btn></td>
                <td style={{ padding: 10, fontWeight: 700, color: theme.accent }}>{fmtMoney(totalDebit)}</td>
                <td style={{ padding: 10, fontWeight: 700, color: theme.accent }}>{fmtMoney(totalCredit)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>

        <div style={{ background: isBalanced ? `${theme.success}11` : `${theme.danger}11`, border: `1px solid ${isBalanced ? theme.success : theme.danger}33`, borderRadius: 10, padding: 12, marginBottom: 16, textAlign: "center" }}>
          <div style={{ textAlign: "center", marginBottom: balanceByCurrency.length > 0 ? 8 : 0 }}>
            {isBalancedByCurrency
              ? <span style={{ color: theme.success, fontWeight: 700 }}>✓ سند متعادل است</span>
              : <span style={{ color: theme.danger, fontWeight: 700 }}>⚠ سند متعادل نیست</span>}
          </div>
          {balanceByCurrency.map(x => (
            <div key={x.cur} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 8px", background: x.ok ? theme.success+"11" : theme.danger+"11", borderRadius: 6, marginTop: 4 }}>
              <span style={{ color: theme.textMuted }}>{x.cur}</span>
              <span>بدهکار: <strong>{fmtMoney(x.d)}</strong></span>
              <span>بستانکار: <strong>{fmtMoney(x.c)}</strong></span>
              <span style={{ color: x.ok ? theme.success : theme.danger, fontWeight: 700 }}>{x.ok ? "✓" : "اختلاف: "+fmtMoney(x.diff)}</span>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setModal(false)}>انصراف</Btn>
          <Btn onClick={save} disabled={saving} style={{ opacity: isBalancedByCurrency && !saving ? 1 : 0.4 }}>{saving ? "⏳ در حال ثبت..." : "✓ ثبت سند"}</Btn>
        </div>
      </Modal>
    </div>
  );
}

// =====================================================
// PERSON TRANSFER PAGE — [فاز۳-آیتم۱۶]
// =====================================================
function PersonTransferPage() {
  const { data } = useApp();
  const { sbWrite, refresh, profile: myProfile, auditLog } = useSb();
  const [toast, showToast] = useToast();
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const emptyForm = { voucher_no: "", date: today(), from_person_id: "", to_person_id: "", currency_code: "USD", amount: 0, reference_no: "", notes: "" };
  const [form, setForm] = useState(emptyForm);

  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      if (!form.from_person_id) { showToast("پرداخت‌کننده الزامی است", "error"); return; }
      if (!form.to_person_id) { showToast("دریافت‌کننده الزامی است", "error"); return; }
      if (form.from_person_id === form.to_person_id) { showToast("پرداخت‌کننده و دریافت‌کننده نمی‌توانند یکسان باشند", "error"); return; }
      if (!(parseFloat(form.amount) > 0)) { showToast("مبلغ الزامی است", "error"); return; }

      const personCtrlAcc = (data.accounts || []).find(a => a.type === "person_control");
      if (!personCtrlAcc) { showToast("حساب کنترل اشخاص برای این ارز تعریف نشده است", "error"); return; }

      const tId = editId || uid();
      const vNo = form.voucher_no || (await getNextVoucherNo()) || genVoucherNoAll(data);

      if (vNo && (data.manualVouchers || []).some(v => v.voucher_no === vNo && v.source_type === "person_transfer" && v.id !== editId)) {
        showToast("شماره سند انتقال تکراری است", "error"); return;
      }

      const amount = parseFloat(form.amount);
      const fromPerson = (data.persons || []).find(p => p.id === form.from_person_id);
      const toPerson = (data.persons || []).find(p => p.id === form.to_person_id);
      const desc = "انتقال از " + (fromPerson?.name || "") + " به " + (toPerson?.name || "");

      const ledgerEntries = [
        { id: uid(), date: form.date, voucher_id: tId, voucher_no: vNo, source_type: "person_transfer", source_id: tId, account_id: personCtrlAcc.id, person_id: form.to_person_id,   currency_code: form.currency_code, debit: amount, credit: 0,      description: desc },
        { id: uid(), date: form.date, voucher_id: tId, voucher_no: vNo, source_type: "person_transfer", source_id: tId, account_id: personCtrlAcc.id, person_id: form.from_person_id, currency_code: form.currency_code, debit: 0,      credit: amount, description: desc }
      ];

      if (!isLedgerBalanced(ledgerEntries)) { showToast("سند حسابداری متعادل نیست؛ ثبت انجام نشد", "error"); return; }

      const wasEdit = !!editId;
      const ptRow = {
        id: tId, voucher_no: vNo, date: form.date,
        description: form.notes || desc,
        total_debit: amount, total_credit: amount, is_balanced: true,
        source_type: "person_transfer",
        created_by: myProfile?.id,
        created_at: wasEdit ? undefined : new Date().toISOString()
      };
      const { data: rpcRes, error: rpcErr } = await supabase.rpc("save_person_transfer", {
        p: { transfer: ptRow, ledger: ledgerEntries, is_edit: wasEdit }
      });
      const rpcOk = rpcRes && (typeof rpcRes === "object" ? rpcRes.ok : false);
      const rawErr = rpcErr?.message || (rpcRes && typeof rpcRes === "object" ? rpcRes.error : String(rpcRes)) || "خطای ناشناخته";
      if (rpcErr || !rpcOk) { showToast("خطا در ثبت: " + rawErr, "error"); return; }
      await refresh();
      setModal(false); setEditId(null); setForm(emptyForm);
      showToast(wasEdit ? "انتقال با موفقیت ویرایش شد ✓" : "انتقال با موفقیت ثبت شد ✓");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const transfers = (data.manualVouchers || []).filter(v => v.source_type === "person_transfer");

  // اطلاعات جزئی هر انتقال را از ledger می‌خوانیم چون در manual_vouchers ذخیره نمی‌شوند
  const getTransferDetails = (t) => {
    const fromLine = (data.ledger || []).find(l => l.source_id === t.id && l.credit > 0);
    const toLine   = (data.ledger || []).find(l => l.source_id === t.id && l.debit  > 0);
    return {
      from_person_id: fromLine ? fromLine.person_id : "",
      to_person_id:   toLine   ? toLine.person_id   : "",
      currency_code:  fromLine ? fromLine.currency_code : "USD",
      amount:         t.total_debit || 0,
      fromName: (data.persons || []).find(p => p.id === fromLine?.person_id)?.name || "-",
      toName:   (data.persons || []).find(p => p.id === toLine?.person_id)?.name   || "-"
    };
  };

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} visible={toast.visible} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>🔄 انتقال بین اشخاص</h2>
        <Btn onClick={() => { setEditId(null); setForm({ ...emptyForm, voucher_no: genVoucherNoAll(data) }); setModal(true); }}>+ ثبت انتقال</Btn>
      </div>
      <Card>
        <Table cols={["ش. سند", "تاریخ", "از", "به", "ارز", "مبلغ", "توضیحات", "عملیات"]}
          rows={transfers.map(t => {
            const det = getTransferDetails(t);
            return { cells: [
              <span style={{ color: theme.accentLight }}>{t.voucher_no}</span>,
              t.date,
              det.fromName,
              det.toName,
              det.currency_code,
              <span style={{ color: theme.warning, fontWeight: 600 }}>{fmtMoney(det.amount)}</span>,
              t.description || "-",
              <div style={{ display: "flex", gap: 4 }}>
                <button onClick={() => {
                  const det2 = getTransferDetails(t);
                  setEditId(t.id);
                  setForm({
                    voucher_no: t.voucher_no, date: t.date,
                    from_person_id: det2.from_person_id,
                    to_person_id:   det2.to_person_id,
                    currency_code:  det2.currency_code,
                    amount:         det2.amount,
                    reference_no:   "",
                    notes:          t.description || ""
                  });
                  setModal(true);
                }} style={{ padding: "4px 10px", background: "transparent", border: "1px solid "+theme.border, borderRadius: 6, color: theme.text, cursor: "pointer", fontSize: 11, fontFamily: "Vazirmatn" }}>✏️ ویرایش</button>
                <button onClick={() => { if (!window.confirm("حذف شود؟")) return; (async () => { const { data: r, error: e } = await supabase.rpc("delete_person_transfer", { p_transfer_id: t.id }); if(e||!r?.ok){alert("خطا: "+(e?.message||r?.error)); return;} await refresh(); })(); }} style={{ padding: "4px 10px", background: "transparent", border: "1px solid "+theme.danger+"55", borderRadius: 6, color: theme.danger, cursor: "pointer", fontSize: 11, fontFamily: "Vazirmatn" }}>🗑 حذف</button>
              </div>
            ]};
          })}
        />
      </Card>
      <Modal open={modal} onClose={() => setModal(false)} title={editId ? "ویرایش انتقال" : "ثبت انتقال بین اشخاص"}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 4 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, color: theme.textMuted, marginBottom: 4 }}>شماره سند (اتوماتیک)</label>
            <div style={{ padding: "9px 12px", background: theme.bgCard, border: `1px solid ${theme.accent}44`, borderRadius: 8, color: theme.accentLight, fontWeight: 700, marginBottom: 14 }}>{form.voucher_no}</div>
          </div>
          <Input label="تاریخ *" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
        </div>
        <Select label="پرداخت‌کننده (از) *" value={form.from_person_id} onChange={e => setForm(p => ({ ...p, from_person_id: e.target.value }))}>
          <option value="">— انتخاب شخص —</option>
          {(data.persons || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select label="دریافت‌کننده (به) *" value={form.to_person_id} onChange={e => setForm(p => ({ ...p, to_person_id: e.target.value }))}>
          <option value="">— انتخاب شخص —</option>
          {(data.persons || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <CurrencySelect label="ارز" value={form.currency_code} onChange={e => setForm(p => ({ ...p, currency_code: e.target.value }))} />
          <Input label="مبلغ *" type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} />
        </div>
        <Input label="شماره مرجع (اختیاری)" value={form.reference_no} onChange={e => setForm(p => ({ ...p, reference_no: e.target.value }))} />
        <Textarea label="توضیحات (اختیاری)" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
        <div style={{ background: `${theme.accent}11`, border: `1px solid ${theme.accent}33`, borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 12, color: theme.accentLight }}>
          ℹ️ صندوق و بانک درگیر نمی‌شوند — فقط مانده حساب اشخاص تغییر می‌کند
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setModal(false)}>انصراف</Btn>
          <Btn variant="warning" onClick={save} disabled={saving}>{saving ? "⏳ در حال ثبت..." : "✓ ثبت انتقال"}</Btn>
        </div>
      </Modal>
    </div>
  );
}

// =====================================================
// VOUCHER PREVIEW MODAL — [فاز۳-آیتم۱۷]
// =====================================================
function VoucherPreviewModal({ sourceId, sourceType, onClose, data, onEdit }) {
  if (!sourceId) return null;
  const ledgerLines = (data.ledger || []).filter(l => l.source_id === sourceId);
  if (!ledgerLines.length) return null;

  const firstLine = ledgerLines[0];
  const voucherNo = firstLine.voucher_no || "-";
  const date = firstLine.date || "-";
  const sourceTypeLabel = { purchase: "خرید", sale: "فروش", payment: "دریافت/پرداخت", expense: "هزینه", manual: "سند دستی", person_transfer: "انتقال بین اشخاص" };

  // ردیف‌های کالا برای خرید/فروش
  const purchaseLines = sourceType === "purchase" ? (data.purchaseLines || []).filter(l => l.purchase_id === sourceId) : [];
  const saleLines = sourceType === "sale" ? (data.saleLines || []).filter(l => l.sale_id === sourceId) : [];
  const sourceDoc = sourceType === "purchase"
    ? (data.purchases || []).find(p => p.id === sourceId)
    : sourceType === "sale"
    ? (data.sales || []).find(s => s.id === sourceId)
    : null;

  const doPrint = () => {
    const rows = ledgerLines.map(l => [
      l.date,
      (data.accounts || []).find(a => a.id === l.account_id)?.name || "-",
      (data.persons || []).find(p => p.id === l.person_id)?.name || "-",
      l.currency_code,
      l.debit > 0 ? fmtMoney(l.debit) : "-",
      l.credit > 0 ? fmtMoney(l.credit) : "-",
      l.description || ""
    ]);
    printArea(`سند ${voucherNo}`, buildHtmlTable(["تاریخ","حساب","شخص","ارز","بدهکار","بستانکار","شرح"], rows));
  };

  return (
    <Modal open onClose={onClose} title={`پیش‌نمایش سند — ${sourceTypeLabel[sourceType] || sourceType}`} width={720}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16, background: theme.bgInput, padding: 14, borderRadius: 10 }}>
        <div><div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 4 }}>شماره سند</div><div style={{ fontWeight: 700, color: theme.accentLight }}>{voucherNo}</div></div>
        <div><div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 4 }}>تاریخ</div><div>{date}</div></div>
        <div><div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 4 }}>نوع سند</div><Badge color={theme.accent}>{sourceTypeLabel[sourceType] || sourceType}</Badge></div>
        {sourceDoc?.invoice_no && <div><div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 4 }}>شماره فاکتور</div><div style={{ color: theme.accentLight }}>{sourceDoc.invoice_no}</div></div>}
        {(sourceDoc?.vendor_id || sourceDoc?.customer_id) && <div><div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 4 }}>شخص</div><div>{(data.persons || []).find(p => p.id === (sourceDoc.vendor_id || sourceDoc.customer_id))?.name || "-"}</div></div>}
      </div>

      {(purchaseLines.length > 0 || saleLines.length > 0) && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>ردیف‌های کالا</div>
          <Table cols={["کالا", "مقدار", "قیمت واحد", "جمع"]}
            rows={(purchaseLines.length ? purchaseLines : saleLines).map(l => ({ cells: [
              (data.items || []).find(i => i.id === l.item_id)?.name || "-",
              fmtQty(l.quantity),
              fmtMoney(l.unit_price, l.currency_code),
              <strong>{fmtMoney(l.total_price || l.quantity * l.unit_price, l.currency_code)}</strong>
            ]}))}
          />
        </div>
      )}

      <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>ردیف‌های حسابداری</div>
      <Table cols={["حساب", "شخص", "ارز", "بدهکار", "بستانکار", "شرح"]}
        rows={ledgerLines.map(l => ({ cells: [
          (data.accounts || []).find(a => a.id === l.account_id)?.name || "-",
          (data.persons || []).find(p => p.id === l.person_id)?.name || "-",
          l.currency_code,
          l.debit > 0 ? <span style={{ color: theme.danger, fontWeight: 600 }}>{fmtMoney(l.debit)}</span> : "-",
          l.credit > 0 ? <span style={{ color: theme.success, fontWeight: 600 }}>{fmtMoney(l.credit)}</span> : "-",
          <span style={{ fontSize: 12, color: theme.textMuted }}>{l.description}</span>
        ]}))}
      />
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
        <Btn variant="ghost" size="sm" onClick={doPrint}>🖨 چاپ</Btn>
        {["purchase","sale","payment","expense","manual","person_transfer"].includes(sourceType) && (
          <Btn variant="primary" size="sm" onClick={() => {
            const pageMap = {purchase:"purchases",sale:"sales",payment:"receive",expense:"expenses",manual:"manual",person_transfer:"transfer"};
            onClose();
            if (onEdit) onEdit(sourceId, sourceType, pageMap[sourceType]);
          }}>✏️ ویرایش سند</Btn>
        )}
        <Btn variant="ghost" onClick={onClose}>بستن</Btn>
      </div>
    </Modal>
  );
}

// hook کمکی برای preview
const useVoucherPreview = () => {
  const [preview, setPreview] = useState(null);
  const openPreview = (sourceId, sourceType) => setPreview({ sourceId, sourceType });
  const closePreview = () => setPreview(null);

  return [preview, openPreview, closePreview];
};

// =====================================================
// EXPENSE CATEGORIES PAGE — آیتم ۱۹
// =====================================================
function ExpenseCategoriesPage() {
  const { data } = useApp();
  const { sbWrite, refresh, profile: myProfile, auditLog } = useSb();
  const [toast, showToast] = useToast();
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [filterText, setFilterText] = useState("");
  const emptyForm = { code: "", name: "", description: "", is_active: true };
  const [form, setForm] = useState(emptyForm);

  const openAdd = () => {
    setEditId(null);
    setForm({ ...emptyForm, code: genExpCatCode(data.expenseCategories) });
    setModal(true);
  };

  const openEdit = (c) => {
    setEditId(c.id);
    setForm({ ...c });
    setModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) return showToast("نام دسته الزامی است", "error");
    if (!form.code.trim()) return showToast("کد دسته الزامی است", "error");
    const duplicate = (data.expenseCategories || []).find(c => c.code === form.code.trim() && c.id !== editId);
    if (duplicate) return showToast("کد " + form.code + " قبلاً استفاده شده است", "error");
    const row = { ...form, id: editId || uid() };
    const error = await sbWrite.upsert("expense_categories", row);
    if (error) { showToast("خطا: " + error.message, "error"); return; }
    await refresh();
    setModal(false);
    showToast(editId ? "دسته با موفقیت ویرایش شد ✓" : "دسته با موفقیت افزوده شد ✓");
  };

  const toggleActive = async (cat) => {
    if (cat.is_active) {
      const hasExpenses = (data.expenses || []).some(e => e.category_id === cat.id);
      if (hasExpenses && !window.confirm("این دسته هزینه دارد. غیرفعال شود؟")) return;
    }
    await sbWrite.upsert("expense_categories", { ...cat, is_active: !cat.is_active });
    await refresh();
  };

  const deleteCat = async (cat) => {
    const hasExpenses = (data.expenses || []).some(e => e.category_id === cat.id);
    if (hasExpenses) { showToast("این دسته هزینه دارد و قابل حذف نیست.", "error"); return; }
    if (!window.confirm("دسته «" + cat.name + "» حذف شود؟")) return;
    await sbWrite.delete("expense_categories", cat.id);
    await refresh();
    showToast("دسته حذف شد");
  };

  const filtered = (data.expenseCategories || []).filter(c =>
    !filterText || c.name.includes(filterText) || c.code.includes(filterText)
  );

  // گردش هر دسته
  const getCatTotal = (catId) =>
    (data.expenses || []).filter(e => e.category_id === catId).reduce((s, e) => s + (e.amount || 0), 0);
  const getCatCount = (catId) =>
    (data.expenses || []).filter(e => e.category_id === catId).length;

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} visible={toast.visible} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>🗂️ دسته‌های هزینه</h2>
        <Btn onClick={openAdd}>+ افزودن دسته</Btn>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <input
          placeholder="جستجو در نام یا کد..."
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          style={{ width: "100%", padding: "9px 12px", background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, fontSize: 14, outline: "none" }}
        />
      </Card>

      <Card>
        <Table
          cols={["کد", "نام", "توضیحات", "تعداد هزینه", "جمع (USD)", "وضعیت", "عملیات"]}
          rows={filtered.map(c => ({
            cells: [
              <span style={{ fontFamily: "monospace", color: theme.accentLight, fontSize: 12 }}>{c.code}</span>,
              <strong>{c.name}</strong>,
              <span style={{ color: theme.textMuted, fontSize: 12 }}>{c.description || "-"}</span>,
              <span style={{ color: theme.textMuted }}>{getCatCount(c.id)} مورد</span>,
              <strong style={{ color: theme.danger }}>{fmtMoney(getCatTotal(c.id), "USD")}</strong>,
              <Badge color={c.is_active !== false ? theme.success : theme.textMuted}>
                {c.is_active !== false ? "فعال" : "غیرفعال"}
              </Badge>,
              <div style={{ display: "flex", gap: 4 }}>
                <button onClick={() => openEdit(c)}
                  style={{ padding: "4px 8px", background: "transparent", border: "1px solid "+theme.border, borderRadius: 6, color: theme.text, cursor: "pointer", fontSize: 11, fontFamily: "Vazirmatn" }}>✏️</button>
                <button onClick={() => toggleActive(c)}
                  style={{ padding: "4px 8px", background: "transparent", border: "1px solid "+theme.warning+"55", borderRadius: 6, color: theme.warning, cursor: "pointer", fontSize: 11, fontFamily: "Vazirmatn" }}>
                  {c.is_active !== false ? "غیرفعال" : "فعال‌کن"}
                </button>
                <button onClick={() => deleteCat(c)}
                  style={{ padding: "4px 8px", background: "transparent", border: "1px solid "+theme.danger+"55", borderRadius: 6, color: theme.danger, cursor: "pointer", fontSize: 11, fontFamily: "Vazirmatn" }}>🗑</button>
              </div>
            ]
          }))}
        />
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title={editId ? "ویرایش دسته هزینه" : "افزودن دسته هزینه"}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
          <Input label="کد *" value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
            placeholder="EXP-001" style={{ fontFamily: "monospace" }} />
          <Input label="نام دسته *" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
        </div>
        <Textarea label="توضیحات" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} style={{ minHeight: 60 }} />
        <Select label="وضعیت" value={String(form.is_active)} onChange={e => setForm(p => ({ ...p, is_active: e.target.value === "true" }))}>
          <option value="true">فعال</option>
          <option value="false">غیرفعال</option>
        </Select>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setModal(false)}>انصراف</Btn>
          <Btn onClick={save}>ذخیره</Btn>
        </div>
      </Modal>
    </div>
  );
}

// =====================================================
// LEDGER PAGE
// =====================================================
function LedgerPage() {
  const { data } = useApp();
  const [filter, setFilter] = useState({ from: "", to: "", account: "" });
  const [preview, openPreview, closePreview] = useVoucherPreview();

  const ledger = (data.ledger || []).filter(l => {
    if (filter.from && l.date < filter.from) return false;
    if (filter.to && l.date > filter.to) return false;
    if (filter.account && l.account_id !== filter.account) return false;
    return true;
  });

  return (
    <div>
      {preview && <VoucherPreviewModal sourceId={preview.sourceId} sourceType={preview.sourceType} onClose={closePreview} data={data} onEdit={(sid, stype, pg) => { closePreview(); }} />}
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20 }}>📒 دفتر کل</h2>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
          <Input label="از تاریخ" type="date" value={filter.from} onChange={e => setFilter(p => ({ ...p, from: e.target.value }))} />
          <Input label="تا تاریخ" type="date" value={filter.to} onChange={e => setFilter(p => ({ ...p, to: e.target.value }))} />
          <Select label="حساب" value={filter.account} onChange={e => setFilter(p => ({ ...p, account: e.target.value }))}>
            <option value="">همه حساب‌ها</option>
            {(data.accounts || []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </div>
        <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 8 }}>💡 دابل‌کلیک روی هر ردیف برای مشاهده جزئیات سند</div>
      </Card>
      <Card>
        <Table cols={["تاریخ", "ش. سند", "نوع", "حساب", "شخص", "ارز", "بدهکار", "بستانکار", "شرح"]} searchable pageSize={50}
          rows={ledger.map(l => ({
            cells: [
              l.date,
              <span style={{ color: theme.textMuted, fontSize: 12 }}>{l.voucher_no || "-"}</span>,
              <Badge color={theme.accent}>{l.source_type}</Badge>,
              (data.accounts || []).find(a => a.id === l.account_id)?.name || "-",
              (data.persons || []).find(p => p.id === l.person_id)?.name || "-",
              l.currency_code,
              l.debit > 0 ? <span style={{ color: theme.danger, fontWeight: 600 }}>{fmtMoney(l.debit)}</span> : "-",
              l.credit > 0 ? <span style={{ color: theme.success, fontWeight: 600 }}>{fmtMoney(l.credit)}</span> : "-",
              <span style={{ fontSize: 12, color: theme.textMuted }}>{l.description}</span>
            ],
            onDoubleClick: () => openPreview(l.source_id, l.source_type)
          }))}
        />
        <div style={{ padding: "14px 14px 0", borderTop: `1px solid ${theme.border}`, display: "flex", gap: 30 }}>
          <span>جمع بدهکار: <strong style={{ color: theme.danger }}>{fmtMoney(ledger.reduce((s, l) => s + l.debit, 0))}</strong></span>
          <span>جمع بستانکار: <strong style={{ color: theme.success }}>{fmtMoney(ledger.reduce((s, l) => s + l.credit, 0))}</strong></span>
        </div>
      </Card>
    </div>
  );
}

// =====================================================
// REPORTS
// =====================================================
function ReportPersonBalances() {
  const { data } = useApp();
  const currency = "USD";

  // مانده هر شخص از تمام سندها که person_id دارند
  const rows = (data.persons || []).map(p => {
    const entries = (data.ledger || []).filter(l => l.person_id === p.id && l.currency_code === "USD");
    const totalDebit  = entries.reduce((s, l) => s + (l.debit  || 0), 0);
    const totalCredit = entries.reduce((s, l) => s + (l.credit || 0), 0);
    return { ...p, totalDebit, totalCredit, balance: totalDebit - totalCredit };
  }).filter(p => p.totalDebit > 0 || p.totalCredit > 0);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>📊 مانده اشخاص</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Btn size="sm" variant="ghost" onClick={() => {
            const balLabel = (bal) => bal > 0 ? " (بدهکار)" : bal < 0 ? " (بستانکار)" : " (تسویه)";
            printArea("مانده اشخاص - " + currency, buildHtmlTable(["نام","نوع","بدهکار","بستانکار","مانده"], rows.map(p => [p.name, p.type, fmt(p.totalDebit,currency), fmt(p.totalCredit,currency), fmt(Math.abs(p.balance),currency)+balLabel(p.balance)])));
          }}>🖨 چاپ</Btn>
          <Btn size="sm" variant="ghost" onClick={() => {
            const wLabel = (b) => b > 0 ? "بدهکار" : b < 0 ? "بستانکار" : "تسویه";
            exportToExcel(rows.map(p => [p.name, p.type, p.totalDebit, p.totalCredit, Math.abs(p.balance), wLabel(p.balance), currency]), ["نام","نوع","بدهکار","بستانکار","مانده","وضعیت","ارز"], "مانده_اشخاص");
          }}>📊 اکسل</Btn>
        </div>
      </div>
      <Card>
        <Table cols={["نام", "نوع", "بدهکار", "بستانکار", "مانده نهایی"]} searchable
          rows={rows.map(p => ({ cells: [
            p.name, <Badge color={theme.accent}>{p.type}</Badge>,
            p.totalDebit > 0 ? <span style={{ color: theme.danger, fontWeight: 600 }}>{fmtMoney(p.totalDebit, currency)}</span> : "-",
            p.totalCredit > 0 ? <span style={{ color: theme.success, fontWeight: 600 }}>{fmtMoney(p.totalCredit, currency)}</span> : "-",
            <span style={{ fontWeight: 700, color: p.balance > 0 ? theme.danger : p.balance < 0 ? theme.success : theme.textMuted }}>
              {fmtMoney(Math.abs(p.balance), currency)}
              {p.balance > 0 && <span style={{ fontSize: 10, marginRight: 4, background: theme.danger+"22", color: theme.danger, padding: "1px 6px", borderRadius: 4 }}>بدهکار</span>}
              {p.balance < 0 && <span style={{ fontSize: 10, marginRight: 4, background: theme.success+"22", color: theme.success, padding: "1px 6px", borderRadius: 4 }}>بستانکار</span>}
              {p.balance === 0 && <span style={{ fontSize: 10, marginRight: 4, background: theme.textMuted+"22", color: theme.textMuted, padding: "1px 6px", borderRadius: 4 }}>تسویه</span>}
            </span>
          ]}))}
        />
      </Card>
    </div>
  );
}

function ReportPersonStatement() {
  const { data } = useApp();
  const [personId, setPersonId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const currency = "USD";
  const [preview, openPreview, closePreview] = useVoucherPreview();

  const allEntries = (data.ledger || [])
    .filter(l => l.person_id === personId && l.currency_code === "USD")
    .sort((a, b) =>
      (a.date || "").localeCompare(b.date || "") ||
      (a.created_at || "").localeCompare(b.created_at || "") ||
      (a.voucher_no || "").localeCompare(b.voucher_no || "")
    );

  // مانده اول دوره: همه سندهای قبل از fromDate
  const openingBalance = fromDate
    ? allEntries.filter(l => l.date < fromDate).reduce((s, l) => s + (l.debit || 0) - (l.credit || 0), 0)
    : 0;

  // سندهای داخل بازه
  const entries = allEntries.filter(l => {
    if (fromDate && l.date < fromDate) return false;
    if (toDate && l.date > toDate) return false;
    return true;
  });

  let running = openingBalance;
  const rows = entries.map(l => { running += (l.debit || 0) - (l.credit || 0); return { ...l, running }; });
  const finalBalance = running;

  const personName = (data.persons || []).find(p => p.id === personId)?.name || "";

  const doPrint = () => {
    const tableRows = [];
    if (fromDate && openingBalance !== 0)
      tableRows.push([fromDate, "-", "-", "مانده اول دوره", openingBalance > 0 ? fmt(openingBalance) : "-", openingBalance < 0 ? fmt(Math.abs(openingBalance)) : "-", fmt(Math.abs(openingBalance), "USD") + (openingBalance > 0 ? " (بدهکار)" : " (بستانکار)")]);
    rows.forEach(l => tableRows.push([l.date, l.voucher_no||"-", l.source_type, l.description, l.debit>0?fmt(l.debit):"-", l.credit>0?fmt(l.credit):"-", fmt(Math.abs(l.running),"USD")+(l.running>0?" (بدهکار)":l.running<0?" (بستانکار)":"")]));
    printArea("ریز حساب: " + personName, buildHtmlTable(["تاریخ","ش. سند","نوع","شرح","بدهکار","بستانکار","مانده"], tableRows));
  };

  const doExcel = () => {
    const xlRows = [];
    if (fromDate && openingBalance !== 0)
      xlRows.push([fromDate, "-", "-", "مانده اول دوره", openingBalance > 0 ? openingBalance : 0, openingBalance < 0 ? Math.abs(openingBalance) : 0, Math.abs(openingBalance), openingBalance > 0 ? "بدهکار" : "بستانکار", "USD"]);
    rows.forEach(l => xlRows.push([l.date, l.voucher_no||"-", l.source_type, l.description, l.debit||0, l.credit||0, Math.abs(l.running), l.running>0?"بدهکار":l.running<0?"بستانکار":"تسویه", "USD"]));
    exportToExcel(xlRows, ["تاریخ","ش. سند","نوع","شرح","بدهکار","بستانکار","مانده","وضعیت","ارز"], "ریز_حساب_" + personName);
  };

  return (
    <div>
      {preview && <VoucherPreviewModal sourceId={preview.sourceId} sourceType={preview.sourceType} onClose={closePreview} data={data} onEdit={(sid, stype, pg) => { closePreview(); }} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>📑 ریز حساب شخص</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn size="sm" variant="ghost" onClick={doPrint} style={{ opacity: personId ? 1 : 0.4 }}>🖨 چاپ</Btn>
          <Btn size="sm" variant="ghost" onClick={doExcel} style={{ opacity: personId ? 1 : 0.4 }}>📊 اکسل</Btn>
        </div>
      </div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
          <Select label="شخص" value={personId} onChange={e => setPersonId(e.target.value)}>
            <option value="">— انتخاب کنید —</option>
            {(data.persons || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Input label="از تاریخ" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          <Input label="تا تاریخ" type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
        </div>
      </Card>
      {personId && (
        <Card>
          {fromDate && openingBalance !== 0 && (
            <div style={{ padding: "10px 14px", marginBottom: 8, background: theme.bgInput, borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: theme.textMuted, fontSize: 13 }}>مانده اول دوره ({fromDate})</span>
              <span style={{ fontWeight: 700, color: openingBalance > 0 ? theme.danger : theme.success }}>
                {fmtMoney(Math.abs(openingBalance), "USD")}
                <span style={{ fontSize: 10, marginRight: 6, padding: "1px 6px", borderRadius: 4,
                  background: openingBalance > 0 ? theme.danger+"22" : theme.success+"22",
                  color: openingBalance > 0 ? theme.danger : theme.success }}>
                  {openingBalance > 0 ? "بدهکار" : "بستانکار"}
                </span>
              </span>
            </div>
          )}
          <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 8 }}>💡 دابل‌کلیک برای مشاهده جزئیات سند</div>
          <Table cols={["تاریخ", "ش. سند", "نوع", "شرح", "بدهکار", "بستانکار", "مانده"]}
            rows={rows.map(l => ({
              cells: [
                l.date,
                <span style={{ fontSize: 12, color: theme.textMuted }}>{l.voucher_no || "-"}</span>,
                <Badge color={theme.accent}>{l.source_type}</Badge>,
                l.description,
                l.debit > 0 ? <span style={{ color: theme.danger, fontWeight: 600 }}>{fmtMoney(l.debit)}</span> : "-",
                l.credit > 0 ? <span style={{ color: theme.success, fontWeight: 600 }}>{fmtMoney(l.credit)}</span> : "-",
                <span style={{ fontWeight: 700 }}>
                  <span style={{ color: l.running > 0 ? theme.danger : l.running < 0 ? theme.success : theme.textMuted }}>{fmtMoney(Math.abs(l.running), "USD")}</span>
                  {l.running > 0 && <span style={{ fontSize: 10, marginRight: 4, background: theme.danger+"22", color: theme.danger, padding: "1px 5px", borderRadius: 4 }}>بدهکار</span>}
                  {l.running < 0 && <span style={{ fontSize: 10, marginRight: 4, background: theme.success+"22", color: theme.success, padding: "1px 5px", borderRadius: 4 }}>بستانکار</span>}
                </span>
              ],
              onDoubleClick: () => openPreview(l.source_id, l.source_type)
            }))}
          />
          {(rows.length > 0 || openingBalance !== 0) && (
            <div style={{ padding: "12px 14px 0", borderTop: `1px solid ${theme.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span><strong>مانده نهایی: </strong></span>
              <span style={{ color: finalBalance > 0 ? theme.danger : finalBalance < 0 ? theme.success : theme.textMuted, fontWeight: 700, fontSize: 16 }}>
                {fmtMoney(Math.abs(finalBalance), "USD")}
                {finalBalance > 0 && <span style={{ fontSize: 11, marginRight: 6, padding: "1px 8px", borderRadius: 4, background: theme.danger+"22", color: theme.danger }}>بدهکار</span>}
                {finalBalance < 0 && <span style={{ fontSize: 11, marginRight: 6, padding: "1px 8px", borderRadius: 4, background: theme.success+"22", color: theme.success }}>بستانکار</span>}
                {finalBalance === 0 && <span style={{ fontSize: 11, marginRight: 6, padding: "1px 8px", borderRadius: 4, background: theme.textMuted+"22", color: theme.textMuted }}>تسویه</span>}
              </span>
            </div>
          )}
          {rows.length === 0 && <p style={{ color: theme.textMuted, fontSize: 13, padding: "20px 0", textAlign: "center" }}>در این بازه تراکنشی وجود ندارد</p>}
        </Card>
      )}
    </div>
  );
}

function ReportInventory() {
  const { data } = useApp();
  const items = (data.items || []).filter(i => i.is_active !== false);
  const totalValue = items.reduce((s, i) => s + (i.current_stock || 0) * (i.avg_purchase_price || 0), 0);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>📦 موجودی کالا</h2>
        <div style={{ background: theme.bgInput, padding: "8px 16px", borderRadius: 8, fontSize: 14 }}>ارزش کل (USD): <strong style={{ color: theme.gold }}>{fmtMoney(totalValue, "USD")}</strong></div>
      </div>
      <Card>
        <Table cols={["کالا", "کد", "واحد", "موجودی", "میانگین خرید", "قیمت فروش", "ارزش موجودی", "وضعیت"]}
          rows={items.map(item => ({ cells: [
            item.name, item.sku || "-", item.unit,
            <span style={{ color: item.min_stock_alert > 0 && item.current_stock <= item.min_stock_alert ? theme.danger : theme.text, fontWeight: 600 }}>{fmtQty(item.current_stock)}</span>,
            fmtMoney(item.avg_purchase_price, item.currency_code),
            fmtMoney(item.suggested_sale_price, item.currency_code),
            <strong>{fmtMoney(item.current_stock * item.avg_purchase_price, item.currency_code)}</strong>,
            item.min_stock_alert > 0 && item.current_stock <= item.min_stock_alert ? <Badge color={theme.danger}>کم‌موجودی</Badge> : <Badge color={theme.success}>موجود</Badge>
          ]}))}
        />
      </Card>
    </div>
  );
}

function ReportCashFlow() {
  const { data } = useApp();
  const cashAccounts = (data.accounts || []).filter(a => a.type === "cash" || a.type === "bank");

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20 }}>💳 گزارش صندوق و بانک</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
        {cashAccounts.map(acc => {
          const entries = (data.ledger || []).filter(l => l.account_id === acc.id);
          const totalIn = entries.reduce((s, l) => s + (l.debit || 0), 0);
          const totalOut = entries.reduce((s, l) => s + (l.credit || 0), 0);
          const balance = (acc.opening_balance || 0) + totalIn - totalOut;
          return (
            <Card key={acc.id}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{acc.name}</div>
              {[["مانده افتتاحیه", acc.opening_balance, theme.text], ["↓ ورودی‌ها", totalIn, theme.success], ["↑ خروجی‌ها", totalOut, theme.danger]].map(([label, val, color]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${theme.border}22` }}>
                  <span style={{ color }}>{label}</span><span style={{ color }}>{fmtMoney(val, acc.currency_code)}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 10, marginTop: 4 }}>
                <span style={{ fontWeight: 700 }}>مانده فعلی</span>
                <span style={{ fontWeight: 700, fontSize: 18, color: balance >= 0 ? theme.success : theme.danger }}>{fmtMoney(balance, acc.currency_code)}</span>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function ReportProfit() {
  const { data } = useApp();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const ledger = (data.ledger || []).filter(l => {
    if (fromDate && l.date < fromDate) return false;
    if (toDate && l.date > toDate) return false;
    return true;
  });

  // درآمد فروش: از حساب income
  const salesRevenue = ledger
    .filter(l => (data.accounts || []).find(a => a.id === l.account_id)?.type === "income")
    .reduce((s, l) => s + l.credit, 0);

  // COGS — اولویت: از ledger (سند COGS) → از saleLines.cost_price
  const cogsFromLedger = ledger
    .filter(l => l.source_type === "sale" && l.debit > 0 && (data.accounts || []).find(a => a.id === l.account_id)?.type === "cogs")
    .reduce((s, l) => s + l.debit, 0);

  // fallback: فقط فروش‌هایی که در بازه تاریخ هستند
  const cogsFromSaleLines = (data.saleLines || []).reduce((sum, line) => {
    const sale = (data.sales || []).find(s => s.id === line.sale_id);
    if (!sale) return sum;
    if (fromDate && sale.date < fromDate) return sum;
    if (toDate && sale.date > toDate) return sum;
    const costPrice = parseFloat(line.cost_price) || 0;
    const qty = parseFloat(line.quantity) || 0;
    return sum + qty * costPrice;
  }, 0);

  const cogs = cogsFromLedger > 0 ? cogsFromLedger : cogsFromSaleLines;

  // هزینه‌ها — فقط USD
  const expenseTotal = ledger
    .filter(l => l.source_type === "expense" && l.debit > 0 && l.currency_code === "USD")
    .reduce((s, l) => s + l.debit, 0);

  const grossProfit = salesRevenue - cogs;
  const netProfit = grossProfit - expenseTotal;

  const inventoryValue = (data.items || []).reduce((s, i) => s + (i.current_stock || 0) * (i.avg_purchase_price || 0), 0);

  // درآمد به تفکیک ارز
  const revenueUSD = ledger.filter(l => (data.accounts||[]).find(a=>a.id===l.account_id)?.type==="income" && l.currency_code==="USD").reduce((s,l)=>s+l.credit,0);

  const dateLabel = fromDate || toDate
    ? "بازه: " + (fromDate || "ابتدا") + " تا " + (toDate || "انتها")
    : "کل دوره";

  const doPrint = () => printArea("گزارش سود و زیان — " + dateLabel, buildHtmlTable(["شرح", "مبلغ (USD)"],
    [["درآمد فروش", fmtMoney(salesRevenue,"USD")],["بهای تمام‌شده (COGS)", fmtMoney(-cogs,"USD")],["سود ناخالص", fmtMoney(grossProfit,"USD")],["هزینه‌ها", fmtMoney(-expenseTotal,"USD")],["سود خالص", fmtMoney(netProfit,"USD")]]
  ));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>📈 سود و زیان</h2>
        <Btn size="sm" variant="ghost" onClick={doPrint}>🖨 چاپ</Btn>
      </div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "flex-end" }}>
          <Input label="از تاریخ" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          <Input label="تا تاریخ" type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
          <div style={{ marginBottom: 14 }}>
            <Btn size="sm" variant="ghost" onClick={() => { setFromDate(""); setToDate(""); }}>✕ پاک</Btn>
          </div>
        </div>
        {(fromDate || toDate) && (
          <div style={{ fontSize: 12, color: theme.accentLight, marginTop: 4 }}>
            📅 {dateLabel}
          </div>
        )}
      </Card>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Card>
          <h3 style={{ fontSize: 15, marginBottom: 16, color: theme.textMuted }}>صورت سود و زیان (USD)</h3>
          {[
            ["درآمد فروش", salesRevenue, theme.success, false, false],
            ["بهای تمام‌شده (COGS)", -cogs, theme.danger, false, false],
            ["سود ناخالص", grossProfit, grossProfit >= 0 ? theme.success : theme.danger, true, false],
            ["هزینه‌های عملیاتی", -expenseTotal, theme.danger, false, false],
            ["سود خالص", netProfit, netProfit >= 0 ? theme.success : theme.danger, true, true]
          ].map(([label, val, color, bold, big], i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: i < 4 ? "1px solid "+theme.border+"22" : "none" }}>
              <span style={{ fontWeight: bold ? 700 : 400 }}>{label}</span>
              <span style={{ fontWeight: bold ? 700 : 400, fontSize: big ? 20 : 14, color }}>{fmtMoney(val, "USD")}</span>
            </div>
          ))}
          <div style={{ marginTop: 16, padding: "12px 14px", background: theme.bgInput, borderRadius: 8 }}>
            <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 4 }}>ارزش موجودی انبار (USD)</div>
            <div style={{ fontWeight: 700, color: theme.gold }}>{fmtMoney(inventoryValue, "USD")}</div>
          </div>
        </Card>
        <Card>
          <h3 style={{ fontSize: 15, marginBottom: 16, color: theme.textMuted }}>آمار و تفکیک ارز</h3>
          {[
            ["فاکتورهای فروش", (data.sales || []).length + " فاکتور"],
            ["فاکتورهای خرید", (data.purchases || []).length + " فاکتور"],
            ["کل هزینه‌ها", (data.expenses || []).length + " رکورد"],
            ["تعداد کالاها", (data.items || []).length + " قلم"],
          ].map(([label, val]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: "1px solid "+theme.border+"22" }}>
              <span style={{ color: theme.textMuted }}>{label}</span><strong>{val}</strong>
            </div>
          ))}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 10, fontWeight: 600 }}>درآمد به تفکیک ارز</div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
              <span>USD</span><strong style={{ color: theme.success }}>{fmtMoney(revenueUSD, "USD")}</strong>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

// =====================================================
// REPORT: KARDEX
// =====================================================
function ReportKardex() {
  const { data } = useApp();
  const [itemId, setItemId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [preview, openPreview, closePreview] = useVoucherPreview();

  const item = (data.items || []).find(i => i.id === itemId);

  // ساخت movements با buildMovements — همان ترتیب دقیق MWA
  const allMovements = itemId
    ? buildMovements(itemId, data.purchaseLines || [], data.saleLines || [], data.purchases || [], data.sales || [])
    : [];

  // مانده اول دوره
  const openingStock = item ? (parseFloat(item.opening_stock) || 0) : 0;
  const openingUnitCost = item ? (parseFloat(item.opening_unit_cost != null ? item.opening_unit_cost : item.avg_purchase_price) || 0) : 0;

  // محاسبه موجودی و avg قبل از fromDate
  let preStock = openingStock;
  let preAvg = openingUnitCost;
  if (from) {
    for (const m of allMovements.filter(m => m.date < from)) {
      if (m.type === "purchase" && m.qty > 0) {
        const pv = preStock * preAvg;
        preStock += m.qty;
        preAvg = preStock > 0 ? (pv + m.qty * m.cost) / preStock : m.cost;
      } else if (m.type === "sale") {
        preStock -= m.qty;
      }
    }
  }

  // فیلتر بازه
  const filteredMovements = allMovements.filter(m => (!from || m.date >= from) && (!to || m.date <= to));

  // ساخت ردیف‌های kardex با avg تجمعی
  let runStock = preStock;
  let runAvg = preAvg;
  const kardexRows = filteredMovements.map(m => {
    let costPrice = runAvg;
    if (m.type === "purchase" && m.qty > 0) {
      const pv = runStock * runAvg;
      runStock += m.qty;
      runAvg = runStock > 0 ? (pv + m.qty * m.cost) / runStock : m.cost;
      costPrice = m.cost; // قیمت خرید این ردیف
    } else if (m.type === "sale") {
      costPrice = runAvg; // avg در لحظه فروش
      runStock -= m.qty;
    }

    // اطلاعات نمایشی
    const pur = m.type === "purchase" ? (data.purchases || []).find(p => p.id === m.sourceId) : null;
    const sal = m.type === "sale" ? (data.sales || []).find(s => s.id === m.saleId) : null;
    const person = m.type === "purchase"
      ? (data.persons || []).find(p => p.id === pur?.vendor_id)?.name || "-"
      : (data.persons || []).find(p => p.id === sal?.customer_id)?.name || "-";

    return {
      date: m.date,
      type: m.type === "purchase" ? "خرید" : "فروش",
      invoice: pur?.invoice_no || sal?.invoice_no || "-",
      person,
      qty_in: m.type === "purchase" ? m.qty : 0,
      qty_out: m.type === "sale" ? m.qty : 0,
      unit_price: costPrice,
      balance: runStock,
      currency: "USD",
      source_id: m.sourceId || m.saleId,
      source_type: m.type === "purchase" ? "purchase" : "sale"
    };
  });

  const totalIn = filteredMovements.filter(m => m.type === "purchase").reduce((s, m) => s + m.qty, 0);
  const totalOut = filteredMovements.filter(m => m.type === "sale").reduce((s, m) => s + m.qty, 0);

  const doPrint = () => printArea("کاردکس: " + (item?.name || ""), buildHtmlTable(
    ["تاریخ","نوع","فاکتور","شخص","ورودی","خروجی","موجودی","قیمت واحد","ارزش"],
    kardexRows.map(r => [r.date, r.type, r.invoice, r.person, r.qty_in>0?fmtQty(r.qty_in):"-", r.qty_out>0?fmtQty(r.qty_out):"-", fmtQty(r.balance), fmt(r.unit_price,"USD"), fmt((r.qty_in||r.qty_out)*r.unit_price,"USD")])
  ));
  const doExcel = () => exportToExcel(
    kardexRows.map(r => [r.date, r.type, r.invoice, r.person, r.qty_in, r.qty_out, r.balance, r.unit_price, (r.qty_in||r.qty_out)*r.unit_price]),
    ["تاریخ","نوع","فاکتور","شخص","ورودی","خروجی","موجودی","قیمت واحد","ارزش"],
    "کاردکس_" + (item?.name || "")
  );

  return (
    <div>
      {preview && <VoucherPreviewModal sourceId={preview.sourceId} sourceType={preview.sourceType} onClose={closePreview} data={data} onEdit={(sid, stype, pg) => { closePreview(); }} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>🗂 کاردکس کالا</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn size="sm" variant="ghost" onClick={doPrint} style={{ opacity: itemId ? 1 : 0.4 }}>🖨 چاپ</Btn>
          <Btn size="sm" variant="ghost" onClick={doExcel} style={{ opacity: itemId ? 1 : 0.4 }}>📊 اکسل</Btn>
        </div>
      </div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
          <Select label="کالا" value={itemId} onChange={e => setItemId(e.target.value)}>
            <option value="">— انتخاب کالا —</option>
            {(data.items || []).map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
          </Select>
          <Input label="از تاریخ" type="date" value={from} onChange={e => setFrom(e.target.value)} />
          <Input label="تا تاریخ" type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
      </Card>
      {itemId && item && (
        <>
          <div style={{ display: "flex", gap: 14, marginBottom: 16 }}>
            <Stat label="موجودی اولیه" value={fmtQty(item.opening_stock)} color={theme.textMuted} icon="📦" />
            <Stat label="کل ورودی" value={fmtQty(totalIn)} color={theme.success} icon="⬇️" />
            <Stat label="کل خروجی" value={fmtQty(totalOut)} color={theme.danger} icon="⬆️" />
            <Stat label={to ? "موجودی پایان بازه" : "موجودی فعلی امروز"} value={fmtQty(to ? runStock : item.current_stock)} color={theme.accent} icon="🏪" />
          </div>
          <Card>
            <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 8 }}>💡 دابل‌کلیک برای مشاهده جزئیات سند</div>
            {from && (
              <div style={{ padding: "10px 14px", marginBottom: 8, background: theme.bgInput, borderRadius: 8, display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: theme.textMuted, fontSize: 13 }}>موجودی اول دوره ({from})</span>
                <strong style={{ color: theme.accent }}>{fmtQty(preStock)} {item.unit}</strong>
              </div>
            )}
            <Table cols={["تاریخ", "نوع", "شماره فاکتور", "شخص", "مقدار ورودی", "مقدار خروجی", "موجودی", "قیمت واحد", "مجموع قیمت"]}
              rows={kardexRows.map(r => ({
                cells: [
                  r.date,
                  <Badge color={r.type === "خرید" ? theme.success : theme.warning}>{r.type}</Badge>,
                  <span style={{ color: theme.accentLight }}>{r.invoice}</span>,
                  r.person,
                  r.qty_in > 0 ? <span style={{ color: theme.success, fontWeight: 600 }}>+{fmtQty(r.qty_in)}</span> : "-",
                  r.qty_out > 0 ? <span style={{ color: theme.danger, fontWeight: 600 }}>-{fmtQty(r.qty_out)}</span> : "-",
                  <span style={{ fontWeight: 700, color: r.balance > 0 ? theme.text : theme.danger }}>{fmtQty(r.balance)}</span>,
                  <span style={{ color: theme.gold }}>{fmtMoney(r.unit_price, r.currency)}</span>,
                  <strong style={{ color: r.type === "خرید" ? theme.success : theme.warning }}>{fmtMoney((r.qty_in || r.qty_out) * r.unit_price, r.currency)}</strong>
                ],
                onDoubleClick: () => r.source_id && openPreview(r.source_id, r.source_type)
              }))}
            />
          </Card>
        </>
      )}
    </div>
  );
}

// =====================================================
// REPORT: EXPENSES
// =====================================================
function ReportExpenses() {
  const { data } = useApp();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [cat, setCat] = useState("");
  const [preview, openPreview, closePreview] = useVoucherPreview();

  const expenses = (data.expenses || []).filter(e => {
    if (from && e.date < from) return false;
    if (to && e.date > to) return false;
    if (cat && e.category_id !== cat) return false;
    return true;
  });

  const totalUSD = expenses.reduce((s, e) => s + (e.amount || 0), 0);

  const byCat = (data.expenseCategories || []).map(c => {
    const catExp = expenses.filter(e => e.category_id === c.id);
    return {
      ...c,
      sumUSD: catExp.filter(e => e.currency_code === "USD").reduce((s, e) => s + e.amount, 0),
      count: catExp.length
    };
  }).filter(c => c.count > 0);

  const getCellPayer = (e) => e.paid_by_person
    ? (data.persons || []).find(p => p.id === e.person_id)?.name || "-"
    : (data.accounts || []).find(a => a.id === e.account_id)?.name || "-";

  const doPrint = () => printArea("گزارش هزینه‌ها", buildHtmlTable(
    ["تاریخ", "دسته", "مبلغ", "ارز", "پرداخت‌کننده", "توضیحات"],
    expenses.map(e => [e.date, (data.expenseCategories || []).find(c => c.id === e.category_id)?.name || "-", fmtMoney(e.amount), e.currency_code, getCellPayer(e), e.description || ""])
  ));
  const doExcel = () => exportToExcel(
    expenses.map(e => [e.date, (data.expenseCategories || []).find(c => c.id === e.category_id)?.name || "-", e.amount, e.currency_code, getCellPayer(e), e.description || ""]),
    ["تاریخ", "دسته", "مبلغ", "ارز", "پرداخت‌کننده", "توضیحات"], "هزینه‌ها"
  );

  return (
    <div>
      {preview && <VoucherPreviewModal sourceId={preview.sourceId} sourceType={preview.sourceType} onClose={closePreview} data={data} onEdit={(sid, stype, pg) => { closePreview(); }} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>🧾 گزارش هزینه‌ها</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn size="sm" variant="ghost" onClick={doPrint}>🖨 چاپ</Btn>
          <Btn size="sm" variant="ghost" onClick={doExcel}>📊 اکسل</Btn>
        </div>
      </div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Input label="از تاریخ" type="date" value={from} onChange={e => setFrom(e.target.value)} />
          <Input label="تا تاریخ" type="date" value={to} onChange={e => setTo(e.target.value)} />
          <Select label="دسته" value={cat} onChange={e => setCat(e.target.value)}>
            <option value="">همه</option>
            {(data.expenseCategories || []).map(c => <option key={c.id} value={c.id}>{c.code ? c.code + " — " : ""}{c.name}</option>)}
          </Select>
        </div>
        <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 8 }}>💡 دابل‌کلیک روی هر ردیف برای مشاهده جزئیات سند</div>
      </Card>
      <div style={{ display: "flex", gap: 14, marginBottom: 16 }}>
        {totalUSD > 0 && <Stat label="جمع USD" value={fmtMoney(totalUSD, "USD")} color={theme.success} icon="🧾" />}
        <Stat label="تعداد" value={expenses.length + " مورد"} color={theme.accent} icon="📋" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16 }}>
        <Card>
          <h3 style={{ fontSize: 14, marginBottom: 14, color: theme.textMuted }}>به تفکیک دسته</h3>
          {byCat.map(c => (
            <div key={c.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${theme.border}22`, fontSize: 13 }}>
              <span>{c.name}</span>
              <div>
                {c.sumUSD > 0 && <div style={{ color: theme.success }}>{fmtMoney(c.sumUSD, "USD")}</div>}
              </div>
            </div>
          ))}
          {byCat.length === 0 && <p style={{ color: theme.textMuted, fontSize: 13 }}>داده‌ای یافت نشد</p>}
        </Card>
        <Card>
          <Table cols={["تاریخ", "دسته", "مبلغ", "پرداخت‌کننده", "توضیحات"]}
            rows={expenses.map(e => ({
              cells: [
                e.date,
                (data.expenseCategories || []).find(c => c.id === e.category_id)?.name || "-",
                <span style={{ color: theme.danger }}>{fmtMoney(e.amount, e.currency_code)}</span>,
                e.paid_by_person ? <Badge color={theme.warning}>{getCellPayer(e)}</Badge> : getCellPayer(e),
                e.description || "-"
              ],
              onDoubleClick: () => e.id && openPreview(e.id, "expense")
            }))}
          />
        </Card>
      </div>
    </div>
  );
}
